import { PRICING } from "@/config/pricing";

// Pricing helpers - keep all money/limit values consistent with PRICING (the
// /pricing page is the source of truth via src/config/pricing.ts).
const PRO_M = PRICING.pro.monthly;                                  // 149
const PRO_Y = PRICING.pro.yearly.toLocaleString("en-US");           // "1,499"
const DOC = PRICING.docPack.price;                                  // 99
const PLUS = PRICING.aiPlus.price;                                  // 9
const FREE_MSG = PRICING.free.messagesPerDay;                       // 3
const PLUS_MSG = PRICING.aiPlus.messagesPerDay;                     // 30
const PRO_MSG = PRICING.pro.messagesPerDay;                         // 150

const en: Record<string, string> = {
  // Email Verification Gate
  'verification.gate.title': 'Verify Your Email',
  'verification.gate.description': 'Please verify your email to access this feature.',
  'verification.gate.oneTimeNote': 'You only need to verify once. After verification, you can access all features.',
  'verification.gate.clickToAccess': 'Click the link in the email to access this feature.',
  'verification.gate.toolsAccess': 'ISO 27001 Tools',
  'verification.gate.successMessage': 'Email verified! You can now download documents.',

  // Pro Preview Banner
  'pro.preview.title': 'Showing only a short preview.',
  'pro.preview.description': 'Pro gives you the full result and file export.',
  'pro.preview.button': 'See Pro options',

  // Free User Tool Dialog
  'tools.freeUserDialog.title': 'Preview Mode',
  'tools.freeUserDialog.description': 'You are using this tool in preview mode. Free users can only add up to 2 items and see limited results.',
  'tools.freeUserDialog.feature1': 'Full access to all entries and results',
  'tools.freeUserDialog.feature2': 'Export complete reports to PDF, Excel, and Word',
  'tools.freeUserDialog.feature3': 'Save and load your projects',
  'tools.freeUserDialog.continuePreview': 'Continue with Preview',
  'tools.freeUserDialog.upgradeToPro': 'Upgrade to Pro',
  'tools.freeLimit': 'Free users can only add up to 2 items. Upgrade to Pro for unlimited entries.',

  // Implementation Roadmap Placeholders (new)
  'tools.roadmap.timelineOverview': 'Timeline Overview',
  'tools.roadmap.totalMonths': 'Total Months',
  'tools.roadmap.startDateLabel': 'Start Date',
  'tools.roadmap.resources': 'Resources',
  'tools.roadmap.exportToExcel': 'Export to Excel',

  // Asset Inventory Placeholders (new)
  'tools.assets.assetNamePlaceholder': 'e.g., Customer Database Server',
  'tools.assets.ownerPlaceholder': 'Person responsible for this asset',
  'tools.assets.locationPlaceholder': 'Physical or logical location',
  'tools.assets.descriptionPlaceholder': 'Brief description of the asset',

  // Supplier Risk Manager Placeholders (new)
  'tools.supplier.supplierNamePlaceholder': 'e.g., AWS, Microsoft Azure',
  'tools.supplier.notesPlaceholder': 'Additional notes about this supplier...',
  'tools.supplier.dataSensitivity.1': 'Minimal',
  'tools.supplier.dataSensitivity.2': 'Low',
  'tools.supplier.dataSensitivity.3': 'Medium',
  'tools.supplier.dataSensitivity.4': 'High',
  'tools.supplier.dataSensitivity.5': 'Critical',
  'tools.supplier.contractStrength.1': 'Weak',
  'tools.supplier.contractStrength.2': 'Below Average',
  'tools.supplier.contractStrength.3': 'Average',
  'tools.supplier.contractStrength.4': 'Strong',
  'tools.supplier.contractStrength.5': 'Very Strong',
  'tools.supplier.countryRisk.1': 'Very Low Risk',
  'tools.supplier.countryRisk.2': 'Low Risk',
  'tools.supplier.countryRisk.3': 'Medium Risk',
  'tools.supplier.countryRisk.4': 'High Risk',
  'tools.supplier.countryRisk.5': 'Very High Risk',

  // RTO/RPO & BIA Helper Placeholders (new)
  'tools.rtorpo.processNamePlaceholder': 'e.g., Order Processing, Customer Support',
  'tools.rtorpo.processOwnerPlaceholder': 'Person responsible',
  'tools.rtorpo.processDescriptionPlaceholder': 'Brief description of the business process...',
  'tools.rtorpo.dependenciesPlaceholder': 'e.g., ERP System, Database Server, Internet Connection',
  'tools.rtorpo.rtoLabel': 'Recovery Time Objective (RTO)',
  'tools.rtorpo.rpoLabel': 'Recovery Point Objective (RPO)',

  // Annex A Selector UI (new)
  'annexSelector.ui.showing': 'Showing',
  'annexSelector.ui.ofControls': 'controls. Upgrade to Pro to see all.',

  // Navigation
  'nav.home': 'Home',
  'nav.documents': 'Documents',
  'nav.tools': 'Tools',
  'nav.blog': 'Blog',
  'nav.consultation': 'Consultation',
  'nav.about': 'About',
  'nav.contact': 'Contact Us',
  'nav.signIn': 'Sign In',
  'nav.signOut': 'Sign Out',
  'nav.account': 'Account',
  'nav.pricing': 'Pricing',

  // Common
  'common.loading': 'Loading...',
  'common.error': 'Error',
  'common.success': 'Success',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.download': 'Download',
  'common.export': 'Export',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.all': 'All',
  'common.learnMore': 'Learn more about this',
  'common.getStarted': 'Get Started',
  'common.viewAll': 'View All',
  'common.readMore': 'Read full article',
  'common.upgrade': 'Upgrade',
  'common.free': 'Free',
  'common.pro': 'Pro',
  'common.pending': 'Pending',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.na': 'N/A',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.generate': 'Generate',
  'common.pickDate': 'Pick a date',

  // Home Page
  'home.hero.title': 'Complete ISO 27001 Implementation Kit',
  'home.hero.subtitle': 'Everything you need to implement ISO 27001 in your organization. Comprehensive templates, policies, and guides to achieve certification.',
  'home.hero.cta': 'Explore Documents',
  'home.hero.secondary': 'Get the Editable Pack →',
  'home.hero.downloadWordPack': 'Download Your Document Pack',
  'home.hero.dealWordPack': `$${DOC} one-time. Save 100+ hours of documentation work.`,
  'home.hero.valueProposition': 'Join 500+ companies who achieved ISO 27001 certification with our kit',
  'home.tools.title': 'Implementation Tools',
  'home.tools.dealPro': `Pro at $${PRO_M}/month. Full tool access + unlimited exports. Cancel anytime.`,
  'home.tools.getFullAccess': `Unlock All Tools - $${PRO_M}/mo`,
  'home.tools.sectionDescription': '11 powerful tools to build your ISMS, assess risks, and generate audit-ready documents in minutes.',
  'home.tools.viewAllTools': 'Try Tools',
  'home.tools.launchTool': 'Launch Tool',
  'home.tools.soa.title': 'SoA Generator',
  'home.tools.soa.description': 'Generate your Statement of Applicability by selecting applicable controls, documenting implementation status, and exporting a professional SoA document.',
  'home.tools.risk.title': 'Risk Assessment',
  'home.tools.risk.description': 'Identify and assess information security risks by evaluating threats, likelihood, and impact, then create comprehensive treatment plans for your organization.',
  'home.tools.gap.title': 'Gap Analysis',
  'home.tools.gap.description': 'Evaluate your current security posture against ISO 27001 requirements, identify compliance gaps, and generate prioritized action plans to address them.',
  'home.tools.audit.title': 'Internal Audit',
  'home.tools.audit.description': 'Conduct internal audits using customizable checklists based on ISO 27001 controls, document evidence collected, and record audit findings systematically.',
  'home.tools.roadmap.title': 'Implementation Roadmap',
  'home.tools.roadmap.description': 'Build a customized implementation timeline with milestones based on your organization size and maturity level, and track progress toward certification.',
  'home.tools.scope.title': 'ISMS Scope Builder',
  'home.tools.scope.description': 'Define your ISMS scope by selecting sites, systems, processes, and legal requirements, then export a ready-to-use scope statement.',
  'home.tools.assets.title': 'Asset Inventory',
  'home.tools.assets.description': 'Create an asset register listing assets, owners, locations, and CIA ratings, then download the complete inventory as a file.',
  'home.tools.annexSelector.title': 'Annex A Selector',
  'home.tools.annexSelector.description': 'Answer simple questions about your company size and tech use to get a tailored list of relevant Annex A controls for your SoA.',
  'home.tools.supplier.title': 'Supplier Risk Manager',
  'home.tools.supplier.description': 'Add suppliers, rate data sensitivity, contract strength, and country risk, then get risk levels and action recommendations.',
  'home.tools.rtorpo.title': 'RTO/RPO & BIA Helper',
  'home.tools.rtorpo.description': 'Set impact and max downtime for each key process to calculate RTO and RPO values plus a business impact summary.',
  'home.tools.vendorChecker.title': 'Vendor Security Checker',
  'home.tools.vendorChecker.description': 'Assess SaaS vendors and suppliers against ISO 27001, SOC 2, GDPR and security best practices in minutes.',
  'home.wordPack.title': 'ISO 27001 Document Pack',
  'home.wordPack.subtitle': '40+ professionally written ISO 27001 policies, procedures, and templates in editable Word format, plus 2 Excel registers (Risk Register and Statement of Applicability). No watermarks. Instant download.',
  'home.wordPack.feature1': '40+ Word documents + 2 Excel registers',
  'home.wordPack.feature2': 'No watermarks',
  'home.wordPack.feature3': 'Instant download',
  'home.wordPack.seeButton': 'Preview Documents',
  'home.wordPack.getButton': 'Get the Editable Pack →',
  'home.consultation.title': 'Need Expert Guidance?',
  'home.consultation.subtitle': 'Book a 30-minute consultation to discuss your ISO 27001 implementation journey. Get personalized advice and answers to your specific questions.',
  'home.consultation.feature1': '30-Minute Consultation',
  'home.consultation.feature2': 'Expert ISO 27001 Advice',
  'home.consultation.feature3': 'Tailored Recommendations',
  'home.consultation.book': 'Book Your Consultation',
  'home.consultation.emailUs': 'Or email us directly at',
  'home.whyChoose.title': 'Why Choose Our ISO 27001 Kit?',
  'home.whyChoose.complete.title': 'Complete Documentation',
  'home.whyChoose.complete.description': 'All mandatory and recommended policies, procedures, and templates required for ISO 27001 certification.',
  'home.whyChoose.expert.title': 'Expert-Developed',
  'home.whyChoose.expert.description': 'Created by information security professionals with years of experience in ISO 27001 implementation.',
  'home.whyChoose.ready.title': 'Ready-to-Use',
  'home.whyChoose.ready.description': "Customizable templates that can be immediately adapted to your organization's needs.",
  'home.keyDocs.title': 'Key Documents Included',
  'home.keyDocs.mandatory': 'Mandatory Policies',
  'home.keyDocs.mandatory.infoSec': 'Information Security Policy',
  'home.keyDocs.mandatory.risk': 'Risk Assessment & Treatment Policy',
  'home.keyDocs.mandatory.docControl': 'Document Control Policy',
  'home.keyDocs.guides': 'Implementation Guides',
  'home.keyDocs.guides.toolkit': 'ISO 27001 Toolkit User Guide',
  'home.keyDocs.guides.research': 'ISO 27001 Research Summary',
  'home.cta.title': 'Need a Customized Version?',
  'home.cta.subtitle': "Get a tailored ISO 27001 implementation kit specific to your organization's needs and industry.",
  'home.cta.button': 'Contact Us',

  // Tools Page
 'tools.page.title': '11 free ISO 27001 tools for implementers',
 'tools.page.subtitle': 'Pick a tool and get a usable artifact in minutes - gap analysis, SoA, risk register, vendor scan, and more. No signup needed to try.',
  'tools.page.aiNotice': 'These tools were built using AI with expert input and review.',

  // Tools Common
  'tools.aboutThisTool': 'About This Tool',
  'tools.orgInfo.title': 'Organization Information',
  'tools.orgInfo.orgName': 'Organization Name',
  'tools.orgInfo.preparedBy': 'Prepared By',
  'tools.orgInfo.approvedBy': 'Approved By',
  'tools.orgInfo.version': 'Version',
  'tools.orgInfo.date': 'Date',
  'tools.fillRequired': 'Please fill in organization name and prepared by fields',
  'tools.filters': 'Filters',
  'tools.filterByCategory': 'Filter by Category',
  'tools.filterByApplicability': 'Filter by Applicability',
  'tools.allCategories': 'All Categories',
  'tools.allControls': 'All Controls',
  'tools.applicableOnly': 'Applicable Only',
  'tools.notApplicableOnly': 'Not Applicable Only',
  'tools.pendingAssessment': 'Pending Assessment',
  'tools.showing': 'Showing',
  'tools.of': 'of',
  'tools.controls': 'controls',
  'tools.common.filters': 'Filters',
  'tools.common.showing': 'Showing',
  'tools.common.of': 'of',
  'tools.common.controls': 'controls',
  'tools.common.control': 'Control',
  'tools.common.organizationName': 'Organization Name',
  'tools.common.orgNamePlaceholder': 'Your Organization Name',
  'tools.gap.category': 'Category',
  'tools.soa.title': 'Statement of Applicability Generator',
  'tools.soa.description': 'Document which ISO 27001 controls apply to your organization',
  'tools.soa.info': 'The Statement of Applicability (SoA) is a mandatory ISO 27001 document that lists all controls from Annex A, identifies which are applicable, provides justification, and describes implementation status.',
  'tools.soa.infoContent': 'The Statement of Applicability (SoA) is a mandatory ISO 27001 document that lists all controls from Annex A, identifies which are applicable, provides justification, and describes implementation status.',
  'tools.soa.assessmentProgress': 'Assessment Progress',
  'tools.soa.totalControls': 'Total Controls',
  'tools.soa.applicable': 'Applicable',
  'tools.soa.notApplicable': 'Not Applicable',
  'tools.soa.pending': 'Pending',
  'tools.soa.filterByCategory': 'Filter by Category',
  'tools.soa.allCategories': 'All Categories',
  'tools.soa.filterByApplicability': 'Filter by Applicability',
  'tools.soa.allControls': 'All Controls',
  'tools.soa.applicableOnly': 'Applicable Only',
  'tools.soa.notApplicableOnly': 'Not Applicable Only',
  'tools.soa.pendingAssessment': 'Pending Assessment',
  'tools.soa.annexAControls': 'ISO 27001:2022 Annex A Controls',
  'tools.soa.applicability': 'Applicability',
  'tools.soa.selectApplicability': 'Select applicability',
  'tools.soa.justification': 'Justification',
  'tools.soa.justificationPlaceholder': 'Explain why this control is or is not applicable',
  'tools.soa.implementationStatus': 'Implementation Status',
  'tools.soa.selectStatus': 'Select status',
  'tools.soa.implemented': 'Implemented',
  'tools.soa.inProgress': 'In Progress',
  'tools.soa.planned': 'Planned',
  'tools.soa.partial': 'Partially Implemented',
  'tools.soa.notStarted': 'Not Started',
  'tools.soa.implementationNotes': 'Implementation Notes',
  'tools.soa.implementationNotesPlaceholder': 'Describe how this control is implemented',
  'tools.soa.isControlApplicable': 'Is this control applicable?',
  'tools.soa.html.title': 'Statement of Applicability (SoA)',
  'tools.soa.html.documentInfo': 'Document Information',
  'tools.soa.html.organization': 'Organization',
  'tools.soa.html.documentVersion': 'Document Version',
  'tools.soa.html.date': 'Date',
  'tools.soa.html.preparedBy': 'Prepared By',
  'tools.soa.html.approvedBy': 'Approved By',
  'tools.soa.html.summaryStats': 'Summary Statistics',
  'tools.soa.html.controlsByCategory': 'Controls by Category',
  'tools.soa.html.controlId': 'Control ID',
  'tools.soa.html.controlTitle': 'Control Title',
  'tools.soa.html.status': 'Status',
  'tools.soa.html.yes': 'YES',
  'tools.soa.html.no': 'NO',
  'tools.soa.html.categoryControls': 'Controls',
  'tools.soa.html.footer': 'This Statement of Applicability was generated for',
  'tools.soa.html.notSpecified': 'Not specified',
  'tools.soa.previewExported': 'Preview HTML exported (limited controls)',
  'tools.soa.reportGenerated': 'HTML report generated successfully',
  'tools.common.exportToExcel': 'Export to Excel',
  'tools.common.exportToWord': 'Export to Word',

  // Risk Assessment
  'tools.risk.title': 'Risk Assessment Generator',
  'tools.risk.description': 'Identify, assess, and document information security risks for ISO 27001 compliance',
  'tools.risk.info': 'Risk assessment is a fundamental requirement of ISO 27001. This tool helps you identify threats, assess their likelihood and impact, evaluate existing controls, and document treatment plans for each identified risk.',
  'tools.risk.infoContent': 'Risk assessment is a fundamental requirement of ISO 27001. This tool helps you identify threats, assess their likelihood and impact, evaluate existing controls, and document treatment plans for each identified risk.',
  'tools.risk.assessmentScope': 'Assessment Scope',
  'tools.risk.assessmentScopePlaceholder': 'e.g., IT Infrastructure, Customer Data Systems, Entire Organization',
  'tools.risk.riskAssessment': 'Risk Assessment',
  'tools.risk.addRisk': 'Add Risk',
  'tools.risk.newRiskAdded': 'New risk added',
  'tools.risk.riskRemoved': 'Risk removed',
  'tools.risk.atLeastOneRequired': 'At least one risk is required',
  'tools.risk.assetSystemName': 'Asset / System Name',
  'tools.risk.assetPlaceholder': 'e.g., Customer Database, Email Server',
  'tools.risk.riskOwner': 'Risk Owner',
  'tools.risk.ownerPlaceholder': 'Person responsible for this risk',
  'tools.risk.threatDescription': 'Threat Description',
  'tools.risk.threatPlaceholder': 'Describe the threat (e.g., Unauthorized access, Ransomware attack, Data breach)',
  'tools.risk.vulnerability': 'Vulnerability',
  'tools.risk.vulnerabilityPlaceholder': 'What weakness could be exploited? (e.g., Weak passwords, Unpatched systems, Lack of encryption)',
  'tools.risk.likelihood': 'Likelihood',
  'tools.risk.impact': 'Impact',
  'tools.risk.riskScore': 'Risk Score',
  'tools.risk.existingControls': 'Existing Controls',
  'tools.risk.existingControlsPlaceholder': 'List current controls that mitigate this risk',
  'tools.risk.treatmentOption': 'Treatment Option',
  'tools.risk.treatmentPlan': 'Treatment Plan',
  'tools.risk.treatmentPlanPlaceholder': 'Describe actions to address this risk',
  'tools.risk.targetDate': 'Target Completion Date',
  'tools.risk.likelihood.rare': 'Rare',
  'tools.risk.likelihood.unlikely': 'Unlikely',
  'tools.risk.likelihood.possible': 'Possible',
  'tools.risk.likelihood.likely': 'Likely',
  'tools.risk.likelihood.almostCertain': 'Almost Certain',
  'tools.risk.impact.insignificant': 'Insignificant',
  'tools.risk.impact.minor': 'Minor',
  'tools.risk.impact.moderate': 'Moderate',
  'tools.risk.impact.major': 'Major',
  'tools.risk.impact.severe': 'Severe',
  'tools.risk.treatment.mitigate': 'Mitigate',
  'tools.risk.treatment.accept': 'Accept',
  'tools.risk.treatment.avoid': 'Avoid',
  'tools.risk.treatment.transfer': 'Transfer',
  'tools.risk.riskLevel.low': 'Low',
  'tools.risk.riskLevel.medium': 'Medium',
  'tools.risk.riskLevel.high': 'High',
  'tools.risk.riskLevel.critical': 'Critical',
  'tools.risk.html.title': 'ISO 27001 Risk Assessment',
  'tools.risk.html.assessmentDate': 'Assessment Date',
  'tools.risk.html.organization': 'Organization',
  'tools.risk.html.assessmentScope': 'Assessment Scope',
  'tools.risk.html.version': 'Version',
  'tools.risk.html.date': 'Date',
  'tools.risk.html.preparedBy': 'Prepared By',
  'tools.risk.html.approvedBy': 'Approved By',
  'tools.risk.html.riskMatrix': 'Risk Assessment Matrix',
  'tools.risk.html.riskFormula': 'Risk Score = Likelihood × Impact',
  'tools.risk.html.riskScale': 'Low (1-5) | Medium (6-9) | High (10-14) | Critical (15-25)',
  'tools.risk.html.totalRisks': 'Total Risks Assessed',
  'tools.risk.html.showingPreview': 'showing preview of',
  'tools.risk.html.total': 'total',
  'tools.risk.html.identifiedRisks': 'Identified Risks',
  'tools.risk.html.footer': 'This risk assessment was generated as part of ISO 27001 compliance efforts for',
  'tools.risk.html.documentVersion': 'Document Version',
  'tools.risk.html.risk': 'Risk',
  'tools.risk.html.unnamedAsset': 'Unnamed Asset',
  'tools.risk.html.assetSystem': 'Asset/System',
  'tools.risk.html.threat': 'Threat',
  'tools.risk.html.vulnerability': 'Vulnerability',
  'tools.risk.html.likelihood': 'Likelihood',
  'tools.risk.html.impact': 'Impact',
  'tools.risk.html.riskScore': 'Risk Score',
  'tools.risk.html.existingControls': 'Existing Controls',
  'tools.risk.html.treatmentOption': 'Treatment Option',
  'tools.risk.html.treatmentPlan': 'Treatment Plan',
  'tools.risk.html.riskOwner': 'Risk Owner',
  'tools.risk.html.targetCompletion': 'Target Completion',
  'tools.risk.html.calculatedAs': 'Calculated as Likelihood',
  'tools.risk.html.times': '×',
  'tools.risk.fillRequired': 'Please fill in organization name and prepared by fields',
  'tools.risk.previewExported': 'Preview HTML exported (limited risks)',
  'tools.risk.reportGenerated': 'HTML report generated successfully',
  'tools.risk.excelPreview': 'Preview Excel file exported (header + first row only)',
  'tools.risk.excelSuccess': 'Excel file exported successfully',
  'tools.risk.excel.riskId': 'Risk ID',
  'tools.risk.excel.riskTitle': 'Risk Title',
  'tools.risk.excel.assetSystem': 'Asset / System',
  'tools.risk.excel.threat': 'Threat',
  'tools.risk.excel.vulnerability': 'Vulnerability',
  'tools.risk.excel.likelihood': 'Likelihood',
  'tools.risk.excel.likelihoodText': 'Likelihood Text',
  'tools.risk.excel.impact': 'Impact',
  'tools.risk.excel.impactText': 'Impact Text',
  'tools.risk.excel.riskScore': 'Risk Score',
  'tools.risk.excel.riskLevel': 'Risk Level',
  'tools.risk.excel.existingControls': 'Existing Controls',
  'tools.risk.excel.treatmentOption': 'Treatment Option',
  'tools.risk.excel.treatmentPlan': 'Treatment Plan',
  'tools.risk.excel.riskOwner': 'Risk Owner',
  'tools.risk.excel.targetDate': 'Target Completion Date',
  'tools.risk.sheetName': 'Risk Register',
  'tools.risk.addAnotherRisk': 'Add Another Risk',

  // Gap Analysis
  'tools.gap.title': 'Gap Analysis Tool',
  'tools.gap.description': 'Assess your current security posture against ISO 27001 requirements',
  'tools.gap.info': 'Gap analysis identifies the difference between your current security controls and ISO 27001 requirements. Use this tool to assess each control, document gaps, and create action plans for implementation.',
  'tools.gap.infoContent': 'Gap analysis identifies the difference between your current security controls and ISO 27001 requirements. Use this tool to assess each control, document gaps, and create action plans for implementation.',
  'tools.gap.assessmentScope': 'Assessment Scope',
  'tools.gap.assessmentScopePlaceholder': 'e.g., IT Department, Entire Organization, Specific Business Unit',
  'tools.gap.controlAssessment': 'Control Assessment',
  'tools.gap.assessed': 'assessed',
  'tools.gap.currentStatus': 'Current Status',
  'tools.gap.selectStatus': 'Select status',
  'tools.gap.fullyImplemented': 'Fully Implemented',
  'tools.gap.partiallyImplemented': 'Partially Implemented',
  'tools.gap.notImplemented': 'Not Implemented',
  'tools.gap.priority': 'Priority',
  'tools.gap.selectPriority': 'Select priority',
  'tools.gap.critical': 'Critical',
  'tools.gap.high': 'High',
  'tools.gap.medium': 'Medium',
  'tools.gap.low': 'Low',
  'tools.gap.gapIdentified': 'Gap Identified',
  'tools.gap.gapPlaceholder': 'Describe the gap between current state and requirements',
  'tools.gap.actionPlan': 'Action Plan',
  'tools.gap.actionPlanPlaceholder': 'Define actions needed to close the gap',
  'tools.gap.responsiblePerson': 'Responsible Person',
  'tools.gap.responsiblePlaceholder': 'Who will implement this?',
  'tools.gap.targetDate': 'Target Date',

  // Internal Audit
  'tools.audit.title': 'Internal Audit Checklist',
  'tools.audit.description': 'Conduct comprehensive internal audits of your ISO 27001 controls',
  'tools.audit.info': 'Internal audits are essential for ISO 27001 compliance. Use this tool to systematically audit your controls, document evidence, record findings, and track compliance status across your ISMS.',
  'tools.audit.infoContent': 'Internal audits are essential for ISO 27001 compliance. Use this tool to systematically audit your controls, document evidence, record findings, and track compliance status across your ISMS.',
  'tools.audit.auditScope': 'Audit Scope',
  'tools.audit.auditScopePlaceholder': 'e.g., IT Department, Specific Process',
  'tools.audit.auditObjective': 'Audit Objective',
  'tools.audit.auditObjectivePlaceholder': 'e.g., Verify control effectiveness',
  'tools.audit.auditor': 'Auditor',
  'tools.audit.auditSummary': 'Audit Summary',
  'tools.audit.overallComplianceRate': 'Overall Compliance Rate',
  'tools.audit.compliant': 'Compliant',
  'tools.audit.nonCompliant': 'Non-Compliant',
  'tools.audit.partial': 'Partial',
  'tools.audit.notAssessed': 'Not Assessed',
  'tools.audit.addControl': 'Add Control to Audit',
  'tools.audit.selectControl': 'Select a control to add',
  'tools.audit.controlAdded': 'Control added to audit checklist',
  'tools.audit.controlRemoved': 'Control removed',
  'tools.audit.controlAlreadyAdded': 'This control has already been added',
  'tools.audit.pleaseSelectControl': 'Please select a control to add',
  'tools.audit.auditQuestions': 'Audit Questions',
  'tools.audit.addQuestion': 'Add Question',
  'tools.audit.question': 'Audit Question',
  'tools.audit.questionPlaceholder': 'Enter your audit question',
  'tools.audit.compliance': 'Compliance',
  'tools.audit.selectCompliance': 'Select',
  'tools.audit.complianceYes': 'Compliant',
  'tools.audit.complianceNo': 'Non-Compliant',
  'tools.audit.compliancePartial': 'Partial',
  'tools.audit.complianceNA': 'N/A',
  'tools.audit.evidence': 'Evidence Collected',
  'tools.audit.evidencePlaceholder': 'Document evidence found',
  'tools.audit.findings': 'Findings/Notes',
  'tools.audit.findingsPlaceholder': 'Record audit findings',
  'tools.audit.atLeastOneQuestion': 'At least one question is required per control',
  'tools.audit.noControlsAdded': 'No controls have been added to the audit yet.',
  'tools.audit.addControlsPrompt': 'Use the form above to add controls to audit.',
  'tools.audit.allControlsAdded': 'All controls have been added to the audit checklist',
  'tools.audit.auditChecklist': 'Audit Checklist',
  'tools.audit.controls': 'controls',
  'tools.audit.addAnotherQuestion': 'Add Another Question',
  'tools.audit.selectControlPlaceholder': 'Select a control to audit',
  'tools.audit.addControlButton': 'Add Control',
  'tools.audit.noControlsYet': 'No Controls Added Yet',
  'tools.audit.selectControlsPrompt': 'Select controls from the dropdown above to begin your audit',
  'tools.audit.complianceStatus': 'Compliance Status',
  'tools.audit.notApplicable': 'Not Applicable',
  'tools.audit.evidenceReviewed': 'Document the evidence reviewed',
  'tools.audit.recordFindings': 'Record any findings, observations, or recommendations',
  'tools.audit.questionNotSpecified': 'Question not specified',
  'tools.audit.html.title': 'ISO 27001 Internal Audit Checklist',
  'tools.audit.html.auditDate': 'Audit Date',
  'tools.audit.html.auditInformation': 'Audit Information',
  'tools.audit.html.organization': 'Organization',
  'tools.audit.html.auditor': 'Auditor',
  'tools.audit.html.auditScope': 'Audit Scope',
  'tools.audit.html.auditObjective': 'Audit Objective',
  'tools.audit.html.auditSummary': 'Audit Summary',
  'tools.audit.html.overallCompliance': 'Overall Compliance Rate',
  'tools.audit.html.compliant': 'COMPLIANT',
  'tools.audit.html.nonCompliant': 'NON-COMPLIANT',
  'tools.audit.html.partial': 'PARTIAL',
  'tools.audit.html.notAssessed': 'NOT ASSESSED',
  'tools.audit.html.detailedFindings': 'Detailed Audit Findings',
  'tools.audit.html.noControlsAudited': 'No controls have been audited yet.',
  'tools.audit.html.footer': 'This internal audit checklist was generated for',
  'tools.audit.html.asPartOf': 'as part of ISO 27001 compliance efforts',
  'tools.audit.html.question': 'Audit Question',
  'tools.audit.html.compliance': 'Compliance',
  'tools.audit.html.evidence': 'Evidence',
  'tools.audit.html.findings': 'Findings/Notes',
  'tools.audit.html.controlId': 'Control ID',
  'tools.audit.html.controlTitle': 'Control Title',
  'tools.audit.fillRequired': 'Please fill in organization name and auditor fields',
  'tools.audit.previewExported': 'Preview HTML exported (limited controls)',
  'tools.audit.reportGenerated': 'HTML report generated successfully',
  'tools.audit.wordPreview': 'Preview Word document exported (header + first row only)',
  'tools.audit.wordSuccess': 'Word document exported successfully',
  'tools.audit.wordTitle': 'ISO 27001 Internal Audit Checklist and Findings',
  'tools.audit.defaultQuestion.policiesDocumented': 'Are information security policies documented and approved by management?',
  'tools.audit.defaultQuestion.policiesCommunicated': 'Are policies communicated to all relevant personnel?',
  'tools.audit.defaultQuestion.policiesReviewed': 'Are policies reviewed and updated regularly?',
  'tools.audit.defaultQuestion.authenticationMethods': 'Are secure authentication methods implemented (e.g., multi-factor authentication)?',
  'tools.audit.defaultQuestion.passwordPolicies': 'Are password policies enforced (complexity, length, expiration)?',
  'tools.audit.defaultQuestion.credentialsProtected': 'Are authentication credentials properly protected and stored?',
  'tools.audit.defaultQuestion.malwareControls': 'Are detection, prevention and recovery controls implemented against malware?',
  'tools.audit.defaultQuestion.antivirusDeployed': 'Is antivirus/anti-malware software deployed and regularly updated?',
  'tools.audit.defaultQuestion.malwareAwareness': 'Are users aware of malware risks and reporting procedures?',
  'tools.audit.defaultQuestion.backupsTaken': 'Are backup copies of information and software regularly taken?',
  'tools.audit.defaultQuestion.backupsTested': 'Are backup procedures tested regularly for effectiveness?',
  'tools.audit.defaultQuestion.backupsSecure': 'Are backups stored securely and in accordance with retention requirements?',

  'tools.roadmap.title': 'Implementation Roadmap Generator',
  'tools.roadmap.description': 'Create a customized timeline for your ISO 27001 certification journey',
  'tools.roadmap.info': 'This tool generates a tailored implementation roadmap based on your organization size, current security maturity, and available resources. The timeline includes all phases from initial preparation through certification audit.',
  'tools.roadmap.infoContent': 'This tool generates a tailored implementation roadmap based on your organization\'s size, current maturity level, and available resources. Get realistic timelines, milestones, and resource requirements for each phase.',
  'tools.roadmap.organizationProfile': 'Organization Profile',
  'tools.roadmap.organizationSize': 'Organization Size',
  'tools.roadmap.selectSize': 'Select size',
  'tools.roadmap.sizeSmall': 'Small (1-50 employees)',
  'tools.roadmap.sizeMedium': 'Medium (51-250 employees)',
  'tools.roadmap.sizeLarge': 'Large (251-1000 employees)',
  'tools.roadmap.sizeEnterprise': 'Enterprise (1000+ employees)',
  'tools.roadmap.securityMaturity': 'Current Security Maturity',
  'tools.roadmap.maturityNone': 'None - Starting from scratch',
  'tools.roadmap.maturityBasic': 'Basic - Some controls in place',
  'tools.roadmap.maturityIntermediate': 'Intermediate - Many controls implemented',
  'tools.roadmap.maturityAdvanced': 'Advanced - Mature security program',
  'tools.roadmap.projectStartDate': 'Project Start Date',
  'tools.roadmap.selectOption': 'Select option',
  'tools.roadmap.orgSize': 'Organization Size',
  'tools.roadmap.selectOrgSize': 'Select organization size',
  'tools.roadmap.small': 'Small (1-50 employees)',
  'tools.roadmap.medium': 'Medium (51-250 employees)',
  'tools.roadmap.large': 'Large (251-1000 employees)',
  'tools.roadmap.enterprise': 'Enterprise (1000+ employees)',
  'tools.roadmap.maturityLevel': 'Current Security Maturity',
  'tools.roadmap.selectMaturity': 'Select maturity level',
  'tools.roadmap.none': 'None - Starting from scratch',
  'tools.roadmap.basic': 'Basic - Some policies exist',
  'tools.roadmap.intermediate': 'Intermediate - Partial ISMS in place',
  'tools.roadmap.advanced': 'Advanced - Near ISO 27001 ready',
  'tools.roadmap.teamSize': 'Implementation Team Size',
  'tools.roadmap.teamSizePlaceholder': 'Number of team members',
  'tools.roadmap.dedicatedResource': 'Dedicated ISMS Resource?',
  'tools.roadmap.selectDedicated': 'Select',
  'tools.roadmap.dedicatedYes': 'Yes - Full-time ISMS lead',
  'tools.roadmap.dedicatedNo': 'No - Part-time only',
  'tools.roadmap.startDate': 'Project Start Date',
  'tools.roadmap.generateRoadmap': 'Generate Roadmap',
  'tools.roadmap.fillRequired': 'Please fill in all required fields',
  'tools.roadmap.generated': 'Implementation roadmap generated!',
  'tools.roadmap.generateFirst': 'Please generate the roadmap first',
  'tools.roadmap.projectOverview': 'Project Overview',
  'tools.roadmap.totalDuration': 'Total Duration',
  'tools.roadmap.months': 'months',
  'tools.roadmap.month': 'month',
  'tools.roadmap.targetCompletion': 'Target Completion',
  'tools.roadmap.implementationPhases': 'Implementation Phases',
  'tools.roadmap.duration': 'Duration',
  'tools.roadmap.keyActivities': 'Key Activities',
  'tools.roadmap.deliverables': 'Deliverables',
  'tools.roadmap.requiredResources': 'Required Resources',

  // Common HTML export translations
  'tools.html.notAssessed': 'Not assessed',
  'tools.html.notDefined': 'Not defined',
  'tools.html.notAssigned': 'Not assigned',
  'tools.html.notSet': 'Not set',
  'tools.html.control': 'Control',
  'tools.html.id': 'ID',
  'tools.html.status': 'Status',
  'tools.html.gap': 'Gap',
  'tools.html.priority': 'Priority',
  'tools.html.actionPlan': 'Action Plan',
  'tools.html.responsible': 'Responsible',
  'tools.html.target': 'Target',
  'tools.html.categoryControls': 'Controls',
  'tools.html.documentInfo': 'Document Information',
  'tools.html.assessor': 'Assessor',
  'tools.html.date': 'Date',
  'tools.html.scope': 'Scope',
  'tools.html.summaryStats': 'Summary Statistics',
  'tools.html.footer': 'This report was generated for',
  
  // Export messages
  'tools.export.previewExported': 'Preview exported (limited data)',
  'tools.export.reportGenerated': 'Report generated successfully',
  'tools.export.excelPreview': 'Preview Excel file exported (header + first row only)',
  'tools.export.excelSuccess': 'Excel file exported successfully',
  'tools.export.wordPreview': 'Preview Word document exported (header + first row only)',
  'tools.export.wordSuccess': 'Word document exported successfully',

  // Tools
  'tools.soa.launch': 'Launch Tool',
  'tools.risk.launch': 'Launch Tool',
  'tools.gap.launch': 'Launch Tool',
  'tools.audit.launch': 'Launch Tool',
  'tools.roadmap.launch': 'Launch Tool',

  // ISMS Scope Builder
  'tools.scope.title': 'ISMS Scope Builder',
  'tools.scope.description': 'Define your ISMS scope by selecting sites, systems, processes, and legal requirements',
  'tools.scope.infoContent': 'Build a comprehensive ISMS scope statement by identifying all locations, systems, processes, and legal requirements that fall within your information security management system.',
  'tools.scope.sites': 'Locations / Sites',
  'tools.scope.siteName': 'Site Name',
  'tools.scope.address': 'Address',
  'tools.scope.type': 'Type',
  'tools.scope.inScope': 'In Scope',
  'tools.scope.addSite': 'Add Site',
  'tools.scope.siteAdded': 'Site added',
  'tools.scope.atLeastOneSite': 'At least one site is required',
  'tools.scope.systems': 'Systems',
  'tools.scope.systemName': 'System Name',
  'tools.scope.systemDescription': 'Description',
  'tools.scope.category': 'Category',
  'tools.scope.addSystem': 'Add System',
  'tools.scope.systemAdded': 'System added',
  'tools.scope.atLeastOneSystem': 'At least one system is required',
  'tools.scope.processes': 'Business Processes',
  'tools.scope.processName': 'Process Name',
  'tools.scope.processDescription': 'Description',
  'tools.scope.processOwner': 'Owner',
  'tools.scope.addProcess': 'Add Process',
  'tools.scope.processAdded': 'Process added',
  'tools.scope.atLeastOneProcess': 'At least one process is required',
  'tools.scope.legalRequirements': 'Legal & Regulatory Requirements',
  'tools.scope.requirementName': 'Requirement',
  'tools.scope.requirementDescription': 'Description',
  'tools.scope.applicability': 'Applicability',
  'tools.scope.addLegal': 'Add Legal Requirement',
  'tools.scope.legalAdded': 'Legal requirement added',
  'tools.scope.atLeastOneLegal': 'At least one legal requirement is required',
  'tools.scope.exclusionsJustification': 'Exclusions & Justification',
  'tools.scope.exclusions': 'Scope Exclusions',
  'tools.scope.justification': 'Scope Justification',
  // Scope Builder Placeholders
  'tools.scope.placeholder.siteName': 'e.g., Main Office',
  'tools.scope.placeholder.address': 'Full address',
  'tools.scope.placeholder.systemName': 'e.g., ERP System',
  'tools.scope.placeholder.systemDescription': 'Brief description',
  'tools.scope.placeholder.processName': 'e.g., Order Processing',
  'tools.scope.placeholder.processDescription': 'Brief description',
  'tools.scope.placeholder.processOwner': 'Process owner',
  'tools.scope.placeholder.requirementName': 'e.g., GDPR',
  'tools.scope.placeholder.requirementDescription': 'Brief description',
  'tools.scope.placeholder.applicability': 'Where it applies',
  'tools.scope.placeholder.exclusions': 'List any areas, systems, or processes explicitly excluded from the ISMS scope and why...',
  'tools.scope.placeholder.justification': 'Provide justification for the defined scope boundaries...',
  'tools.scope.html.title': 'ISMS Scope Statement',
  'tools.scope.html.scopeOverview': 'Scope Overview',
  'tools.scope.html.scopeOverviewText': 'This document defines the scope of the Information Security Management System (ISMS) for',
  'tools.scope.html.isoCompliance': 'The ISMS has been established in accordance with ISO/IEC 27001:2022 requirements.',
  'tools.scope.html.locationsInScope': 'Locations / Sites in Scope',
  'tools.scope.html.systemsInScope': 'Systems in Scope',
  'tools.scope.html.processesInScope': 'Processes in Scope',
  'tools.scope.html.legalRequirements': 'Legal & Regulatory Requirements',
  'tools.scope.html.scopeExclusions': 'Scope Exclusions',
  'tools.scope.html.scopeJustification': 'Scope Justification',
  'tools.scope.html.siteName': 'Site Name',
  'tools.scope.html.address': 'Address',
  'tools.scope.html.type': 'Type',
  'tools.scope.html.status': 'Status',
  'tools.scope.html.inScope': 'In Scope',
  'tools.scope.html.systemName': 'System Name',
  'tools.scope.html.description': 'Description',
  'tools.scope.html.category': 'Category',
  'tools.scope.html.processName': 'Process Name',
  'tools.scope.html.processOwner': 'Process Owner',
  'tools.scope.html.requirement': 'Requirement',
  'tools.scope.html.applicability': 'Applicability',
  'tools.scope.excel.category': 'Category',
  'tools.scope.excel.name': 'Name',
  'tools.scope.excel.descriptionAddress': 'Description/Address',
  'tools.scope.excel.typeOwner': 'Type/Owner',
  'tools.scope.excel.inScope': 'In Scope',
  'tools.scope.excel.site': 'Site',
  'tools.scope.excel.system': 'System',
  'tools.scope.excel.process': 'Process',
  'tools.scope.excel.legalRequirement': 'Legal Requirement',
  'tools.scope.excel.yes': 'Yes',
  'tools.scope.excel.no': 'No',
  'tools.scope.systemCategory.it': 'IT Infrastructure',
  'tools.scope.systemCategory.application': 'Application',
  'tools.scope.systemCategory.network': 'Network',
  'tools.scope.systemCategory.cloud': 'Cloud Service',
  'tools.scope.systemCategory.ot': 'OT/Industrial',
  'scope.siteType.headquarters': 'Headquarters',
  'scope.siteType.branch': 'Branch',
  'scope.siteType.datacenter': 'Data Center',
  'scope.siteType.remote': 'Remote',

  // Asset Inventory
  'tools.assets.title': 'Asset Inventory',
  'tools.assets.description': 'Create and manage your information asset register with CIA ratings',
  'tools.assets.infoContent': 'Document all information assets including their owners, locations, and CIA (Confidentiality, Integrity, Availability) ratings for comprehensive asset management.',
  'tools.assets.assetEntry': 'Asset Entry',
  'tools.assets.assetName': 'Asset Name',
  'tools.assets.assetType': 'Asset Type',
  'tools.assets.classification': 'Classification',
  'tools.assets.owner': 'Asset Owner',
  'tools.assets.location': 'Location',
  'tools.assets.assetDescription': 'Description',
  'tools.assets.confidentiality': 'Confidentiality',
  'tools.assets.integrity': 'Integrity',
  'tools.assets.availability': 'Availability',
  'tools.assets.addAsset': 'Add Asset',
  'tools.assets.assetAdded': 'Asset added',
  'tools.assets.assetRemoved': 'Asset removed',
'tools.assets.atLeastOne': 'At least one asset is required',
  'tools.assets.html.title': 'Asset Inventory',
  'tools.assets.html.assetRegister': 'Asset Register',
  'tools.assets.html.assetDetails': 'Asset Details',
  'tools.assets.html.assetNumber': 'Asset',
  'tools.assets.html.assetName': 'Asset Name',
  'tools.assets.html.type': 'Type',
  'tools.assets.html.owner': 'Owner',
  'tools.assets.html.location': 'Location',
  'tools.assets.html.classification': 'Classification',
  'tools.assets.html.risk': 'Risk',
  'tools.assets.html.description': 'Description',
  'tools.assets.html.confidentialityRating': 'Confidentiality Rating',
  'tools.assets.html.integrityRating': 'Integrity Rating',
  'tools.assets.html.availabilityRating': 'Availability Rating',
  'tools.assets.html.overallRiskLevel': 'Overall Risk Level',
  'tools.assets.html.unnamedAsset': 'Unnamed Asset',
  'tools.assets.html.total': 'Total Assets',
  'tools.assets.html.footer': 'Asset Inventory Register for',
  'tools.assets.risk.critical': 'Critical',
  'tools.assets.risk.high': 'High',
  'tools.assets.risk.medium': 'Medium',
  'tools.assets.risk.low': 'Low',
  'tools.assets.type.hardware': 'Hardware',
  'tools.assets.type.software': 'Software',
  'tools.assets.type.data': 'Data/Information',
  'tools.assets.type.service': 'Service',
  'tools.assets.type.people': 'People',
  'tools.assets.type.facility': 'Facility',
  'tools.assets.type.network': 'Network',
  'tools.assets.classification.public': 'Public',
  'tools.assets.classification.internal': 'Internal',
  'tools.assets.classification.confidential': 'Confidential',
  'tools.assets.classification.restricted': 'Restricted',
  'tools.assets.excel.assetId': 'Asset ID',
  'tools.assets.excel.assetName': 'Asset Name',
  'tools.assets.excel.type': 'Type',
  'tools.assets.excel.owner': 'Owner',
  'tools.assets.excel.location': 'Location',
  'tools.assets.excel.classification': 'Classification',
  'tools.assets.excel.confidentiality': 'Confidentiality',
  'tools.assets.excel.integrity': 'Integrity',
  'tools.assets.excel.availability': 'Availability',
  'tools.assets.excel.overallRisk': 'Overall Risk',
  'tools.assets.excel.description': 'Description',
  // Asset Inventory Placeholders
  'tools.assets.placeholder.assetName': 'e.g., Customer Database',
  'tools.assets.placeholder.owner': 'e.g., IT Manager',
  'tools.assets.placeholder.location': 'e.g., Main Data Center',
  'tools.assets.placeholder.description': 'Brief description of the asset',

  // Annex A Control Selector
  'tools.annexSelector.title': 'Annex A Control Selector',
  'tools.annexSelector.description': 'Answer questions about your organization to get a tailored list of recommended ISO 27001 Annex A controls',
  'tools.annexSelector.infoContent': 'This tool helps you identify the most relevant Annex A controls based on your organization\'s size, technology usage, and data handling practices. Use this as a starting point for your Statement of Applicability.',
  'tools.annexSelector.recommendedControls': 'Recommended Controls',
  'tools.annexSelector.showingOf': 'Showing 5 of',
  'tools.annexSelector.upgradeToSeeAll': 'controls. Upgrade to Pro to see all.',
  'tools.annexSelector.startOver': 'Start Over',

  // Supplier Risk Manager
  'tools.supplier.title': 'Supplier Risk Manager',
  'tools.supplier.description': 'Assess and manage third-party supplier security risks',
  'tools.supplier.infoContent': 'Evaluate supplier risks based on data sensitivity, contractual controls, and geographic factors. Get actionable recommendations to mitigate third-party risks.',
  'tools.supplier.supplierEntry': 'Supplier Assessment',
  'tools.supplier.supplierName': 'Supplier Name',
  'tools.supplier.serviceType': 'Service Type',
  'tools.supplier.dataAccess': 'Data Access Level',
  'tools.supplier.contractExpiry': 'Contract Expiry',
  'tools.supplier.dataSensitivity': 'Data Sensitivity',
  'tools.supplier.contractStrength': 'Contract Strength',
  'tools.supplier.countryRisk': 'Country Risk',
  'tools.supplier.notes': 'Notes',
  'tools.supplier.recommendedActions': 'Recommended Actions',
  'tools.supplier.addSupplier': 'Add Supplier',
  'tools.supplier.supplierAdded': 'Supplier added',
  'tools.supplier.supplierRemoved': 'Supplier removed',
  'tools.supplier.atLeastOne': 'At least one supplier is required',
  // Supplier Risk Manager Placeholders
  'tools.supplier.placeholder.supplierName': 'e.g., Acme Cloud Services',
  'tools.supplier.placeholder.serviceType': 'e.g., Cloud Hosting',
  'tools.supplier.placeholder.dataAccess': 'e.g., Customer data, financial records',
  'tools.supplier.placeholder.notes': 'Additional notes or observations...',
  'tools.supplier.html.title': 'Supplier Risk Assessment',
  'tools.supplier.html.totalSuppliers': 'Total Suppliers',
  'tools.supplier.html.riskSummary': 'Supplier Risk Summary',
  'tools.supplier.html.supplier': 'Supplier',
  'tools.supplier.html.serviceType': 'Service Type',
  'tools.supplier.html.dataAccess': 'Data Access',
  'tools.supplier.html.riskScore': 'Risk Score',
  'tools.supplier.html.riskLevel': 'Risk Level',
  'tools.supplier.html.detailedAssessments': 'Detailed Assessments',
  'tools.supplier.html.supplierNumber': 'Supplier',
  'tools.supplier.html.risk': 'Risk',
  'tools.supplier.html.score': 'Score',
  'tools.supplier.html.dataSensitivityRating': 'Data Sensitivity Rating',
  'tools.supplier.html.contractStrengthRating': 'Contract Strength Rating',
  'tools.supplier.html.countryRiskRating': 'Country Risk Rating',
  'tools.supplier.html.dataAccessLevel': 'Data Access Level',
  'tools.supplier.html.contractExpiry': 'Contract Expiry',
  'tools.supplier.html.notSpecified': 'Not specified',
  'tools.supplier.html.notes': 'Notes',
  'tools.supplier.html.recommendedActions': 'Recommended Actions',
  'tools.supplier.html.footer': 'Supplier Risk Assessment for',
  'tools.supplier.excel.supplierName': 'Supplier Name',
  'tools.supplier.excel.serviceType': 'Service Type',
  'tools.supplier.excel.dataSensitivity': 'Data Sensitivity',
  'tools.supplier.excel.contractStrength': 'Contract Strength',
  'tools.supplier.excel.countryRisk': 'Country Risk',
  'tools.supplier.excel.dataAccess': 'Data Access',
  'tools.supplier.excel.riskScore': 'Risk Score',
  'tools.supplier.excel.riskLevel': 'Risk Level',
  'tools.supplier.excel.contractExpiry': 'Contract Expiry',
  'tools.supplier.excel.recommendedActions': 'Recommended Actions',

  // RTO RPO BIA Helper
  'tools.rtorpo.title': 'RTO, RPO & BIA Helper',
  'tools.rtorpo.description': 'Calculate Recovery Time Objectives, Recovery Point Objectives, and Business Impact Analysis',
  'tools.rtorpo.infoContent': 'Define impact levels for each business process to calculate appropriate RTO and RPO values. Generate a comprehensive Business Impact Analysis summary for your continuity planning.',
  'tools.rtorpo.processEntry': 'Business Process',
  'tools.rtorpo.processName': 'Process Name',
  'tools.rtorpo.processOwner': 'Process Owner',
  'tools.rtorpo.processDescription': 'Description',
  'tools.rtorpo.dependencies': 'Dependencies',
  'tools.rtorpo.financialImpact': 'Financial Impact',
  'tools.rtorpo.operationalImpact': 'Operational Impact',
  'tools.rtorpo.reputationalImpact': 'Reputational Impact',
  'tools.rtorpo.regulatoryImpact': 'Regulatory Impact',
  'tools.rtorpo.maxDowntime': 'Max Tolerable Downtime (hours)',
  'tools.rtorpo.dataLossTolerance': 'Data Loss Tolerance (hours)',
  'tools.rtorpo.biaSummary': 'BIA Summary',
  'tools.rtorpo.addProcess': 'Add Business Process',
  'tools.rtorpo.processAdded': 'Process added',
  'tools.rtorpo.processRemoved': 'Process removed',
  'tools.rtorpo.atLeastOne': 'At least one process is required',
  // RTO RPO BIA Placeholders
  'tools.rtorpo.placeholder.processName': 'e.g., Order Processing',
  'tools.rtorpo.placeholder.processOwner': 'e.g., Operations Manager',
  'tools.rtorpo.placeholder.processDescription': 'Brief description of the process',
  'tools.rtorpo.placeholder.dependencies': 'e.g., ERP System, Payment Gateway',
  'tools.rtorpo.excel.processName': 'Process Name',
  'tools.rtorpo.excel.owner': 'Owner',
  'tools.rtorpo.excel.financialImpact': 'Financial Impact',
  'tools.rtorpo.excel.operationalImpact': 'Operational Impact',
  'tools.rtorpo.excel.reputationalImpact': 'Reputational Impact',
  'tools.rtorpo.excel.regulatoryImpact': 'Regulatory Impact',
  'tools.rtorpo.excel.maxDowntime': 'Max Downtime (hrs)',
  'tools.rtorpo.excel.dataLossTolerance': 'Data Loss Tolerance (hrs)',
  'tools.rtorpo.excel.rto': 'RTO',
  'tools.rtorpo.excel.rpo': 'RPO',
  'tools.rtorpo.excel.criticality': 'Criticality',
  'tools.rtorpo.excel.biaSummary': 'BIA Summary',

  // Documents Page
  'documents.title': 'ISO 27001 Document Library',
  'documents.subtitle': 'Access our comprehensive collection of ISO 27001 policy templates, guides, and checklists.',
  'documents.aiNotice': 'These documents were built using AI with expert input and review.',
  'documents.searchPlaceholder': 'Search by name, description, or tags...',
  'documents.documentsAcross': 'documents across',
  'documents.categoriesLabel': 'categories',
  'documents.documentsLabel': 'documents',
  'documents.filters': 'Filters',
  'documents.clearAll': 'Clear All',
  'documents.clearFilters': 'Clear Filters',
  'documents.sortBy': 'Sort by',
  'documents.sortName': 'Name',
  'documents.sortCategory': 'Category',
  'documents.tags': 'Tags',
  'documents.showing': 'Showing',
  'documents.of': 'of',
  'documents.filtered': 'filtered',
  'documents.watermarked': 'Watermarked',
  'documents.more': 'more',
  'documents.editableWordAvailable': 'Editable Word available in the ISO 27001 Document Pack',
  'documents.noDocuments': 'No documents found',
  'documents.tryAdjusting': 'Try adjusting your search or filters',
  'documents.banner.title': 'PDF versions are free but watermarked',
  'documents.banner.subtitle': 'Get the ISO 27001 Document Pack for a one-time',
  'documents.cat.mandatory': 'Mandatory Policies',
  'documents.cat.recommended': 'Recommended Policies',
  'documents.cat.guides': 'Implementation Guides',
  'documents.cat.templates': 'Templates',
  'documents.cat.audit': 'Audit Checklists',
  'documents.downloadStarted': 'Download started!',
  'documents.generatingPreview': 'Generating preview...',
  'documents.previewDownloaded': 'Preview downloaded',
  'documents.preview': 'Preview',
  'documents.previewError': 'Failed to generate preview',
  'documents.pages': 'pages',
  'documents.previewDialog.title': 'Preview Only',
  'documents.previewDialog.description': 'As a free user, you will download a preview containing only the first 2 pages of this document. Upgrade to Pro to download the full document.',
  'documents.previewDialog.confirm': 'Download Preview',
  'documents.previewDialog.upgrade': 'Upgrade to Pro',
  'documents.generatingWatermarked': 'Generating watermarked PDF...',
  'documents.processingPage': 'Processing page',
  'documents.watermarkError': 'Failed to generate watermarked PDF',
  'documents.watermarkDialog.title': 'Watermarked PDF',
  'documents.watermarkDialog.description': "As a free user you can download the full document as a watermarked PDF. It is read-only and cannot be copied. To get the clean, editable Word version click 'Get Editable Word Version' to access the ISO 27001 Document Pack.",
  'documents.watermarkDialog.confirm': 'Download Watermarked',
  'documents.watermarkDialog.upgrade': 'Upgrade to Pro',
  'documents.watermarkDialog.getEditable': 'Get Editable Word Version',
  
  // Email Verification Dialog
  'documents.emailDialog.title': 'Verify Email to Download',
  'documents.emailDialog.verifyDescription': 'Enter your email to receive a verification link for',
  'documents.emailDialog.linkExpiry': 'The link expires in 30 minutes.',
  'documents.emailDialog.label': 'Email address',
  'documents.emailDialog.sendVerification': 'Send Verification Link',
  'documents.emailDialog.checkInbox': 'Check Your Inbox',
  'documents.emailDialog.sentTo': "We've sent a verification link to",
  'documents.emailDialog.clickToDownload': 'Click the link in the email to download your document.',
  'documents.emailDialog.didntReceive': "Didn't receive the email?",
  'documents.emailDialog.checkSpam': 'Check your spam or junk folder',
  'documents.emailDialog.waitMinute': 'Wait a minute and refresh',
  'documents.emailDialog.tryAgain': 'Try a different email address',
  'documents.emailDialog.tryDifferent': 'Try Different Email',
  'documents.emailDialog.verificationFailed': 'Verification Failed',
  'documents.emailDialog.genericError': 'Something went wrong. Please try again.',
  
  // Verification Success
  'documents.emailVerified': 'Email verified! Your download is ready.',
  'documents.verificationFailed': 'Verification failed. Please try again.',
  'documents.verificationSuccess.title': 'Email Verified!',
  'documents.verificationSuccess.ready': 'Your download is ready.',
  'documents.verificationSuccess.verifiedAs': 'Verified as',
  'documents.verificationSuccess.watermarkNote': 'Free downloads include a watermark. Get the ISO 27001 Document Pack for clean, editable documents.',
  'documents.downloadNow': 'Download Now',
  'documents.downloadWatermarked': 'Download Watermarked',
  'documents.getWordVersion': 'Get Word Version',
  'documents.getWordPack': 'Get Document Pack',
  'documents.convertingToImages': 'Converting to protected format...',
  
  // Common additions
  'common.sending': 'Sending...',
  'common.close': 'Close',
  'common.tryAgain': 'Try Again',
  'common.processing': 'Processing...',
  
  // Document names and descriptions
  'documents.doc.infoSecPolicy': 'Information Security Policy',
  'documents.doc.infoSecPolicy.desc': 'Top-level policy defining organization\'s approach to information security',
  'documents.doc.riskPolicy': 'Risk Assessment & Treatment Policy',
  'documents.doc.riskPolicy.desc': 'Framework for identifying and managing information security risks',
  'documents.doc.docControl': 'Document Control Policy',
  'documents.doc.docControl.desc': 'Procedures for managing and controlling ISMS documentation',
  'documents.doc.acceptableUse': 'Acceptable Use Policy',
  'documents.doc.acceptableUse.desc': 'Rules for acceptable use of IT resources',
  'documents.doc.accessControl': 'Access Control Policy',
  'documents.doc.accessControl.desc': 'Controls for managing user access to systems and data',
  'documents.doc.assetMgmt': 'Asset Management Policy',
  'documents.doc.assetMgmt.desc': 'Management and tracking of information assets',
  'documents.doc.backup': 'Backup Policy',
  'documents.doc.backup.desc': 'Procedures for data backup and recovery',
  'documents.doc.businessCont': 'Business Continuity Policy',
  'documents.doc.businessCont.desc': 'Ensuring business operations during disruptions',
  'documents.doc.byod': 'BYOD Policy',
  'documents.doc.byod.desc': 'Bring Your Own Device security requirements',
  'documents.doc.changeMgmt': 'Change Management Policy',
  'documents.doc.changeMgmt.desc': 'Controlling changes to IT systems and infrastructure',
  'documents.doc.clearDesk': 'Clear Desk & Clear Screen Policy',
  'documents.doc.clearDesk.desc': 'Protecting information through workspace security',
  'documents.doc.cloudSec': 'Cloud Security Policy',
  'documents.doc.cloudSec.desc': 'Security requirements for cloud services',
  'documents.doc.compliance': 'Compliance Policy',
  'documents.doc.compliance.desc': 'Ensuring compliance with legal and regulatory requirements',
  'documents.doc.crypto': 'Cryptography Policy',
  'documents.doc.crypto.desc': 'Use of encryption and cryptographic controls',
  'documents.doc.dataClass': 'Data Classification & Handling Policy',
  'documents.doc.dataClass.desc': 'Classifying and handling sensitive information',
  'documents.doc.dataProtection': 'Data Protection Policy Template',
  'documents.doc.dataProtection.desc': 'GDPR and data protection compliance',
  'documents.doc.emailSec': 'Email Security Policy',
  'documents.doc.emailSec.desc': 'Secure use of email systems',
  'documents.doc.endpoint': 'Endpoint Security Policy',
  'documents.doc.endpoint.desc': 'Securing end-user devices and workstations',
  'documents.doc.hrSecurity': 'Human Resources Security Policy',
  'documents.doc.hrSecurity.desc': 'Security aspects of employment lifecycle',
  'documents.doc.incident': 'Incident Management Policy',
  'documents.doc.incident.desc': 'Detecting and responding to security incidents',
  'documents.doc.infoClass': 'Information Classification Policy',
  'documents.doc.infoClass.desc': 'Framework for classifying information assets',
  'documents.doc.itAudit': 'IT Security Audit Policy',
  'documents.doc.itAudit.desc': 'Internal auditing of information security controls',
  'documents.doc.logging': 'Logging & Monitoring Policy',
  'documents.doc.logging.desc': 'System logging and security monitoring',
  'documents.doc.mobileRemote': 'Mobile Device & Remote Working Policy',
  'documents.doc.mobileRemote.desc': 'Security for mobile devices and remote workers',
  'documents.doc.networkSec': 'Network Security Policy',
  'documents.doc.networkSec.desc': 'Securing network infrastructure and communications',
  'documents.doc.password': 'Password Policy',
  'documents.doc.password.desc': 'Password requirements and management',
  'documents.doc.physEnvSec': 'Physical & Environmental Security Policy',
  'documents.doc.physEnvSec.desc': 'Physical protection of facilities and equipment',
  'documents.doc.physSec': 'Physical Security Policy',
  'documents.doc.physSec.desc': 'Physical access controls and security measures',
  'documents.doc.privacy': 'Privacy Policy',
  'documents.doc.privacy.desc': 'Personal data protection and privacy',
  'documents.doc.remoteAccess': 'Remote Access Policy',
  'documents.doc.remoteAccess.desc': 'Secure remote access to organizational systems',
  'documents.doc.secureDev': 'Secure Development Policy',
  'documents.doc.secureDev.desc': 'Security in software development lifecycle',
  'documents.doc.socialMedia': 'Social Media Security Policy',
  'documents.doc.socialMedia.desc': 'Safe use of social media platforms',
  'documents.doc.supplier': 'Supplier Relationship Security Policy',
  'documents.doc.supplier.desc': 'Managing security with suppliers and vendors',
  'documents.doc.thirdParty': 'Third Party Security Policy',
  'documents.doc.thirdParty.desc': 'Security requirements for third-party relationships',
  'documents.doc.vulnMgmt': 'Vulnerability Management Policy',
  'documents.doc.vulnMgmt.desc': 'Identifying and remediating security vulnerabilities',
  'documents.doc.implGuide': 'ISO 27001 Implementation Guide',
  'documents.doc.implGuide.desc': 'Step-by-step guide for ISO 27001 implementation',
  'documents.doc.toolkitGuide': 'ISO 27001 Toolkit User Guide',
  'documents.doc.toolkitGuide.desc': 'How to use this toolkit effectively',
  'documents.doc.research': 'ISO 27001 Research Summary',
  'documents.doc.research.desc': 'Research insights on ISO 27001 implementation',
  'documents.doc.policyList': 'Policy Template List',
  'documents.doc.policyList.desc': 'Complete list of available policy templates',
  'documents.doc.requiredDocs': 'Required Documents List',
  'documents.doc.requiredDocs.desc': 'All mandatory and recommended documents',
  'documents.doc.todoList': 'Implementation To-Do List',
  'documents.doc.todoList.desc': 'Step-by-step implementation checklist',
  'documents.doc.gapTool': 'Gap Analysis Tool',
  'documents.doc.gapTool.desc': 'Template for conducting gap analysis',
  'documents.doc.infoSecTemplate': 'Information Security Policy Template',
  'documents.doc.infoSecTemplate.desc': 'Customizable information security policy template',
  'documents.doc.riskTemplate': 'Risk Assessment Template',
  'documents.doc.riskTemplate.desc': 'Template for risk assessment documentation',
  'documents.doc.soaTemplate': 'Statement of Applicability Template',
  'documents.doc.soaTemplate.desc': 'Template for creating Statement of Applicability',
  'documents.doc.auditChecklist': 'Internal Audit Checklist',
  'documents.doc.auditChecklist.desc': 'Comprehensive internal audit checklist',
  'documents.doc.validationChecklist': 'Validation Checklist',
  'documents.doc.validationChecklist.desc': 'Validation checklist for ISMS controls',
  'documents.doc.validationReport': 'Validation Report',
  'documents.doc.validationReport.desc': 'Template for validation reporting',

  // Document Tags
  'documents.tag.mandatory': 'Mandatory',
  'documents.tag.policy': 'Policy',
  'documents.tag.governance': 'Governance',
  'documents.tag.risk': 'Risk',
  'documents.tag.compliance': 'Compliance',
  'documents.tag.HR': 'HR',
  'documents.tag.user-behavior': 'User Behavior',
  'documents.tag.technical': 'Technical',
  'documents.tag.access-management': 'Access Management',
  'documents.tag.operations': 'Operations',
  'documents.tag.inventory': 'Inventory',
  'documents.tag.business-continuity': 'Business Continuity',
  'documents.tag.disaster-recovery': 'Disaster Recovery',
  'documents.tag.mobile': 'Mobile',
  'documents.tag.physical-security': 'Physical Security',
  'documents.tag.cloud': 'Cloud',
  'documents.tag.encryption': 'Encryption',
  'documents.tag.data-protection': 'Data Protection',
  'documents.tag.classification': 'Classification',
  'documents.tag.privacy': 'Privacy',
  'documents.tag.template': 'Template',
  'documents.tag.communications': 'Communications',
  'documents.tag.devices': 'Devices',
  'documents.tag.personnel': 'Personnel',
  'documents.tag.incident-response': 'Incident Response',
  'documents.tag.audit': 'Audit',
  'documents.tag.monitoring': 'Monitoring',
  'documents.tag.remote-work': 'Remote Work',
  'documents.tag.network': 'Network',
  'documents.tag.authentication': 'Authentication',
  'documents.tag.facilities': 'Facilities',
  'documents.tag.development': 'Development',
  'documents.tag.third-party': 'Third Party',
  'documents.tag.vendor-management': 'Vendor Management',
  'documents.tag.guide': 'Guide',
  'documents.tag.implementation': 'Implementation',
  'documents.tag.getting-started': 'Getting Started',
  'documents.tag.documentation': 'Documentation',
  'documents.tag.research': 'Research',
  'documents.tag.reference': 'Reference',
  'documents.tag.checklist': 'Checklist',
  'documents.tag.assessment': 'Assessment',
  'documents.tag.gap-analysis': 'Gap Analysis',
  'documents.tag.SoA': 'SoA',
  'documents.tag.validation': 'Validation',
  'documents.tag.reporting': 'Reporting',

  // Blog
  'blog.title': 'ISO 27001 Blog',
  'blog.subtitle': 'Expert insights, implementation guides, and best practices for ISO 27001 compliance and information security management.',
  'blog.featured': 'Featured Articles',
  'blog.featuredBadge': 'Featured',
  'blog.categories': 'Categories',
  'blog.allPosts': 'All Articles',
  'blog.readTime': 'min read',
  'blog.readMore': 'Read full article',
  'blog.by': 'By',
  'blog.share': 'Share',
  'blog.relatedPosts': 'Related Articles',
  'blog.latestTitle': 'Latest from the Blog',
  'blog.viewAll': 'View all articles',
  'blog.backToBlog': 'Back to Blog',
  'blog.notFound': 'Article Not Found',
  'blog.notFoundDesc': 'The article you are looking for does not exist.',
  'blog.cta.title': 'Ready to Start Your ISO 27001 Journey?',
  'blog.cta.subtitle': 'Use our free tools to assess your current security posture and plan your implementation.',
  'blog.cta.gapAnalysis': 'Start Gap Analysis',
  'blog.cta.browseTemplates': 'Browse Templates',
  'blog.sidebar.ctaTitle': 'Start Your ISO 27001 Journey',
  'blog.sidebar.ctaDesc': 'Use our free tools to assess and plan your implementation.',
  'blog.sidebar.gapAnalysis': 'Gap Analysis',
  'blog.sidebar.riskAssessment': 'Risk Assessment',
  'blog.sidebar.soaGenerator': 'SoA Generator',
  'blog.sidebar.internalAudit': 'Internal Audit',
  'blog.sidebar.roadmap': 'Implementation Roadmap',

  // Related Resources
  'relatedResources.title': 'Related Resources',
  'relatedResources.tools': 'Tools',
  'relatedResources.documents': 'Documents',
  'relatedResources.infoSecPolicy': 'Information Security Policy',
  'relatedResources.riskTemplate': 'Risk Assessment Template',
  'relatedResources.implGuide': 'Implementation Guide',
  'relatedResources.auditChecklist': 'Internal Audit Checklist',

  // Consultation keys moved to bottom of file (consolidated)

  // Contact keys moved to bottom of file (consolidated)

  // Auth and Account keys moved to bottom of file (consolidated)

  // Pricing
  'pricing.title': 'Choose Your Plan',
  'pricing.subtitle': 'Select the plan that best fits your needs.',
  'pricing.currentPlan': 'Current Plan',
  'pricing.free.title': 'Free',
  'pricing.free.price': '$0',
  'pricing.free.period': 'forever',
  'pricing.free.feature1': 'All ISO 27001 policies and templates in PDF',
  'pricing.free.feature2': 'All guides and the full site',
  'pricing.free.feature3': 'Run tools with your own data and see a short preview only',
  'pricing.free.feature4': 'Excel and Word exports limited to header plus first row',
  'pricing.free.feature5': 'No project save or load',
  'pricing.pro.title': 'Pro',
  'pricing.pro.badge': 'Best for consultants',
  'pricing.pro.perMonth': '/ month',
  'pricing.pro.orYearly': `or $${PRO_Y} USD per year`,
  'pricing.pro.feature1': 'Everything in Free',
  'pricing.pro.feature2': 'Full use of all ISO 27001 tools',
  'pricing.pro.feature3': 'See complete results for SoA, risk register, gaps, audits, and roadmap',
  'pricing.pro.feature4': 'Export clean Excel and Word files with all rows',
  'pricing.pro.feature5': 'Save and load projects',
  'pricing.pro.upgrade': 'Upgrade to Pro',
  'pricing.pro.disclaimer': 'You can cancel your subscription anytime from your account. You keep full access until the end of the paid month or year, then your account moves back to review mode.',
  'pricing.pro.agreeToRefund': 'By upgrading to Pro you agree to our',
  'pricing.wordPack.title': 'ISO 27001 Document Pack',
  'pricing.wordPack.badge': 'One-time',
  'pricing.wordPack.oneTime': 'one-time',
  'pricing.wordPack.description': '40+ professionally written ISO 27001 policies, procedures, and templates in editable Word format, plus 2 Excel registers (Risk Register and Statement of Applicability). No watermarks. Instant download.',
  'pricing.wordPack.feature1': '40+ ISO 27001 documents in clean Word format',
  'pricing.wordPack.feature2': '2 Excel registers: Risk Register & Statement of Applicability',
  'pricing.wordPack.feature3': 'No watermarks - ready to edit',
  'pricing.wordPack.feature4': 'Download as a single ZIP file',
  'pricing.wordPack.feature5': 'One-time payment - yours forever',
  'pricing.wordPack.download': 'Download ISO 27001 Document Pack',
  'pricing.wordPack.get': 'Get the Editable Pack →',

  // Pricing Page (full page)
  'pricingPage.hero.title': 'ISO 27001 Toolkit Pricing',
  'pricingPage.hero.subtitle': 'Everything you need to achieve ISO 27001 certification - from free templates to a full risk management platform. No hidden fees. Cancel anytime.',
  'pricingPage.billing.monthly': 'Monthly',
  'pricingPage.billing.yearly': 'Yearly',
  'pricingPage.billing.save': 'Save ${amount}',
  'pricingPage.billing.billedYearly': 'Billed yearly - works out to ${amount}/mo',
  'pricingPage.billing.orYearly': 'Or save with yearly: ${amount}/yr',
  'pricingPage.plan.free.name': 'Free',
  'pricingPage.plan.free.feature1': 'Tool previews with sample output',
  'pricingPage.plan.free.feature2': 'PDF previews (first page only)',
  'pricingPage.plan.free.feature3': 'All guides and full site access',
  'pricingPage.plan.free.feature4': 'Exports limited to first row',
  'pricingPage.plan.free.cta': 'Get Started Free',
  'pricingPage.plan.pro.name': 'Pro',
  'pricingPage.plan.pro.badge': 'Subscription',
  'pricingPage.plan.pro.feature1': 'Everything in Free',
  'pricingPage.plan.pro.feature2': 'Full use of all 11 ISO 27001 tools',
  'pricingPage.plan.pro.feature3': `ISO 27001 AI Assistant - ${PRO_MSG} messages/day (${Math.round(PRO_MSG / PLUS_MSG)}x the Plus tier)`,
  'pricingPage.plan.pro.feature4': 'Complete exports - Excel and Word with all rows',
  'pricingPage.plan.pro.feature5': 'Save and load projects',
  'pricingPage.plan.pro.feature6': 'SoA, risk register, gap analysis, audit results',
  'pricingPage.plan.pro.feature7': 'Full Risk Copilot platform (12 modules)',
  'pricingPage.plan.pro.feature8': 'Persistent data with audit trail',
  'pricingPage.plan.pro.feature9': 'Custom risk framework and matrix',
  'pricingPage.plan.pro.feature10': '180-day review cycle tracking',
  'pricingPage.plan.pro.ctaYearly': 'Upgrade - Yearly',
  'pricingPage.plan.pro.ctaMonthly': 'Upgrade - Monthly',
  'pricingPage.plan.pro.note': 'Cancel anytime. Full access until end of billing period.',
  'pricingPage.plan.docPack.name': 'Document Pack',
  'pricingPage.plan.docPack.period': ' one-time',
  'pricingPage.plan.docPack.badge': 'Most Popular',
  'pricingPage.plan.docPack.feature1': '40+ documents in clean Word format',
  'pricingPage.plan.docPack.feature2': '2 Excel registers (Risk Register & Statement of Applicability)',
  'pricingPage.plan.docPack.feature3': 'No watermarks - ready to edit',
  'pricingPage.plan.docPack.feature4': 'Download as a single ZIP file',
  'pricingPage.plan.docPack.feature5': 'Yours forever',
  'pricingPage.plan.docPack.ctaOwn': 'Download Document Pack',
  'pricingPage.plan.docPack.ctaBuy': 'Get the Editable Pack',
  'pricingPage.enterprise.name': 'Enterprise',
  'pricingPage.enterprise.price': 'Custom',
  'pricingPage.enterprise.badge': 'For larger teams',
  'pricingPage.enterprise.tagline': 'Tailored plans for organizations with specific compliance, scale, or integration needs.',
  'pricingPage.enterprise.feature1': 'Everything in Pro',
  'pricingPage.enterprise.feature2': 'Multi-user access and team management',
  'pricingPage.enterprise.feature3': 'Custom onboarding and training',
  'pricingPage.enterprise.feature4': 'Tailored compliance support',
  'pricingPage.enterprise.feature5': 'Dedicated account contact',
  'pricingPage.enterprise.feature6': 'Custom integrations on request',
  'pricingPage.enterprise.note': 'We\'ll reply within one business day.',
  'pricingPage.enterprise.contact': 'Contact us',
  'pricingPage.enterprise.ribbon': 'Enterprise Level',
  'pricingPage.enterprise.learnMore': 'Learn more about enterprise plans',
  'pricingPage.included.title': "What's Included at Every Level",
  'pricingPage.included.tools.title': '11 Implementation Tools',
  'pricingPage.included.tools.desc': 'SoA Generator, Gap Analysis, Risk Assessment, Internal Audit, and more - all browser-based and export-ready.',
  'pricingPage.included.templates.title': '40+ Policy Templates',
  'pricingPage.included.templates.desc': 'Pre-written, ISO 27001-aligned policies and registers available as free PDFs or as editable Word documents and Excel registers in the Document Pack.',
  'pricingPage.included.copilot.title': 'Risk Copilot Platform',
  'pricingPage.included.copilot.desc': 'A 12-module risk management suite with persistent data, audit trails, and compliance-ready exports - included in Pro.',
  'pricingPage.faqCta.title': 'Still have questions?',
  'pricingPage.faqCta.subtitle': "Check our FAQ or reach out - we're happy to help you pick the right plan.",
  'pricingPage.faqCta.faq': 'Visit FAQ',
  'pricingPage.faqCta.contact': 'Contact Us',

  // AI Assistant Plus pricing card
  'aiPlus.badge': 'AI Assistant',
  'aiPlus.title': 'ISO 27001 AI Assistant - Plus',
  'aiPlus.context': `Standalone subscription for the AI Assistant only. Free tier includes ${FREE_MSG} messages/day. Pro subscribers get ${PRO_MSG} messages/day included.`,
  'aiPlus.price': `$${PLUS}`,
  'aiPlus.period': '/month',
  'aiPlus.feature1': `${PLUS_MSG} messages per day`,
  'aiPlus.feature2': 'Implementation, controls, risk and policy guidance',
  'aiPlus.feature3': 'Cancel anytime',
  'aiPlus.cta': `Get Plus - $${PLUS}/mo`,
  'aiPlus.learnMore': 'Learn more about AI Assistant Plus',

  // Privacy Policy (rewritten 2026 version)
  'privacy.s1.title': '1. Who we are',
  'privacy.s1.p1': 'ISO27001KIT (operated under iso27001kit.com, the "Service") provides ISO 27001 implementation tools, document templates, an AI Assistant, the Risk Copilot platform, and the ISO 27001 Audit Room workspace. This Privacy Policy explains what information we collect, why we collect it, how we use it, and the choices you have.',
  'privacy.s1.p2': 'The data controller is the operator of iso27001kit.com. You can contact us at',
  'privacy.s2.title': '2. Information we collect',
  'privacy.s2.h1': '2.1 Account information',
  'privacy.s2.p1': 'When you create an account we collect:',
  'privacy.s2.li1': 'Email address (required for sign-in and account recovery)',
  'privacy.s2.li2': 'Full name (optional)',
  'privacy.s2.li3': 'Phone number (optional)',
  'privacy.s2.li4': 'A securely hashed password, or your Google account identifier if you sign in with Google',
  'privacy.s2.h2': '2.2 Email verification (anonymous tools)',
  'privacy.s2.p2': 'To use certain free tools without an account, we ask you to verify your email address. We store your email, a one-time verification token, the document or tool you requested, the time of the request, and the IP address used to verify, so we can prevent abuse and deliver the requested resource.',
  'privacy.s2.h3': '2.3 Payment and subscription information',
  'privacy.s2.p3': 'Payments are processed by our payment provider, Lemon Squeezy. We do not see or store your full card number, CVV, or banking details. We receive and store: order ID, subscription ID, product purchased, billing email, country, plan status, and renewal/cancellation events. This information is used to fulfil your purchase, manage your subscription, and comply with tax and accounting obligations.',
  'privacy.s2.h4': '2.4 Tool and project content',
  'privacy.s2.p4': "When you use our implementation tools (SoA Generator, Gap Analysis, Risk Assessment, Asset Inventory, ISMS Scope Builder, ISO 27001 Audit Room checklist, etc.), the data you enter is held in your browser's local storage and, for Pro users, can be saved to your account. We treat this content as confidential and only access it where strictly necessary for support or to operate the Service.",
  'privacy.s2.h5': '2.5 Risk Copilot data',
  'privacy.s2.p5': 'When you use Risk Copilot we store the records you create (risks, assets, controls, treatments, threats, vulnerabilities, business processes, incidents, audit log entries, settings) in our backend database, scoped to your user account by row-level security. Only you can read or modify your records; we do not access them except where required to operate the Service or where you ask us to provide support.',
  'privacy.s2.h6': '2.6 AI Assistant conversations',
  'privacy.s2.p6': 'When you use the ISO 27001 AI Assistant, your messages are sent to our AI provider (Google Gemini, via the Lovable AI Gateway) to generate a response. We track metadata such as the number of messages you have used per day to enforce plan limits. We recommend you do not paste sensitive client data, secrets, or regulated personal data into the AI Assistant.',
  'privacy.s2.h7': '2.7 Click and usage analytics',
  'privacy.s2.p7': 'We log a small amount of metadata about clicks on key buttons (button identifier, page path, timestamp, and your user ID if signed in) to understand which features are useful. We do not log the content of forms, exports, or AI conversations in this analytics stream. Click events are retained for up to 90 days.',
  'privacy.s2.h8': '2.8 Automatically collected technical data',
  'privacy.s2.li5': 'IP address (used for verification, fraud prevention, and abuse limits)',
  'privacy.s2.li6': 'Browser type and version, device type, screen size',
  'privacy.s2.li7': 'Pages visited, referring URL, and basic timing information',
  'privacy.s2.li8': 'Microsoft Clarity may record session interactions (heatmaps, scroll, clicks) on a sampled basis to help us improve the user experience',
  'privacy.s3.title': '3. How we use your information',
  'privacy.s3.intro': 'We process your data to:',
  'privacy.s3.li1': 'Create and manage your account and verify your identity',
  'privacy.s3.li2': 'Process payments and provision your subscription or Document Pack',
  'privacy.s3.li3': 'Deliver the tools, templates, AI Assistant, Risk Copilot, and ISO 27001 Audit Room you requested',
  'privacy.s3.li4': 'Send transactional emails (purchase confirmations, password resets, download links, verification codes)',
  'privacy.s3.li5': 'Send product update emails to registered users (you can opt out at any time)',
  'privacy.s3.li6': 'Provide customer support',
  'privacy.s3.li7': 'Detect, prevent, and respond to fraud, abuse, and security incidents',
  'privacy.s3.li8': 'Improve the Service through aggregated usage analysis',
  'privacy.s3.li9': 'Comply with legal, tax, and accounting obligations',
  'privacy.s3.bases': 'Our legal bases under the GDPR are: performance of a contract (operating your account and delivering purchases), legitimate interest (fraud prevention, security, service improvement), consent (where required, e.g. optional analytics), and legal obligation (tax, accounting).',
  'privacy.s4.title': '4. Service providers we share data with',
  'privacy.s4.intro': 'We do not sell your personal data. We share the minimum necessary information with vetted service providers acting as processors on our behalf:',
  'privacy.s4.li1.b': 'Lovable Cloud (Supabase infrastructure):',
  'privacy.s4.li1.t': 'hosting, authentication, database, edge functions, and file storage.',
  'privacy.s4.li2.b': 'Lemon Squeezy:',
  'privacy.s4.li2.t': 'payment processing, subscription billing, and merchant-of-record tax handling.',
  'privacy.s4.li3.b': 'Lovable AI Gateway:',
  'privacy.s4.li3.t': 'routes requests from the AI Assistant to the underlying AI model providers.',
  'privacy.s4.li4.b': 'Google (Gemini models, accessed via the Lovable AI Gateway):',
  'privacy.s4.li4.t': 'generates AI Assistant responses from the messages you send.',
  'privacy.s4.li5.b': 'SMTP email provider (Hostinger):',
  'privacy.s4.li5.t': 'delivers transactional emails such as verification codes, password resets, and purchase confirmations.',
  'privacy.s4.li6.b': 'Microsoft Clarity:',
  'privacy.s4.li6.t': 'sampled session analytics for usability research.',
  'privacy.s4.li7.b': 'Google OAuth:',
  'privacy.s4.li7.t': 'only if you choose to sign in with Google.',
  'privacy.s4.outro': 'We may also disclose information when required by law, to protect our rights or the safety of users, or in connection with a corporate transaction (with notice to affected users where feasible).',
  'privacy.s5.title': '5. International transfers',
  'privacy.s5.p1': 'Some of our service providers are located outside your country, including in the United States. Where personal data is transferred outside the EEA or UK, we rely on appropriate safeguards such as the EU Standard Contractual Clauses and provider certifications.',
  'privacy.s6.title': '6. Cookies and local storage',
  'privacy.s6.intro': 'We use a limited set of cookies and browser storage:',
  'privacy.s6.li1.b': 'Authentication:',
  'privacy.s6.li1.t': 'to keep you signed in to your account.',
  'privacy.s6.li2.b': 'Local storage:',
  'privacy.s6.li2.t': 'to save your tool inputs, theme preference, and Risk Copilot organization name on your device.',
  'privacy.s6.li3.b': 'Analytics:',
  'privacy.s6.li3.t': 'Microsoft Clarity uses cookies to record sampled interactions for usability research.',
  'privacy.s6.outro': 'You can clear cookies and local storage at any time in your browser settings. Disabling authentication cookies will sign you out.',
  'privacy.s7.title': '7. Security',
  'privacy.s7.intro': 'We protect your data using industry-standard measures, including:',
  'privacy.s7.li1': 'HTTPS/TLS for all traffic between your browser and our servers',
  'privacy.s7.li2': 'Hashed passwords (we never store plaintext passwords)',
  'privacy.s7.li3': 'Row-level security policies in the database so each user only sees their own data',
  'privacy.s7.li4': 'JWT verification on protected edge functions',
  'privacy.s7.li5': 'Restricted, least-privilege access for the small team that operates the Service',
  'privacy.s7.outro': 'No system is 100% secure. If we ever become aware of a breach affecting your personal data, we will notify you and the relevant authorities as required by law.',
  'privacy.s8.title': '8. Data retention',
  'privacy.s8.li1.b': 'Account data:',
  'privacy.s8.li1.t': 'kept for as long as your account is active, then deleted on request.',
  'privacy.s8.li2.b': 'Subscription and billing records:',
  'privacy.s8.li2.t': 'kept for the period required by tax and accounting law (typically up to 7-10 years).',
  'privacy.s8.li3.b': 'Email verification tokens:',
  'privacy.s8.li3.t': 'expire automatically and are cleaned up shortly after.',
  'privacy.s8.li4.b': 'Click analytics:',
  'privacy.s8.li4.t': 'retained for up to 90 days.',
  'privacy.s8.li5.b': 'Risk Copilot records and tool data:',
  'privacy.s8.li5.t': 'kept until you delete them or close your account.',
  'privacy.s8.li6.b': 'AI Assistant usage counters:',
  'privacy.s8.li6.t': 'reset daily; we do not retain conversation history beyond what is needed to deliver the response.',
  'privacy.s9.title': '9. Your rights',
  'privacy.s9.intro': 'Depending on where you live, you may have the right to:',
  'privacy.s9.li1': 'Access the personal data we hold about you',
  'privacy.s9.li2': 'Correct inaccurate data',
  'privacy.s9.li3': 'Delete your account and associated data',
  'privacy.s9.li4': 'Cancel your subscription at any time',
  'privacy.s9.li5': 'Object to or restrict certain processing',
  'privacy.s9.li6': 'Withdraw consent where processing is based on consent',
  'privacy.s9.li7': 'Export your data in a portable format',
  'privacy.s9.li8': 'Lodge a complaint with your local data protection authority',
  'privacy.s9.contactPre': 'To exercise any of these rights, email us at',
  'privacy.s9.contactPost': 'from the address linked to your account. We respond within 30 days.',
  'privacy.s10.title': '10. Children',
  'privacy.s10.p1': 'The Service is intended for professionals and is not directed to children under 16. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, contact us and we will delete it.',
  'privacy.s11.title': '11. Third-party links and content',
  'privacy.s11.p1': 'Our Service may contain links to third-party websites (for example, our payment providers). We are not responsible for the privacy practices of those sites and recommend you read their own privacy policies.',
  'privacy.s12.title': '12. Changes to this policy',
  'privacy.s12.p1': 'We may update this Privacy Policy from time to time. When we make material changes we will update the "Last updated" date at the top and, where appropriate, notify you by email or in-app notice.',
  'privacy.s13.title': '13. Contact',
  'privacy.s13.p1': 'For any privacy-related question, contact:',

  // Footer
  'footer.description': 'Your complete toolkit for ISO 27001 compliance.',
  'footer.quickLinks': 'Quick Links',
  'footer.resources': 'Resources',
  'footer.legal': 'Legal',
  'footer.termsOfService': 'Terms of Service',
  'footer.privacyPolicy': 'Privacy Policy',
  'footer.refundPolicy': 'Refund Policy',
  'footer.disclaimer': 'Disclaimer',
  'footer.copyright': '© 2026 ISO27001KIT. All rights reserved.',
  'footer.language': 'Language',

  // About keys moved to bottom of file (consolidated)

  // FAQ keys moved to bottom of file (consolidated)

  // Export
  'export.toExcel': 'Export to Excel',
  'export.toWord': 'Export to Word',
  'export.toPdf': 'Export to PDF',
  'export.toHtml': 'Export to HTML',
  'export.proRequired': 'Pro subscription required for full export',

  // Errors
  'error.notFound': 'Page Not Found',
  'error.notFoundDesc': 'The page you are looking for does not exist.',
  'error.goHome': 'Go to Homepage',

  // Contact Page
  "contact.title": "Contact Us",
  "contact.intro": "Need a customized ISO 27001 Implementation Kit or have questions about our documents? Fill out the form below and we'll get back to you as soon as possible.",
  "contact.form.name": "Name",
  "contact.form.email": "Email",
  "contact.form.organization": "Organization",
  "contact.form.subject": "Subject",
  "contact.form.subjectPlaceholder": "Select a subject",
  "contact.form.subjectConsultation": "Book 30-Min Consultation",
  "contact.form.subjectCustomized": "Customized ISO 27001 Kit",
  "contact.form.subjectQuestion": "Question about Documents",
  "contact.form.subjectSupport": "Technical Support",
  "contact.form.subjectOther": "Other",
  "contact.form.message": "Message",
  "contact.form.submit": "Send Message",
  "contact.toast.title": "Message Sent",
  "contact.toast.description": "Thank you for contacting us. We'll get back to you soon.",
  "contact.consultation.title": "Book a Consultation",
  "contact.consultation.description": "Need expert guidance on your ISO 27001 implementation? Book a 30-minute consultation to discuss your specific needs and get personalized recommendations.",
  "contact.consultation.button": "Book Your 30-Min Call",
  "contact.consultation.email": "Email:",
  "contact.website.title": "Visit Our Website",

  // Consultation Page
  "consultation.hero.title": "Book Your ISO 27001 Consultation",
  "consultation.hero.description": "Get personalized expert guidance for your ISO 27001 implementation journey. Schedule a 30-minute consultation to discuss your specific needs and challenges.",
  "consultation.hero.bookButton": "Book Your Consultation",
  "consultation.hero.contactButton": "Contact Us",
  "consultation.whatYouGet.title": "What You'll Get",
  "consultation.whatYouGet.session.title": "30-Minute Session",
  "consultation.whatYouGet.session.description": "A focused 30-minute video or phone consultation to address your most pressing ISO 27001 questions and concerns.",
  "consultation.whatYouGet.expert.title": "Expert Guidance",
  "consultation.whatYouGet.expert.description": "Direct access to an experienced ISO 27001 professional who has helped organizations achieve certification.",
  "consultation.whatYouGet.tailored.title": "Tailored Advice",
  "consultation.whatYouGet.tailored.description": "Personalized recommendations based on your organization's size, industry, and current security maturity level.",
  "consultation.topics.title": "Topics We Can Discuss",
  "consultation.topics.gettingStarted": "Getting started with ISO 27001 implementation",
  "consultation.topics.scope": "Defining your ISMS scope",
  "consultation.topics.riskAssessment": "Risk assessment methodology",
  "consultation.topics.soa": "Statement of Applicability guidance",
  "consultation.topics.internalAudit": "Internal audit preparation",
  "consultation.topics.certificationAudit": "Certification audit readiness",
  "consultation.topics.gapAnalysis": "Gap analysis and remediation planning",
  "consultation.topics.documents": "Document and policy requirements",
  "consultation.howItWorks.title": "How It Works",
  "consultation.howItWorks.step1.title": "Send a Request",
  "consultation.howItWorks.step1.description": "Click the button below to send us an email with your consultation request. Include a brief description of what you'd like to discuss.",
  "consultation.howItWorks.step2.title": "Schedule a Time",
  "consultation.howItWorks.step2.description": "We'll respond within 24-48 hours with available time slots that work for your schedule.",
  "consultation.howItWorks.step3.title": "Meet with an Expert",
  "consultation.howItWorks.step3.description": "Join the 30-minute video or phone call and get answers to your ISO 27001 questions.",
  "consultation.cta.title": "Ready to Get Started?",
  "consultation.cta.description": "Take the next step in your ISO 27001 journey. Book a consultation today and get personalized guidance from an expert.",
  "consultation.cta.emailPrefix": "Or email us directly at",

  // About Page
  "about.title": "About ISO27001KIT",
  "about.consultation.title": "Need Expert Guidance?",
  "about.consultation.description": "Book a 30-minute consultation to discuss your ISO 27001 implementation journey. Get personalized advice and answers to your specific questions.",
  "about.consultation.button": "Book Your Consultation",
  "about.whatIs.title": "What is ISO 27001?",
  "about.whatIs.description": "ISO/IEC 27001 is an international standard for information security management. It provides a framework for organizations to protect their information through the adoption of an Information Security Management System (ISMS).",
  "about.ourKit.title": "Our ISO 27001 Implementation Kit",
  "about.ourKit.description": "ISO27001KIT is a complete implementation system that combines editable Word templates, AI-powered tools, an ISO 27001 AI Assistant, and a full Risk Copilot platform. Instead of stitching together generic templates and manual spreadsheets, you get one workspace that takes you from gap analysis to audit-ready evidence.",
  "about.benefits.title": "Key Benefits",
  "about.benefits.documentation.title": "Complete Documentation",
  "about.benefits.documentation.description": "Complete set of mandatory and recommended policies",
  "about.benefits.templates.title": "Ready-to-Use Templates",
  "about.benefits.templates.description": "Templates that can be easily customized",
  "about.benefits.guides.title": "Implementation Guides",
  "about.benefits.guides.description": "Step-by-step implementation guides",
  "about.benefits.riskTools.title": "Risk Assessment Tools",
  "about.benefits.riskTools.description": "Risk assessment tools and templates",
  "about.benefits.checklists.title": "Audit Checklists",
  "about.benefits.checklists.description": "Audit checklists and validation reports",
  "about.whoShould.title": "Who Should Use This Kit?",
  "about.whoShould.intro": "Our ISO 27001 Implementation Kit is ideal for:",
  "about.whoShould.securityOfficers": "Information Security Officers",
  "about.whoShould.complianceManagers": "Compliance Managers",
  "about.whoShould.itManagers": "IT Managers and Directors",
  "about.whoShould.sme": "Small and Medium-sized Enterprises",
  "about.whoShould.seekingCert": "Organizations seeking ISO 27001 certification",
  "about.approach.title": "How We Work",
  "about.approach.intro": "ISO27001KIT replaces the usual mess of generic templates, scattered spreadsheets, and expensive consultants with one AI-native workspace. You start free, pick the plan that matches your stage, and get to an audit-ready ISMS in weeks instead of quarters.",
  "about.approach.standards.title": "Standards-aligned",
  "about.approach.standards.description": "Every template, tool, and AI Assistant answer maps directly to ISO/IEC 27001:2022 clauses and Annex A controls, kept current with the latest published guidance.",
  "about.approach.practical.title": "AI where it saves time",
  "about.approach.practical.description": "An ISO 27001-trained AI Assistant drafts policies, interprets clauses, scores risks, and reviews evidence - so you focus on decisions, not formatting.",
  "about.approach.privacy.title": "Start free, scale when ready",
  "about.approach.privacy.description": "Try every tool with previews and sample outputs at no cost. Upgrade to AI Assistant Plus, the Document Pack, or the Pro Audit Room only when you need more. Your documents and risk data stay under your control.",
  "about.cta.title": "Ready to Secure Your Organization?",
  "about.cta.description": "Whether you need documentation templates, implementation tools, or a full risk management platform, ISO27001KIT has everything you need to achieve and maintain ISO 27001 certification.",
  "about.cta.button": "Contact Us",
  "about.cta.exploreCopilot": "Explore Risk Copilot",
  "about.products.title": "Our Product Suite",
  "about.products.intro": "Pick the entry point that matches where you are. Every plan plugs into the same workspace, so you can start free and upgrade only when you need more.",
  "about.products.free.title": "Free Starter",
  "about.products.free.description": `Full site access, tool previews, sample outputs, and ${FREE_MSG} ISO 27001 AI Assistant messages per day. The fastest way to see how ISO27001KIT works before you commit.`,
  "about.products.aiPlus.title": "AI Assistant Plus",
  "about.products.aiPlus.description": `${PLUS_MSG} ISO 27001 AI Assistant messages per day for $${PLUS}/month. Ideal if you mainly need an expert chat copilot to interpret clauses, draft answers, and review policies.`,
  "about.products.docPack.title": "ISO 27001 Document Pack",
  "about.products.docPack.description": `40+ editable Word policies plus 2 Excel registers (Risk Register and Statement of Applicability) and implementation guides. One-time $${DOC} - yours forever, no watermarks, ready to brand.`,
  "about.products.proTools.title": "Pro Audit Room",
  "about.products.proTools.description": `Everything: 11 AI-powered ISO 27001 tools, the full 12-module Risk Copilot platform, ${PRO_MSG} AI Assistant messages/day, full exports, save/load projects, and audit trail. $${PRO_M}/month or $${PRO_Y}/year.`,
  "about.products.learnMore": "Learn more about our products",
  "about.benefits.copilot.title": "Risk Copilot Platform",
  "about.benefits.copilot.description": "Full risk management suite with dashboards, reports, and audit trails",
  "about.whoShould.riskManagers": "Risk Managers needing a centralized risk management platform",

  // FAQ Page
  "faq.title": "Frequently Asked Questions",
  "faq.intro": "Find answers to common questions about ISO 27001 implementation, certification, and using our free documentation toolkit.",
  "faq.stillQuestions.title": "Still Have Questions?",
  "faq.stillQuestions.description": "If you couldn't find the answer you're looking for, feel free to reach out to us. We're here to help you with your ISO 27001 implementation journey.",
  "faq.stillQuestions.button": "Contact Us",
  "faq.category.general": "General ISO 27001 Questions",
  "faq.category.implementation": "Implementation Questions",
  "faq.category.certification": "Certification Process",
  "faq.category.toolkit": "Using This Toolkit",
  "faq.category.maintenance": "Maintenance and Compliance",
  "faq.general.q1": "What is ISO 27001?",
  "faq.general.a1": "ISO 27001 is an internationally recognized standard for information security management systems (ISMS). It provides a systematic approach to managing sensitive company information, including financial data, intellectual property, employee details, and information entrusted by third parties. The standard helps organizations protect their information assets through a risk management process.",
  "faq.general.q2": "Who needs ISO 27001 certification?",
  "faq.general.a2": "While ISO 27001 certification is voluntary, it's particularly valuable for organizations that handle sensitive information, including technology companies, financial services, healthcare providers, government agencies, and businesses that work with regulated industries.",
  "faq.general.q3": "What are the benefits of ISO 27001 certification?",
  "faq.general.a3": "ISO 27001 certification demonstrates your commitment to information security to customers and partners, helps identify and mitigate security risks, ensures compliance with legal and regulatory requirements, improves business processes and efficiency, and reduces the likelihood of security breaches.",
  "faq.general.q4": "Is ISO 27001 certification mandatory?",
  "faq.general.a4": "No, ISO 27001 certification is voluntary. However, it may be required by certain clients, partners, or industries as a condition of doing business.",
  "faq.implementation.q1": "How long does it take to implement ISO 27001?",
  "faq.implementation.a1": "Implementation timelines vary significantly based on organization size, complexity, existing security maturity, and resources dedicated to the project. Small organizations might implement ISO 27001 in 3-6 months, while larger organizations may need 12-18 months or longer.",
  "faq.implementation.q2": "What are the main steps to implement ISO 27001?",
  "faq.implementation.a2": "The main steps include: Define the scope of your ISMS, conduct risk assessment, develop policies and procedures, implement security controls, train employees, conduct internal audits, perform management review, and schedule certification audit.",
  "faq.implementation.q3": "Do I need a consultant to implement ISO 27001?",
  "faq.implementation.a3": "While not mandatory, many organizations benefit from consultant expertise, especially for their first certification. However, smaller organizations may successfully implement ISO 27001 independently using resources like this toolkit.",
  "faq.implementation.q4": "Can small businesses implement ISO 27001?",
  "faq.implementation.a4": "Yes, absolutely. ISO 27001 is scalable and applicable to organizations of all sizes. Small businesses can implement a proportionate ISMS that fits their size, complexity, and risk profile.",
  "faq.certification.q1": "How much does ISO 27001 certification cost?",
  "faq.certification.a1": "Certification costs vary widely based on organization size, scope, and location. Expect to budget for certification body audit fees, consultant fees if used, internal resource time, training costs, and technology/tool costs.",
  "faq.certification.q2": "What is the certification audit process?",
  "faq.certification.a2": "The certification process has two stages: Stage 1 is a documentation review. Stage 2 is a full audit where auditors assess implementation effectiveness. If successful, you receive certification valid for three years.",
  "faq.certification.q3": "How do I choose a certification body?",
  "faq.certification.a3": "Select a certification body that is accredited by a recognized accreditation body, has experience in your industry, offers reasonable pricing and scheduling, and is recognized by your target markets.",
  "faq.certification.q4": "What happens if we fail the certification audit?",
  "faq.certification.a4": "If major non-conformities are found, certification may be delayed while you address issues. Minor non-conformities typically allow certification with required corrections within a specified timeframe.",
  "faq.toolkit.q1": "Are these templates sufficient for ISO 27001 certification?",
  "faq.toolkit.a1": "These templates provide a comprehensive foundation, but they must be customized for your specific organization, risk profile, and context. Use these templates as starting points, then adapt them to your business operations.",
  "faq.toolkit.q2": "Can I use these templates commercially?",
  "faq.toolkit.a2": "Yes, you can use these templates for your organization's internal ISO 27001 implementation, including commercial businesses. However, you cannot resell, redistribute, or sublicense these materials to others.",
  "faq.toolkit.q3": "How do I customize the templates?",
  "faq.toolkit.a3": "Replace placeholder text with your organization-specific information. Add or remove sections based on your scope and applicability. Adjust control implementations to match your actual practices.",
  "faq.toolkit.q4": "Do these templates comply with the latest ISO 27001 standard?",
  "faq.toolkit.a4": "Our templates are designed to align with ISO 27001:2022 requirements. However, standards evolve. Always verify current requirements with the official ISO standard documentation.",
  "faq.toolkit.q5": "What documents are mandatory for ISO 27001?",
  "faq.toolkit.a5": "ISO 27001 specifically requires: Information Security Policy, Risk Assessment methodology and results, Risk Treatment Plan, Statement of Applicability (SoA), internal audit program and results, and management review records.",
  "faq.category.products": "Products & Pricing",
  "faq.products.q1": "What is the ISO 27001 Document Pack?",
  "faq.products.a1": `The Document Pack is a one-time $${DOC} purchase containing 40+ editable Word policy templates, 2 Excel registers (Risk Register and Statement of Applicability), and implementation guides. All documents are built using AI with expert review and supervision.`,
  "faq.products.q2": "What is ISO27001KIT Risk Copilot?",
  "faq.products.a2": `Risk Copilot is our full risk management SaaS platform with 12 modules: Risk Register, Asset Management, Controls, Treatments, Threat Library, Vulnerability Library, Business Processes, Reports, Audit Trail, Settings, and more. It's available at $${PRO_M}/month or $${PRO_Y}/year.`,
  "faq.products.q3": "What does the Pro plan include?",
  "faq.products.a3": `The Pro plan ($${PRO_M}/month or $${PRO_Y}/year) includes all 11 implementation tools plus the full 12-module Risk Copilot platform - risk register, asset management, controls, treatments, audit trail, and professional exports.`,
  "faq.products.q4": "What is your refund policy?",
  "faq.products.a4": "All our products are digital and delivered instantly, so all sales are final. We do not offer refunds. However, if you experience any issue with your purchase, contact us at contact@iso27001kit.com and we'll do our best to help. Refunds are only considered for clear errors such as double charges.",
  "faq.maintenance.q1": "What happens after certification?",
  "faq.maintenance.a1": "After certification, you must maintain your ISMS through continuous operation, regular internal audits, management reviews, updating risk assessments when needed, and participating in annual surveillance audits.",
  "faq.maintenance.q2": "How often must we conduct internal audits?",
  "faq.maintenance.a2": "ISO 27001 requires internal audits at planned intervals, typically at least once per year. Many organizations conduct more frequent audits covering different areas throughout the year.",
  "faq.maintenance.q3": "Can we lose our certification?",
  "faq.maintenance.a3": "Yes, certification can be suspended or withdrawn if you fail surveillance audits, don't address major non-conformities, don't maintain your ISMS effectively, or have significant security incidents.",
  "faq.maintenance.q4": "How do we handle changes to our business or scope?",
  "faq.maintenance.a4": "Significant changes may require updating your ISMS documentation, conducting new risk assessments, implementing additional controls, and notifying your certification body.",

  // Auth Page
  "auth.title": "ISO 27001 Toolkit",
  "auth.description": "Sign in or create an account to access all documents and features",
  "auth.signIn": "Sign In",
  "auth.signUp": "Sign Up",
  "auth.email": "Email",
  "auth.emailPlaceholder": "you@example.com",
  "auth.password": "Password",
  "auth.passwordPlaceholder": "••••••••",
  "auth.fullName": "Full Name (optional)",
  "auth.fullNamePlaceholder": "John Doe",
  "auth.forgotPassword": "Forgot password?",
  "auth.backToSignIn": "← Back to Sign In",
  "auth.resetPassword": "Reset Password",
  "auth.resetPasswordDesc": "Enter your email and we'll send you a link to reset your password.",
  "auth.sendResetLink": "Send Reset Link",
  "auth.sending": "Sending...",
  "auth.signingIn": "Signing in...",
  "auth.creatingAccount": "Creating account...",
  "auth.createAccount": "Create Account",
  "auth.passwordMinLength": "Must be at least 6 characters",
  "auth.termsAgree": "By signing up, you agree to our",
  "auth.termsOfService": "Terms of Service",
  "auth.and": "and",
  "auth.privacyPolicy": "Privacy Policy",
  "auth.setNewPassword": "Set New Password",
  "auth.enterNewPassword": "Enter your new password below",
  "auth.newPassword": "New Password",
  "auth.confirmPassword": "Confirm New Password",
  "auth.updatePassword": "Update Password",
  "auth.updating": "Updating...",
  "auth.errorAlreadyRegistered": "This email is already registered. Please sign in instead.",
  "auth.errorInvalidCredentials": "Invalid email or password. Please try again.",
  "auth.errorPasswordMismatch": "Passwords do not match.",
  "auth.errorResetFailed": "We could not send the reset email. Please try again later.",
  "auth.successAccountCreated": "Account created successfully! You are now logged in.",
  "auth.successResetSent": "If this email exists in our system, a reset link has been sent.",
  "auth.successPasswordUpdated": "Password updated successfully! Redirecting...",

  // Account Page
  "account.title": "Account Settings",
  "account.subtitle": "Manage your profile, subscription and account preferences",
  "account.profile.title": "Profile Information",
  "account.profile.subtitle": "Your personal details",
  "account.profile.fullName": "Full Name",
  "account.profile.email": "Email",
  "account.profile.emailCannotChange": "Email cannot be changed",
  "account.profile.phone": "Phone Number",
  "account.profile.phonePlaceholder": "+1 234 567 8900",
  "account.profile.phoneOptional": "Phone Number (optional)",
  "account.profile.notSet": "Not set",
  "account.profile.saveChanges": "Save Changes",
  "account.profile.saving": "Saving...",
  "account.plan.title": "Current Plan",
  "account.plan.proPlan": "Pro Plan",
  "account.plan.freePlan": "Free Plan",
  "account.plan.proDesc": "Full access to all tools, Risk Copilot, and exports",
  "account.plan.freeDesc": "Preview access with sample output",
  "account.plan.perYear": "/year",
  "account.plan.perMonth": "/month",
  "account.plan.yourFeatures": "Your Features",
  "account.plan.feature.allPdfs": "PDF previews (first page only, watermarked)",
  "account.plan.feature.allGuides": "All guides and full site access",
  "account.plan.feature.fullTools": "Full use of all 11 ISO 27001 tools",
  "account.plan.feature.completeResults": "Complete exports - Excel and Word with all rows",
  "account.plan.feature.saveLoad": "Save and load projects",
  "account.plan.feature.fullExports": "Full Risk Copilot platform (12 modules)",
  "account.plan.feature.previewOnly": "Tool previews with sample output only",
  "account.plan.feature.limitedExports": "Exports limited to first row",
  "account.plan.feature.noSaveLoad": "No project save/load",
  "account.plan.accessUntil": "Access active until:",
  "account.plan.cancelInfo": "You can manage, update, or cancel your subscription anytime using the 'Manage Subscription' button below. Your access stays active until the end of the paid period.",
  "account.plan.upgradePrompt": "Upgrade to Pro for full access to all tools, Risk Copilot, and exports",
  "account.plan.upgradeNow": "Upgrade Now",
  "account.wordPack.title": "ISO 27001 Document Pack",
  "account.wordPack.hasAccess": "You have access to the ISO 27001 Document Pack",
  "account.wordPack.description": "40+ ISO 27001 documents in editable Word format + 2 Excel registers (Risk Register & Statement of Applicability)",
  "account.wordPack.youHave": "You have the ISO 27001 Document Pack.",
  "account.wordPack.download": "Download ISO 27001 Document Pack (ZIP)",
  "account.wordPack.getFor": "Get the ISO 27001 Document Pack for a one-time",
  "account.wordPack.andDownload": "USD and download all documents in Word and Excel formats.",
  "account.wordPack.feature1": "40+ ISO 27001 documents in clean Word format",
  "account.wordPack.feature2": "2 Excel registers: Risk Register & Statement of Applicability",
  "account.wordPack.feature3": "No watermarks - download as a single ZIP file",
  "account.wordPack.getButton": `Get the Document Pack - $${DOC} →`,
  "account.billing.title": "Billing & Orders",
  "account.billing.subtitle": "Manage your subscription and purchases",
  "account.billing.paymentMethod": "Payment Method",
  "account.billing.accessEnds": "Access Ends",
  "account.billing.active": "Active",
  "account.billing.update": "Update",
  "account.billing.cancelInfo": "Use the button above to manage or cancel your subscription. You'll keep full access until",
  "account.billing.endOfPeriod": "the end of your paid period",
  "account.whyUpgrade.title": "Why Upgrade to Pro?",
  "account.whyUpgrade.subtitle": "Get the most out of your ISO 27001 implementation",
  "account.whyUpgrade.fullTools.title": "All 11 Tools + Risk Copilot",
  "account.whyUpgrade.fullTools.desc": "Unlock complete results for all 11 ISO 27001 tools and the full 12-module Risk Copilot platform - dashboard, risk register, treatment plans, audit trail, and more.",
  "account.whyUpgrade.exports.title": "Complete Exports",
  "account.whyUpgrade.exports.desc": "Export full Excel and Word documents with all your data. Perfect for presentations, audits, and documentation.",
  "account.whyUpgrade.saveLoad.title": "Save & Load Projects",
  "account.whyUpgrade.saveLoad.desc": "Save your progress and come back later. Work on multiple projects without losing data.",
  "account.whyUpgrade.riskCopilot.title": "Risk Copilot Platform",
  "account.whyUpgrade.riskCopilot.desc": "Full access to 12 integrated modules: risk register, asset management, threat & vulnerability libraries, business processes, controls, treatment plans, reports, and a searchable audit trail.",
  "account.whyUpgrade.button": "Upgrade to Pro -",
  "account.actions.title": "Account Actions",
  "account.actions.subtitle": "Sign out or manage your account",
  "account.actions.changePassword": "Change Password",
  "account.actions.changePasswordDesc": "Enter your new password below. It must be at least 6 characters.",
  "account.actions.newPassword": "New Password",
  "account.actions.confirmPassword": "Confirm Password",
  "account.actions.changing": "Changing...",
  "account.actions.signOut": "Sign Out",
  "account.actions.deleteAccount": "Delete Account",
  "account.actions.deleting": "Deleting...",
  "account.actions.deleteConfirmTitle": "Are you sure?",
  "account.actions.deleteConfirmDesc": "This action cannot be undone. This will permanently delete your account and remove all your data from our servers. Any active subscriptions will need to be cancelled separately through your billing portal.",
  "account.actions.deleteNote": "Note: If you have an active Audit Room subscription, please cancel it in your Lemon Squeezy account before deleting your account.",

  // Privacy Policy
  "privacy.title": "Privacy Policy",
  "privacy.lastUpdated": "Last updated:",
  "privacy.intro.title": "1. Introduction",
  "privacy.intro.content": "ISO27001KIT (\"we,\" \"our,\" or \"us\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard information when you visit our website, create an account, use our tools, or purchase our products.",
  "privacy.collect.title": "2. Information We Collect",
  "privacy.collect.account.title": "2.1 Account Information",
  "privacy.collect.account.intro": "When you create an account, we collect:",
  "privacy.collect.account.email": "Email address",
  "privacy.collect.account.name": "Full name (optional)",
  "privacy.collect.account.phone": "Phone number (optional)",
  "privacy.collect.account.password": "Password (encrypted)",
  "privacy.collect.payment.title": "2.2 Payment Information",
  "privacy.collect.payment.content": "When you purchase an Audit Room subscription or the ISO 27001 Document Pack, payments are processed through Lemon Squeezy. We store your subscription status and Lemon Squeezy order or subscription ID, but we do not store your payment card details. Lemon Squeezy handles all payment processing securely.",
  "privacy.collect.download.title": "2.3 Document Download Information",
  "privacy.collect.download.content": "When you download free PDF documents, we collect your email address to provide access and send relevant updates. This email is stored in our database.",
  "privacy.collect.tool.title": "2.4 Free Tool Usage Data",
  "privacy.collect.tool.content": "When you use our free ISO 27001 tools (SoA Generator, Risk Assessment, Gap Analysis, Internal Audit, Implementation Roadmap, ISMS Scope Builder, Asset Inventory, Annex A Selector, Supplier Risk Manager, RTO/RPO Helper), any data you enter is processed entirely in your browser. Free users can save project drafts locally on their own device using localStorage. We do not store your free tool input data on our servers.",
  "privacy.collect.copilot.title": "2.5 Risk Copilot Data",
  "privacy.collect.copilot.content": "When you use the Risk Copilot (Pro feature), your risk register entries, assets, controls, business processes, threat library, vulnerability library, treatments, security incidents, and audit logs are stored in our database so you can manage your ISO 27001 compliance program across sessions. This data is tied to your user account and protected by row-level security. We only store functional metadata (risk titles, scores, control names, dates) - we do not collect confidential business secrets or sensitive client data.",
  "privacy.collect.chat.title": "2.6 AI Assistant Chat Data",
  "privacy.collect.chat.content": "When you use the ISO 27001 AI Assistant, the messages you send are transmitted to our backend and forwarded to third-party AI model providers (currently Google Gemini and OpenAI via the Lovable AI Gateway) so they can generate a response. Your conversation history is stored locally in your browser (localStorage) and is not saved to our database. We do log minimal metadata for each request - your user ID (if signed in), timestamp, and daily message count - solely to enforce the free-tier daily limit and prevent abuse. We do not sell or share your chat content with anyone other than the AI provider needed to answer you. Please do not paste real passwords, secrets, or sensitive client data into the assistant; treat it like any other third-party AI tool.",
  "privacy.collect.auto.title": "2.7 Automatically Collected Information",
  "privacy.collect.auto.intro": "When you visit our website, we may automatically collect:",
  "privacy.collect.auto.ip": "IP address",
  "privacy.collect.auto.browser": "Browser type and version",
  "privacy.collect.auto.device": "Device information",
  "privacy.collect.auto.pages": "Pages visited and time spent on pages",
  "privacy.collect.auto.referrer": "Referring website addresses",
  "privacy.use.title": "3. How We Use Your Information",
  "privacy.use.intro": "We use the collected information to:",
  "privacy.use.account": "Create and manage your account",
  "privacy.use.payments": "Process payments and manage subscriptions",
  "privacy.use.access": "Provide access to purchased products (Pro tools, ISO 27001 Document Pack)",
  "privacy.use.deliver": "Deliver PDF documents you request",
  "privacy.use.support": "Respond to your inquiries and support requests",
  "privacy.use.improve": "Improve our website and services",
  "privacy.use.updates": "Send important service updates",
  "privacy.use.fraud": "Detect and prevent fraud or abuse",
  "privacy.sharing.title": "4. Data Sharing and Disclosure",
  "privacy.sharing.intro": "We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances:",
  "privacy.sharing.paypal": "Lemon Squeezy: To process payments for Audit Room subscriptions and ISO 27001 Document Pack purchases",
  "privacy.sharing.backend": "Lovable Cloud: Our managed backend and authentication provider (data stored securely with row-level access controls)",
  "privacy.sharing.consent": "With your explicit consent",
  "privacy.sharing.legal": "To comply with legal obligations or valid legal processes",
  "privacy.sharing.rights": "To protect our rights, privacy, safety, or property",
  "privacy.cookies.title": "5. Cookies and Tracking Technologies",
  "privacy.cookies.intro": "Our website uses cookies and local storage to enhance your experience, including:",
  "privacy.cookies.auth": "Authentication cookies to keep you logged in",
  "privacy.cookies.local": "Local storage to save your preferences and cached email",
  "privacy.cookies.analytics": "Analytics cookies to understand website usage",
  "privacy.cookies.control": "You can control cookie preferences through your browser settings. Disabling cookies may affect website functionality.",
  "privacy.security.title": "6. Data Security",
  "privacy.security.intro": "We implement appropriate security measures to protect your personal information:",
  "privacy.security.passwords": "Passwords are encrypted and never stored in plain text",
  "privacy.security.https": "All data transmission uses HTTPS encryption",
  "privacy.security.access": "Database access is restricted and monitored",
  "privacy.security.paypal": "Payment processing is handled by Lemon Squeezy's secure systems",
  "privacy.security.disclaimer": "However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.",
  "privacy.retention.title": "7. Data Retention",
  "privacy.retention.intro": "We retain your information as follows:",
  "privacy.retention.account": "Account data: Until you request account deletion",
  "privacy.retention.subscription": "Subscription data: For the duration of your subscription plus any legally required period",
  "privacy.retention.download": "Download records: Indefinitely for our records",
  "privacy.retention.tool": "Free tool data: Stored locally on your device only",
  "privacy.retention.copilot": "Risk Copilot data: Retained while your account is active; deleted upon account deletion",
  "privacy.rights.title": "8. Your Rights",
  "privacy.rights.intro": "You have the right to:",
  "privacy.rights.access": "Access the personal information we hold about you",
  "privacy.rights.correct": "Request correction of inaccurate information",
  "privacy.rights.delete": "Request deletion of your account and personal information",
  "privacy.rights.cancel": "Cancel your subscription at any time through your Account page or by contacting us",
  "privacy.rights.object": "Object to processing of your personal information",
  "privacy.rights.withdraw": "Withdraw consent at any time",
  "privacy.rights.contact": "To exercise these rights, contact us at",
  "privacy.thirdParty.title": "9. Third-Party Links",
  "privacy.thirdParty.content": "Our website may contain links to external websites (such as Lemon Squeezy). We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review their privacy policies.",
  "privacy.children.title": "10. Children's Privacy",
  "privacy.children.content": "Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.",
  "privacy.changes.title": "11. Changes to This Policy",
  "privacy.changes.content": "We may update this Privacy Policy periodically. Changes will be posted on this page with an updated revision date. Continued use of our website after changes constitutes acceptance of the updated policy.",
  "privacy.contact.title": "12. Contact Us",
  "privacy.contact.intro": "If you have questions about this Privacy Policy or our data practices, please contact us at:",
  "privacy.contact.email": "Email:",

  // Terms of Service
  "terms.title": "Terms of Service",
  "terms.lastUpdated": "Last updated:",
  "terms.acceptance.title": "1. Acceptance of Terms",
  "terms.acceptance.content": "By accessing and using ISO27001KIT (\"the Website\"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website, create an account, or purchase our products.",
  "terms.services.title": "2. Description of Services",
  "terms.services.intro": "ISO27001KIT provides the following services:",
  "terms.services.free.title": "2.1 Free Services",
  "terms.services.free.pdfs": "Free watermarked PDF documents (policies, templates, guides, checklists)",
  "terms.services.free.preview": "Preview access to ISO 27001 implementation tools (limited results)",
  "terms.services.free.exports": "Limited Excel and Word exports (header and first row only)",
  "terms.services.pro.title": `2.2 Pro Subscription ($${PRO_M}/month or $${PRO_Y}/year)`,
  "terms.services.pro.tools": "Full access to the ISO 27001 implementation tools (including SoA Generator, Risk Assessment, Gap Analysis, Internal Audit, Implementation Roadmap, ISMS Scope Builder, Asset Inventory, Annex A Selector, Supplier Risk Manager, RTO/RPO Helper), the Risk Copilot workspace, and the ISO 27001 Audit Room",
  "terms.services.pro.results": "Complete tool results with all rows and data",
  "terms.services.pro.exports": "Full Excel and Word exports",
  "terms.services.pro.saveLoad": "Project save and load functionality",
  "terms.services.wordPack.title": `2.3 ISO 27001 Document Pack ($${DOC} one-time)`,
  "terms.services.wordPack.docs": "40+ ISO 27001 documents in clean, editable Word format + 2 Excel registers (Risk Register and Statement of Applicability)",
  "terms.services.wordPack.noWatermark": "No watermark",
  "terms.services.wordPack.zip": "Single ZIP file download",
  "terms.registration.title": "3. Account Registration",
  "terms.registration.intro": "To access certain features, you must create an account. You agree to:",
  "terms.registration.accurate": "Provide accurate and complete registration information",
  "terms.registration.secure": "Maintain the security of your password",
  "terms.registration.responsible": "Accept responsibility for all activities under your account",
  "terms.registration.notify": "Notify us immediately of any unauthorized use",
  "terms.payments.title": "4. Payments and Billing",
  "terms.payments.pro.title": "4.1 Pro Subscription",
  "terms.payments.pro.paypal": "Payments are processed through Lemon Squeezy",
  "terms.payments.pro.renew": "Subscriptions automatically renew unless cancelled",
  "terms.payments.pro.cancel": "You can cancel anytime from your account page",
  "terms.payments.pro.retain": "After cancellation, you retain access until the end of your paid period",
  "terms.payments.wordPack.title": "4.2 ISO 27001 Document Pack",
  "terms.payments.wordPack.oneTime": `One-time payment of $${DOC} USD`,
  "terms.payments.wordPack.immediate": "Immediate access to download after payment",
  "terms.payments.wordPack.lifetime": "Lifetime access to the purchased version",
  "terms.payments.refunds.title": "4.3 Refunds",
  "terms.payments.refunds.intro": "Please see our",
  "terms.payments.refunds.link": "Refund Policy",
  "terms.payments.refunds.outro": "for details on refunds and cancellations.",
  "terms.license.title": "5. Use License",
  "terms.license.permitted.title": "5.1 Permitted Use",
  "terms.license.permitted.intro": "You are granted a non-exclusive, non-transferable license to:",
  "terms.license.permitted.download": "Download and use free PDF documents for your organization's internal purposes",
  "terms.license.permitted.tools": "Use Pro tools for ISO 27001 implementation work",
  "terms.license.permitted.modify": "Modify and customize ISO 27001 Document Pack documents for your organization's needs",
  "terms.license.permitted.export": "Export and use tool outputs for your compliance documentation",
  "terms.license.restrictions.title": "5.2 Restrictions",
  "terms.license.restrictions.intro": "You may NOT:",
  "terms.license.restrictions.resell": "Resell, redistribute, or sublicense any materials or tool outputs",
  "terms.license.restrictions.share": "Share your account credentials with others",
  "terms.license.restrictions.automate": "Use automated systems to access the tools or download content",
  "terms.license.restrictions.claim": "Claim authorship or ownership of the original templates",
  "terms.license.restrictions.remove": "Remove copyright or attribution notices from documents",
  "terms.license.restrictions.commercial": "Use the materials for commercial resale or distribution",
  "terms.disclaimer.title": "6. Disclaimer of Warranties",
  "terms.disclaimer.content": "THE MATERIALS AND TOOLS ARE PROVIDED \"AS IS\" WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.",
  "terms.disclaimer.intro": "We do not guarantee that:",
  "terms.disclaimer.requirements": "The materials or tools will meet your specific requirements",
  "terms.disclaimer.certification": "Use of the materials will result in ISO 27001 certification",
  "terms.disclaimer.errorFree": "The materials are error-free or complete",
  "terms.disclaimer.uninterrupted": "The website or tools will be uninterrupted or secure",
  "terms.professional.title": "7. Professional Advice Disclaimer",
  "terms.professional.content": "The materials and tool outputs are for informational purposes only and do not constitute professional advice. ISO 27001 implementation requirements vary by organization, industry, and jurisdiction. We strongly recommend consulting with qualified information security professionals and legal advisors before implementing any security policies or procedures.",
  "terms.liability.title": "8. Limitation of Liability",
  "terms.liability.intro": "TO THE MAXIMUM EXTENT PERMITTED BY LAW, ISO27001KIT SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES ARISING FROM:",
  "terms.liability.use": "Use or inability to use the materials or tools",
  "terms.liability.errors": "Errors, omissions, or inaccuracies in the documents or tool outputs",
  "terms.liability.audits": "Failed certification audits or compliance gaps",
  "terms.liability.breaches": "Security breaches or data loss",
  "terms.liability.payments": "Payment processing issues",
  "terms.liability.other": "Any other matter relating to the service",
  "terms.ip.title": "9. Intellectual Property",
  "terms.ip.content": "All content on this website, including templates, guides, documentation, and tool interfaces, is the intellectual property of ISO27001KIT. The materials are protected by copyright and other intellectual property laws. Purchasing access grants you a license to use the materials but does not transfer ownership.",
  "terms.userResponsibility.title": "10. User Responsibilities",
  "terms.userResponsibility.intro": "You are responsible for:",
  "terms.userResponsibility.review": "Reviewing and customizing templates for your organization's specific context",
  "terms.userResponsibility.verify": "Verifying tool outputs before using them in official documentation",
  "terms.userResponsibility.compliance": "Ensuring compliance with applicable laws and regulations",
  "terms.userResponsibility.security": "Maintaining the security of your account and downloaded materials",
  "terms.userResponsibility.advice": "Seeking professional advice when necessary",
  "terms.modifications.title": "11. Modifications to Service",
  "terms.modifications.content": "We reserve the right to modify, suspend, or discontinue any aspect of the service at any time. This includes changes to pricing, features, or available content. We will make reasonable efforts to notify users of significant changes.",
  "terms.termination.title": "12. Termination",
  "terms.termination.content": "We reserve the right to terminate or restrict your access to the website and services at our sole discretion, without notice, for any violation of these Terms of Service. Upon termination, your right to use the services will immediately cease.",
  "terms.indemnification.title": "13. Indemnification",
  "terms.indemnification.content": "You agree to indemnify and hold harmless ISO27001KIT from any claims, damages, losses, liabilities, and expenses arising from your use of the materials and tools, or violation of these Terms of Service.",
  "terms.governing.title": "14. Governing Law",
  "terms.governing.content": "These Terms of Service shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.",
  "terms.changes.title": "15. Changes to Terms",
  "terms.changes.content": "We may revise these Terms of Service at any time. Continued use of the website after changes constitutes acceptance of the revised terms. Please review this page periodically.",
  "terms.contact.title": "16. Contact Information",
  "terms.contact.intro": "For questions about these Terms of Service, please contact us at:",
  "terms.contact.email": "Email:",

  // Disclaimer Page
  "disclaimer.title": "Disclaimer",
  "disclaimer.lastUpdated": "Last updated:",
  "disclaimer.general.title": "General Disclaimer",
  "disclaimer.general.content": "The information, templates, policies, procedures, documentation, and tool outputs (collectively \"Materials\") provided on ISO27001KIT are for general informational and educational purposes only. Use of these Materials is entirely at your own risk.",
  "disclaimer.products.title": "About Our Products",
  "disclaimer.products.intro": "ISO27001KIT offers:",
  "disclaimer.products.freePdfs": "Free PDF Documents: Watermarked ISO 27001 policies, templates, guides, and checklists",
  "disclaimer.products.proTools": `Pro Tools ($${PRO_M}/month or $${PRO_Y}/year): Five ISO 27001 implementation tools with full export capabilities`,
  "disclaimer.products.wordPack": `ISO 27001 Document Pack ($${DOC} one-time): 40+ documents in clean, editable Word format without watermarks + 2 Excel registers (Risk Register and Statement of Applicability)`,
  "disclaimer.products.note": "All products contain the same core content adapted to different formats and use cases. The ISO 27001 Document Pack contains the same documents available as free PDFs, but in editable format.",
  "disclaimer.professional.title": "No Professional Advice",
  "disclaimer.professional.intro": "The Materials do not constitute professional advice in information security, legal compliance, risk management, or any other professional field. ISO27001KIT does not provide:",
  "disclaimer.professional.legal": "Legal advice or legal services",
  "disclaimer.professional.consulting": "Information security consulting",
  "disclaimer.professional.certification": "Compliance certification services",
  "disclaimer.professional.audit": "Audit or assessment services",
  "disclaimer.professional.risk": "Risk management consulting",
  "disclaimer.professional.recommendation": "You should consult with qualified professionals, including information security consultants, legal advisors, and certified auditors, before implementing any policies or procedures based on our Materials.",
  "disclaimer.noCertification.title": "No Guarantee of Certification",
  "disclaimer.noCertification.intro": "Using the Materials from ISO27001KIT does not guarantee ISO 27001 certification or compliance. ISO 27001 certification requires:",
  "disclaimer.noCertification.riskAssessment": "Comprehensive risk assessment specific to your organization",
  "disclaimer.noCertification.controls": "Implementation of appropriate controls",
  "disclaimer.noCertification.evidence": "Evidence of effective information security management",
  "disclaimer.noCertification.audit": "Successful third-party certification audit",
  "disclaimer.noCertification.ongoing": "Ongoing compliance and continuous improvement",
  "disclaimer.noCertification.note": "Our tools (SoA Generator, Risk Assessment, Gap Analysis, Internal Audit, Implementation Roadmap) are designed to assist with documentation, but the outputs must be reviewed, customized, and validated by qualified professionals.",
  "disclaimer.toolOutput.title": "Tool Output Disclaimer",
  "disclaimer.toolOutput.intro": "The ISO 27001 tools provided on this website generate outputs based on your inputs. These outputs:",
  "disclaimer.toolOutput.starting": "Are starting points for documentation, not final deliverables",
  "disclaimer.toolOutput.review": "Require review and customization by qualified personnel",
  "disclaimer.toolOutput.requirements": "May not address all requirements specific to your organization",
  "disclaimer.toolOutput.validate": "Should be validated against current ISO 27001 standards",
  "disclaimer.toolOutput.substitute": "Are not a substitute for professional security assessment",
  "disclaimer.accuracy.title": "Accuracy and Completeness",
  "disclaimer.accuracy.intro": "While we strive to provide accurate and up-to-date Materials, we make no representations or warranties regarding:",
  "disclaimer.accuracy.accurate": "Accuracy or completeness of the Materials",
  "disclaimer.accuracy.suitability": "Suitability for your specific requirements",
  "disclaimer.accuracy.compliance": "Compliance with current ISO 27001:2022 standards",
  "disclaimer.accuracy.local": "Compliance with your local regulations and laws",
  "disclaimer.accuracy.errors": "Freedom from errors or omissions",
  "disclaimer.accuracy.note": "ISO 27001 standards, regulations, and best practices evolve over time. Users are responsible for verifying current requirements and updating Materials accordingly.",
  "disclaimer.customization.title": "Customization Required",
  "disclaimer.customization.intro": "All templates, policies, and tool outputs MUST be customized to reflect your organization's:",
  "disclaimer.customization.context": "Specific business context and operations",
  "disclaimer.customization.risk": "Risk assessment findings",
  "disclaimer.customization.regulatory": "Regulatory and legal requirements",
  "disclaimer.customization.industry": "Industry-specific standards",
  "disclaimer.customization.structure": "Organizational structure and processes",
  "disclaimer.customization.geographic": "Geographic location and applicable laws",
  "disclaimer.customization.note": "Generic templates and tool outputs should never be implemented without thorough review and adaptation to your organization's unique circumstances.",
  "disclaimer.liability.title": "Limitation of Liability",
  "disclaimer.liability.intro": "ISO27001KIT shall not be held liable for any damages, losses, or consequences arising from:",
  "disclaimer.liability.use": "Use or misuse of the Materials or tool outputs",
  "disclaimer.liability.implementation": "Implementation of policies or procedures based on our templates",
  "disclaimer.liability.audits": "Failed certification audits",
  "disclaimer.liability.breaches": "Security breaches, data loss, or incidents",
  "disclaimer.liability.nonCompliance": "Regulatory non-compliance or penalties",
  "disclaimer.liability.business": "Business interruption or financial loss",
  "disclaimer.liability.reliance": "Reliance on information provided on this website",
  "disclaimer.payment.title": "Payment and Product Disclaimer",
  "disclaimer.payment.intro": "Regarding our paid products (Audit Room subscription and ISO 27001 Document Pack):",
  "disclaimer.payment.sameContent": "The ISO 27001 Document Pack contains the same content as free PDFs, in editable Word format",
  "disclaimer.payment.noGuarantee": "Pro tools provide assistance with documentation but do not guarantee compliance",
  "disclaimer.payment.secure": "Payments are processed securely through Lemon Squeezy",
  "disclaimer.payment.refund": "Refund policy is detailed in our",
  "disclaimer.external.title": "External Links Disclaimer",
  "disclaimer.external.intro": "Our website may contain links to external resources, including Lemon Squeezy for payments. We do not endorse, verify, or assume responsibility for:",
  "disclaimer.external.accuracy": "Content accuracy on third-party websites",
  "disclaimer.external.privacy": "Privacy practices of external sites",
  "disclaimer.external.services": "Services or products offered by third parties",
  "disclaimer.noWarranties.title": "No Warranties",
  "disclaimer.noWarranties.intro": "The Materials are provided \"AS IS\" and \"AS AVAILABLE\" without warranties of any kind, either express or implied, including but not limited to:",
  "disclaimer.noWarranties.merchantability": "Merchantability",
  "disclaimer.noWarranties.fitness": "Fitness for a particular purpose",
  "disclaimer.noWarranties.nonInfringement": "Non-infringement",
  "disclaimer.noWarranties.accuracy": "Accuracy or reliability",
  "disclaimer.noWarranties.uninterrupted": "Uninterrupted or error-free operation",
  "disclaimer.regulatory.title": "Regulatory Compliance",
  "disclaimer.regulatory.content": "Organizations may be subject to various regulations beyond ISO 27001, including but not limited to GDPR, HIPAA, PCI DSS, SOC 2, and industry-specific requirements. Our Materials do not address all regulatory requirements. You are responsible for identifying and complying with all applicable regulations.",
  "disclaimer.updates.title": "Updates and Changes",
  "disclaimer.updates.content": "We may update, modify, or discontinue Materials, tools, or pricing at any time without notice. Purchased products (ISO 27001 Document Pack) provide access to the version available at time of purchase. Pro subscribers have access to current tool versions during their subscription period.",
  "disclaimer.userResponsibility.title": "User Responsibility",
  "disclaimer.userResponsibility.intro": "By using ISO27001KIT Materials and tools, you acknowledge and accept that:",
  "disclaimer.userResponsibility.evaluate": "You are solely responsible for evaluating the suitability of the Materials",
  "disclaimer.userResponsibility.advice": "You will seek professional advice where appropriate",
  "disclaimer.userResponsibility.review": "You will conduct thorough review and testing before implementation",
  "disclaimer.userResponsibility.risks": "You accept all risks associated with using the Materials",
  "disclaimer.userResponsibility.comply": "You will comply with all applicable laws and regulations",
  "disclaimer.contact.title": "Contact",
  "disclaimer.contact.intro": "For questions about this Disclaimer, please contact us at:",
  "disclaimer.contact.email": "Email:",

  // Refund Policy
  "refund.title": "Refund Policy",
  "refund.lastUpdated": "Last updated:",
  "refund.noRefunds.title": "No refunds - digital product",
  "refund.noRefunds.content": "All our products and services are digital and delivered instantly. Because digital products cannot be returned, all sales are final and we do not offer refunds. If you have any questions or concerns about your purchase, please contact us and we will do our best to help.",
  "refund.reviewMode.title": "Questions about your purchase?",
  "refund.reviewMode.content": "If you have any issue with your order, reach out to us at contact@iso27001kit.com and we'll be happy to assist you.",
  "refund.wordPack.title": "ISO 27001 Document Pack",
  "refund.wordPack.content1": "The \"ISO 27001 Document Pack\" is a one-time digital purchase. Because it is delivered instantly as a digital download, it cannot be returned or refunded.",
  "refund.wordPack.content2": "If you experience any technical issue accessing your files, contact us and we will resolve it promptly.",
  "refund.whenRefund.title": "When we will refund",
  "refund.whenRefund.intro": "We only give refunds in clear error cases, for example:",
  "refund.whenRefund.doubleCharge": "a double charge for the same plan or product",
  "refund.whenRefund.contact": "If you think there was a payment error, email us at",
  "refund.whenRefund.within": "within 7 days with your Lemon Squeezy receipt and we will check it.",
  "refund.cancelling.title": "Cancelling paid access to tools",
  "refund.cancelling.content": "You can cancel your paid access to the tools at any time from your account page. After you cancel, you keep access until the end of the paid period, then your account moves back to review mode. We do not give partial refunds for the remaining time of a paid month or year.",

  // Document Pack Checkout
  "wordPackCheckout.title": "ISO 27001 Document Pack",
  "wordPackCheckout.subtitle": "One-time purchase for 40+ ISO 27001 documents in editable Word format + 2 Excel registers (Risk Register and Statement of Applicability)",
  "wordPackCheckout.loadingCheckout": "Loading checkout...",
  "wordPackCheckout.checkoutFailed": "Checkout failed to load. Please try again or contact support.",
  "wordPackCheckout.successMessage": "Thank you. You now have access to the ISO 27001 Document Pack.",
  "wordPackCheckout.activationError": "Error activating ISO 27001 Document Pack. Please contact support.",
  "wordPackCheckout.paymentFailed": "Payment failed. Please try again or contact support.",
  "wordPackCheckout.paymentCancelled": "Payment was cancelled. You were not charged.",
  "wordPackCheckout.pleaseSignIn": "Please sign in to purchase.",
  "wordPackCheckout.signInRequired": "Sign in required",
  "wordPackCheckout.signInDescription": "Please sign in or create an account to purchase the ISO 27001 Document Pack.",
  "wordPackCheckout.signInSignUp": "Sign In / Sign Up",
  "wordPackCheckout.pdfWatermarked": "40+ professionally written ISO 27001 policies, procedures, and templates in editable Word format, plus 2 Excel registers (Risk Register and Statement of Applicability). No watermarks. Instant download.",
  "wordPackCheckout.feature1": "40+ ISO 27001 documents in clean Word format",
  "wordPackCheckout.feature2": "2 Excel registers: Risk Register & Statement of Applicability",
  "wordPackCheckout.feature3": "No watermarks - ready to edit",
  "wordPackCheckout.oneTimePayment": "One-time payment",
  "wordPackCheckout.testModeButton": "Get Document Pack (Test Mode)",
  "wordPackCheckout.processing": "Processing...",
  "wordPackCheckout.signInToPurchase": "Sign In to Purchase",
  "wordPackCheckout.emailRequired": "Your email is required to receive the download link.",
  "wordPackCheckout.continueToPayment": "Continue to Payment",
  "wordPackCheckout.changeEmail": "Change email",
  "wordPackCheckout.emailLabel": "Email address",
  "wordPackCheckout.downloadStarted": "Download started!",
  "wordPackCheckout.accountRequired": "Account Required",
  "wordPackCheckout.accountRequiredDesc": "Please create an account or sign in to purchase the ISO 27001 Document Pack. This helps us deliver your purchase and provide support.",
  "wordPackCheckout.whyAccount": "Why create an account?",
  "wordPackCheckout.accountBenefit1": "Access your purchase anytime",
  "wordPackCheckout.accountBenefit2": "Secure payment processing",
  "wordPackCheckout.accountBenefit3": "Priority customer support",
  "wordPackCheckout.createAccount": "Create Account to Purchase",
  "wordPackCheckout.alreadyHaveAccount": "Already have an account?",
  "wordPackCheckout.signIn": "Sign in",

  // AI Chatbot
  "openChat": "Open chat assistant",
  "closeChat": "Close chat",
  "aiAssistant": "ISO 27001 Assistant",
  "chatWelcome": "Hi! I'm your ISO 27001 assistant. Ask me anything about implementation, certification, or how to use the toolkit.",
  "chatTooltip": "Ask me anything about ISO 27001!",
  "typeMessage": "Type your message...",
  "sendMessage": "Send message",
  "chat.messagesLeft": "left today",
  "chat.freeModeBanner": `${FREE_MSG} free messages per day • Upgrade for full implementation help`,
  "chat.dailyLimitReached": `You've reached your daily free message limit (${FREE_MSG} messages/day). For more, upgrade to AI Assistant Plus ($${PLUS}/month, ${PLUS_MSG} messages/day) or a ISO 27001 Audit Room subscription (${PRO_MSG} messages/day).\n\n💬 AI Assistant Plus ($${PLUS}/month): https://iso27001kit.com/pricing\n🔧 ISO 27001 Audit Room ($${PRO_M}/month): https://iso27001kit.com/pricing\n📄 Document Pack ($${DOC} one-time): https://iso27001kit.com/documents`,
  "chat.limitReachedToast": "Daily message limit reached. Upgrade for unlimited access.",
  "chat.limitBanner": "Daily limit reached",
  "chat.upgradeCta": "Upgrade for unlimited help →",
  "chat.limitPlaceholder": "Daily limit reached - upgrade for more",

  // Implementation Roadmap Phases
  "roadmap.phase1.name": "Phase 1: Preparation & Gap Analysis",
  "roadmap.phase1.activity1": "Secure management commitment and budget approval",
  "roadmap.phase1.activity2": "Form information security steering committee",
  "roadmap.phase1.activity3": "Define ISMS scope and boundaries",
  "roadmap.phase1.activity4": "Conduct initial gap analysis against ISO 27001",
  "roadmap.phase1.activity5": "Identify current security controls and documentation",
  "roadmap.phase1.activity6": "Document current state assessment findings",
  "roadmap.phase1.deliverable1": "Project charter and scope document",
  "roadmap.phase1.deliverable2": "Steering committee charter",
  "roadmap.phase1.deliverable3": "Gap analysis report",
  "roadmap.phase1.deliverable4": "Current state assessment",
  "roadmap.phase1.deliverable5": "Resource and budget plan",
  "roadmap.phase1.resource1": "Project manager",
  "roadmap.phase1.resource2": "ISO 27001 consultant (recommended)",
  "roadmap.phase1.resource3": "IT and security teams",
  "roadmap.phase1.resource4": "Department representatives",

  "roadmap.phase2.name": "Phase 2: Risk Assessment & Treatment",
  "roadmap.phase2.activity1": "Identify information assets and asset owners",
  "roadmap.phase2.activity2": "Identify threats and vulnerabilities",
  "roadmap.phase2.activity3": "Assess likelihood and impact of risks",
  "roadmap.phase2.activity4": "Calculate risk levels using risk matrix",
  "roadmap.phase2.activity5": "Define risk acceptance criteria",
  "roadmap.phase2.activity6": "Develop risk treatment plans",
  "roadmap.phase2.deliverable1": "Asset inventory",
  "roadmap.phase2.deliverable2": "Risk assessment report",
  "roadmap.phase2.deliverable3": "Risk treatment plan",
  "roadmap.phase2.deliverable4": "Statement of Applicability (draft)",
  "roadmap.phase2.resource1": "Risk assessment team",
  "roadmap.phase2.resource2": "Asset owners",
  "roadmap.phase2.resource3": "Technical specialists",
  "roadmap.phase2.resource4": "ISMS coordinator",

  "roadmap.phase3.name": "Phase 3: Policy & Documentation Development",
  "roadmap.phase3.activity1": "Develop information security policy framework",
  "roadmap.phase3.activity2": "Create required policies and procedures",
  "roadmap.phase3.activity3": "Document control implementation guidelines",
  "roadmap.phase3.activity4": "Establish document control processes",
  "roadmap.phase3.activity5": "Review and approve all documentation",
  "roadmap.phase3.activity6": "Communicate policies to all staff",
  "roadmap.phase3.deliverable1": "Information Security Policy",
  "roadmap.phase3.deliverable2": "Access Control Policy",
  "roadmap.phase3.deliverable3": "Incident Management Procedures",
  "roadmap.phase3.deliverable4": "Business Continuity Plans",
  "roadmap.phase3.deliverable5": "All required ISO 27001 documentation",
  "roadmap.phase3.resource1": "Policy writers",
  "roadmap.phase3.resource2": "Legal/compliance team",
  "roadmap.phase3.resource3": "Department heads",
  "roadmap.phase3.resource4": "ISMS coordinator",

  "roadmap.phase4.name": "Phase 4: Security Control Implementation",
  "roadmap.phase4.activity1": "Implement technical security controls",
  "roadmap.phase4.activity2": "Deploy access management systems",
  "roadmap.phase4.activity3": "Establish incident response procedures",
  "roadmap.phase4.activity4": "Implement logging and monitoring",
  "roadmap.phase4.activity5": "Configure backup and recovery systems",
  "roadmap.phase4.activity6": "Deploy encryption and data protection",
  "roadmap.phase4.deliverable1": "Implemented technical controls",
  "roadmap.phase4.deliverable2": "Access control systems",
  "roadmap.phase4.deliverable3": "Incident management system",
  "roadmap.phase4.deliverable4": "Monitoring and logging infrastructure",
  "roadmap.phase4.deliverable5": "Backup and recovery procedures",
  "roadmap.phase4.resource1": "IT security team",
  "roadmap.phase4.resource2": "System administrators",
  "roadmap.phase4.resource3": "Network engineers",
  "roadmap.phase4.resource4": "Security vendors",

  "roadmap.phase5.name": "Phase 5: Training & Awareness Program",
  "roadmap.phase5.activity1": "Develop security awareness training program",
  "roadmap.phase5.activity2": "Conduct mandatory security training sessions",
  "roadmap.phase5.activity3": "Train incident response team",
  "roadmap.phase5.activity4": "Educate on policies and procedures",
  "roadmap.phase5.activity5": "Establish ongoing awareness campaigns",
  "roadmap.phase5.activity6": "Test knowledge and understanding",
  "roadmap.phase5.deliverable1": "Training materials and presentations",
  "roadmap.phase5.deliverable2": "Training attendance records",
  "roadmap.phase5.deliverable3": "Security awareness campaign",
  "roadmap.phase5.deliverable4": "Competency assessment results",
  "roadmap.phase5.resource1": "Training coordinator",
  "roadmap.phase5.resource2": "Subject matter experts",
  "roadmap.phase5.resource3": "HR department",
  "roadmap.phase5.resource4": "All employees",

  "roadmap.phase6.name": "Phase 6: Internal Audit & Management Review",
  "roadmap.phase6.activity1": "Conduct internal ISMS audit",
  "roadmap.phase6.activity2": "Review audit findings with control owners",
  "roadmap.phase6.activity3": "Implement corrective actions",
  "roadmap.phase6.activity4": "Perform management review meeting",
  "roadmap.phase6.activity5": "Update risk assessment and SoA",
  "roadmap.phase6.activity6": "Finalize all documentation",
  "roadmap.phase6.deliverable1": "Internal audit report",
  "roadmap.phase6.deliverable2": "Corrective action plans",
  "roadmap.phase6.deliverable3": "Management review minutes",
  "roadmap.phase6.deliverable4": "Updated Statement of Applicability",
  "roadmap.phase6.deliverable5": "Final risk assessment",
  "roadmap.phase6.resource1": "Internal auditors",
  "roadmap.phase6.resource2": "Management team",
  "roadmap.phase6.resource3": "Control owners",
  "roadmap.phase6.resource4": "ISMS coordinator",

  "roadmap.phase7.name": "Phase 7: Certification Audit & Certification",
  "roadmap.phase7.activity1": "Stage 1 audit: Documentation review",
  "roadmap.phase7.activity2": "Address Stage 1 findings",
  "roadmap.phase7.activity3": "Stage 2 audit: On-site assessment",
  "roadmap.phase7.activity4": "Respond to auditor questions",
  "roadmap.phase7.activity5": "Address any non-conformities",
  "roadmap.phase7.activity6": "Receive certification decision",
  "roadmap.phase7.deliverable1": "Stage 1 audit report",
  "roadmap.phase7.deliverable2": "Stage 2 audit report",
  "roadmap.phase7.deliverable3": "Non-conformity responses",
  "roadmap.phase7.deliverable4": "ISO 27001 certificate",
  "roadmap.phase7.deliverable5": "Continuous improvement plan",
  "roadmap.phase7.resource1": "Certification body auditors",
  "roadmap.phase7.resource2": "ISMS coordinator",
  "roadmap.phase7.resource3": "Management team",
  "roadmap.phase7.resource4": "All staff",

  "roadmap.html.title": "ISO 27001 Implementation Roadmap",
  "roadmap.html.subtitle": "Strategic Plan for Certification",
  "roadmap.html.projectOverview": "Project Overview",
  "roadmap.html.orgSize": "Organization Size",
  "roadmap.html.maturityLevel": "Maturity Level",
  "roadmap.html.totalDuration": "Total Duration",
  "roadmap.html.startDate": "Start Date",
  "roadmap.html.targetCompletion": "Target Completion",
  "roadmap.html.dedicatedResource": "Dedicated ISMS Resource",
  "roadmap.html.implementationPhases": "Implementation Phases",
  "roadmap.html.duration": "Duration",
  "roadmap.html.month": "month",
  "roadmap.html.months": "months",
  "roadmap.html.keyActivities": "Key Activities",
  "roadmap.html.deliverables": "Deliverables",
  "roadmap.html.requiredResources": "Required Resources",
  "roadmap.html.footer1": "This implementation roadmap was generated for",
  "roadmap.html.footer2": "as part of ISO 27001 certification planning",

  // Supplier Risk Manager
  "supplier.action.securityAssessment": "Conduct detailed security assessment",
  "supplier.action.reviewDataHandling": "Review data handling procedures",
  "supplier.action.strengthenClauses": "Strengthen contractual security clauses",
  "supplier.action.addDataProtection": "Add data protection addendum",
  "supplier.action.assessTransfer": "Assess data transfer compliance (GDPR/SCCs)",
  "supplier.action.considerAlternative": "Consider alternative suppliers in lower-risk regions",
  "supplier.action.scheduleAudit": "Schedule supplier security audit",
  "supplier.action.enhancedMonitoring": "Implement enhanced monitoring",
  "supplier.action.continueMonitoring": "Continue regular monitoring",
  "supplier.action.reviewAnnually": "Review annually",

  "supplier.serviceType.cloud": "Cloud Services (IaaS/PaaS/SaaS)",
  "supplier.serviceType.softwareDev": "Software Development",
  "supplier.serviceType.itSupport": "IT Support/Managed Services",
  "supplier.serviceType.dataProcessing": "Data Processing",
  "supplier.serviceType.consulting": "Consulting",
  "supplier.serviceType.logistics": "Logistics/Delivery",
  "supplier.serviceType.financial": "Financial Services",
  "supplier.serviceType.legal": "Legal Services",
  "supplier.serviceType.marketing": "Marketing/Advertising",
  "supplier.serviceType.other": "Other",

  "supplier.dataAccess.none": "No data access",
  "supplier.dataAccess.metadata": "Metadata only",
  "supplier.dataAccess.nonSensitive": "Non-sensitive data",
  "supplier.dataAccess.sensitive": "Sensitive/PII data",
  "supplier.dataAccess.critical": "Critical/Regulated data",

  "supplier.rating.minimal": "1 - Minimal",
  "supplier.rating.low": "2 - Low",
  "supplier.rating.medium": "3 - Medium",
  "supplier.rating.high": "4 - High",
  "supplier.rating.critical": "5 - Critical",
  "supplier.rating.veryWeak": "1 - Very Weak",
  "supplier.rating.weak": "2 - Weak",
  "supplier.rating.adequate": "3 - Adequate",
  "supplier.rating.strong": "4 - Strong",
  "supplier.rating.veryStrong": "5 - Very Strong",
  "supplier.rating.veryLow": "1 - Very Low",
  "supplier.rating.veryHigh": "5 - Very High",

  "supplier.html.title": "Supplier Risk Assessment",
  "supplier.html.riskSummary": "Supplier Risk Summary",
  "supplier.html.detailedAssessments": "Detailed Assessments & Actions",
  "supplier.html.recommendedActions": "Recommended Actions:",
  "supplier.html.riskScore": "Risk Score",
  "supplier.html.riskLevel": "Risk Level",
  "supplier.html.dataSensitivityRating": "Data Sensitivity Rating",
  "supplier.html.contractStrengthRating": "Contract Strength Rating",
  "supplier.html.countryRiskRating": "Country Risk Rating",
  "supplier.html.dataAccessLevel": "Data Access Level",
  "supplier.html.contractExpiry": "Contract Expiry",
  "supplier.html.notSpecified": "Not specified",

  // RTO/RPO & BIA Helper
  "rtorpo.impact.critical": "Critical",
  "rtorpo.impact.high": "High",
  "rtorpo.impact.medium": "Medium",
  "rtorpo.impact.low": "Low",

  "rtorpo.rto.lessThan1Hour": "< 1 hour",
  "rtorpo.rto.1to4Hours": "1-4 hours",
  "rtorpo.rto.4to8Hours": "4-8 hours",
  "rtorpo.rto.8to24Hours": "8-24 hours",
  "rtorpo.rto.24to48Hours": "24-48 hours",
  "rtorpo.rto.48to72Hours": "48-72 hours",
  "rtorpo.rto.72PlusHours": "72+ hours",

  "rtorpo.rpo.zeroDataLoss": "Zero data loss",
  "rtorpo.rpo.lessThan1Hour": "< 1 hour",
  "rtorpo.rpo.1to4Hours": "1-4 hours",
  "rtorpo.rpo.4to24Hours": "4-24 hours",
  "rtorpo.rpo.24PlusHours": "24+ hours",

  "rtorpo.bia.classifiedAs": "is classified as",
  "rtorpo.bia.impact": "impact",
  "rtorpo.bia.criticalProcess": "This is a critical business process requiring immediate recovery",
  "rtorpo.bia.dataLossMinimal": "Data loss tolerance is minimal",
  "rtorpo.bia.recommendRedundant": "Recommend redundant systems and real-time data replication.",
  "rtorpo.bia.highPriority": "High priority recovery needed within",
  "rtorpo.bia.acceptableDataLoss": "Acceptable data loss window:",
  "rtorpo.bia.considerWarmStandby": "Consider warm standby systems and regular backups.",
  "rtorpo.bia.recoveryTarget": "Recovery target:",
  "rtorpo.bia.dataRecoveryPoint": "Data recovery point:",
  "rtorpo.bia.standardBackup": "Standard backup and recovery procedures should suffice.",
  "rtorpo.bia.lowPriority": "Low priority for recovery. Target RTO:",
  "rtorpo.bia.rpo": "RPO:",
  "rtorpo.bia.basicBackup": "Basic backup procedures are adequate.",

  "rtorpo.html.title": "Business Impact Analysis",
  "rtorpo.html.subtitle": "RTO, RPO & BIA Report",
  "rtorpo.html.processesAnalyzed": "Processes Analyzed",
  "rtorpo.html.rtoRpoSummary": "RTO/RPO Summary",
  "rtorpo.html.process": "Process",
  "rtorpo.html.criticality": "Criticality",
  "rtorpo.html.maxDowntime": "Max Downtime",
  "rtorpo.html.priority": "Priority",
  "rtorpo.html.detailedBIA": "Detailed Business Impact Analysis",
  "rtorpo.html.processNumber": "Process #",
  "rtorpo.html.processName": "Process Name",
  "rtorpo.html.description": "Description",
  "rtorpo.html.processOwner": "Process Owner",
  "rtorpo.html.dependencies": "Dependencies",
  "rtorpo.html.impactAssessment": "Impact Assessment",
  "rtorpo.html.financialImpact": "Financial Impact",
  "rtorpo.html.operationalImpact": "Operational Impact",
  "rtorpo.html.reputationalImpact": "Reputational Impact",
  "rtorpo.html.regulatoryImpact": "Regulatory Impact",
  "rtorpo.html.recoveryObjectives": "Recovery Objectives",
  "rtorpo.html.maxTolerableDowntime": "Max Tolerable Downtime",
  "rtorpo.html.dataLossTolerance": "Data Loss Tolerance",
  "rtorpo.html.rto": "Recovery Time Objective (RTO)",
  "rtorpo.html.rpoFull": "Recovery Point Objective (RPO)",
  "rtorpo.html.biaSummary": "BIA Summary:",
  "rtorpo.html.keyDefinitions": "Key Definitions",
  "rtorpo.html.rtoDef": "Maximum acceptable time to restore a process after disruption",
  "rtorpo.html.rpoDef": "Maximum acceptable data loss measured in time",
  "rtorpo.html.mtdDef": "Absolute maximum time a process can be unavailable",
  "rtorpo.html.hours": "hours",

  // Annex A Selector
  "annexSelector.question.size": "What is your organization size?",
  "annexSelector.question.remote": "Do you have remote workers?",
  "annexSelector.question.cloud": "Do you use cloud services?",
  "annexSelector.question.development": "Do you develop software in-house?",
  "annexSelector.question.data": "What type of sensitive data do you handle?",

  "annexSelector.option.small": "Small (1-50 employees)",
  "annexSelector.option.medium": "Medium (51-250 employees)",
  "annexSelector.option.large": "Large (250+ employees)",
  "annexSelector.option.noRemote": "No remote workers",
  "annexSelector.option.someRemote": "Some remote workers",
  "annexSelector.option.mostlyRemote": "Mostly or fully remote",
  "annexSelector.option.noCloud": "No cloud services",
  "annexSelector.option.someCloud": "Some cloud services (SaaS)",
  "annexSelector.option.extensiveCloud": "Extensive cloud usage (IaaS/PaaS)",
  "annexSelector.option.noDev": "No software development",
  "annexSelector.option.someDev": "Some internal development",
  "annexSelector.option.primaryDev": "Software development is our primary business",
  "annexSelector.option.minimalData": "Minimal sensitive data",
  "annexSelector.option.personalData": "Personal data (PII/GDPR)",
  "annexSelector.option.financialData": "Financial or health data",

  "annexSelector.html.title": "Annex A Control Selection Report",
  "annexSelector.html.assessmentAnswers": "Assessment Answers",
  "annexSelector.html.question": "Question",
  "annexSelector.html.answer": "Answer",
  "annexSelector.html.notAnswered": "Not answered",
  "annexSelector.html.recommendedControls": "Recommended Annex A Controls",
  "annexSelector.html.controls": "controls",
  "annexSelector.html.controlId": "Control ID",
  "annexSelector.html.controlTitle": "Control Title",
  "annexSelector.html.category": "Category",
  "annexSelector.html.showingOf": "Showing",
  "annexSelector.html.of": "of",
  "annexSelector.html.upgradeToSeeAll": "controls. Upgrade to Pro to see all.",
  "annexSelector.html.nextSteps": "Next Steps",
  "annexSelector.html.step1": "Review the recommended controls with your security team",
  "annexSelector.html.step2": "Document justification for any controls you decide to exclude",
  "annexSelector.html.step3": "Use these controls as a starting point for your Statement of Applicability (SoA)",
  "annexSelector.html.step4": "Consider additional controls based on specific risk assessments",

  "annexSelector.ui.questionOf": "Question",
  "annexSelector.ui.of": "of",
  "annexSelector.ui.complete": "Complete",
  "annexSelector.ui.previous": "Previous",
  "annexSelector.ui.next": "Next",
  "annexSelector.ui.viewResults": "View Results",
  "annexSelector.ui.assessmentComplete": "Assessment Complete",
  "annexSelector.ui.basedOnAnswers": "Based on your answers, we recommend",
  "annexSelector.ui.annexAControls": "Annex A controls",
  "annexSelector.ui.forYourOrg": "for your organization.",
  "annexSelector.ui.startOver": "Start Over",

  // ISO 27001 Control Categories
  "controls.category.Organizational": "Organizational",
  "controls.category.People": "People",
  "controls.category.Physical": "Physical",
  "controls.category.Technological": "Technological",

  // Organizational Controls (5.1-5.37)
  "controls.5.1": "Policies for information security",
  "controls.5.2": "Information security roles and responsibilities",
  "controls.5.3": "Segregation of duties",
  "controls.5.4": "Management responsibilities",
  "controls.5.5": "Contact with authorities",
  "controls.5.6": "Contact with special interest groups",
  "controls.5.7": "Threat intelligence",
  "controls.5.8": "Information security in project management",
  "controls.5.9": "Inventory of information and other associated assets",
  "controls.5.10": "Acceptable use of information and other associated assets",
  "controls.5.11": "Return of assets",
  "controls.5.12": "Classification of information",
  "controls.5.13": "Labelling of information",
  "controls.5.14": "Information transfer",
  "controls.5.15": "Access control",
  "controls.5.16": "Identity management",
  "controls.5.17": "Authentication information",
  "controls.5.18": "Access rights",
  "controls.5.19": "Information security in supplier relationships",
  "controls.5.20": "Addressing information security within supplier agreements",
  "controls.5.21": "Managing information security in the ICT supply chain",
  "controls.5.22": "Monitoring, review and change management of supplier services",
  "controls.5.23": "Information security for use of cloud services",
  "controls.5.24": "Information security incident management planning and preparation",
  "controls.5.25": "Assessment and decision on information security events",
  "controls.5.26": "Response to information security incidents",
  "controls.5.27": "Learning from information security incidents",
  "controls.5.28": "Collection of evidence",
  "controls.5.29": "Information security during disruption",
  "controls.5.30": "ICT readiness for business continuity",
  "controls.5.31": "Legal, statutory, regulatory and contractual requirements",
  "controls.5.32": "Intellectual property rights",
  "controls.5.33": "Protection of records",
  "controls.5.34": "Privacy and protection of PII",
  "controls.5.35": "Independent review of information security",
  "controls.5.36": "Compliance with policies, rules and standards for information security",
  "controls.5.37": "Documented operating procedures",

  // People Controls (6.1-6.8)
  "controls.6.1": "Screening",
  "controls.6.2": "Terms and conditions of employment",
  "controls.6.3": "Information security awareness, education and training",
  "controls.6.4": "Disciplinary process",
  "controls.6.5": "Responsibilities after termination or change of employment",
  "controls.6.6": "Confidentiality or non-disclosure agreements",
  "controls.6.7": "Remote working",
  "controls.6.8": "Information security event reporting",

  // Physical Controls (7.1-7.14)
  "controls.7.1": "Physical security perimeters",
  "controls.7.2": "Physical entry",
  "controls.7.3": "Securing offices, rooms and facilities",
  "controls.7.4": "Physical security monitoring",
  "controls.7.5": "Protecting against physical and environmental threats",
  "controls.7.6": "Working in secure areas",
  "controls.7.7": "Clear desk and clear screen",
  "controls.7.8": "Equipment siting and protection",
  "controls.7.9": "Security of assets off-premises",
  "controls.7.10": "Storage media",
  "controls.7.11": "Supporting utilities",
  "controls.7.12": "Cabling security",
  "controls.7.13": "Equipment maintenance",
  "controls.7.14": "Secure disposal or re-use of equipment",

  // Technological Controls (8.1-8.34)
  "controls.8.1": "User endpoint devices",
  "controls.8.2": "Privileged access rights",
  "controls.8.3": "Information access restriction",
  "controls.8.4": "Access to source code",
  "controls.8.5": "Secure authentication",
  "controls.8.6": "Capacity management",
  "controls.8.7": "Protection against malware",
  "controls.8.8": "Management of technical vulnerabilities",
  "controls.8.9": "Configuration management",
  "controls.8.10": "Information deletion",
  "controls.8.11": "Data masking",
  "controls.8.12": "Data leakage prevention",
  "controls.8.13": "Information backup",
  "controls.8.14": "Redundancy of information processing facilities",
  "controls.8.15": "Logging",
  "controls.8.16": "Monitoring activities",
  "controls.8.17": "Clock synchronization",
  "controls.8.18": "Use of privileged utility programs",
  "controls.8.19": "Installation of software on operational systems",
  "controls.8.20": "Networks security",
  "controls.8.21": "Security of network services",
  "controls.8.22": "Segregation of networks",
  "controls.8.23": "Web filtering",
  "controls.8.24": "Use of cryptography",
  "controls.8.25": "Secure development life cycle",
  "controls.8.26": "Application security requirements",
  "controls.8.27": "Secure system architecture and engineering principles",
  "controls.8.28": "Secure coding",
  "controls.8.29": "Security testing in development and acceptance",
  "controls.8.30": "Outsourced development",
  "controls.8.31": "Separation of development, test and production environments",
  "controls.8.32": "Change management",
  "controls.8.33": "Test information",
  "controls.8.34": "Protection of information systems during audit testing",

  // Phase 1 - Header / Footer / Chatbot additions
  "nav.startHere": "Start Here",
  "nav.aiAssistant": "AI Assistant",
  "nav.riskManagement": "Risk Management",
  "nav.tryFree": "TRY FREE",
  "nav.openMenu": "Open navigation menu",

  "footer.docPack.banner": "ISO 27001 Document Pack",
  "footer.docPack.tagline": "40+ editable Word files, no watermarks",
  "footer.docPack.price": `$${DOC} one-time`,

  "chatbot.title": "ISO 27001 AI Assistant",
  "chatbot.plan.free": "Free",
  "chatbot.plan.plus": "Plus",
  "chatbot.plan.proTools": "Pro Tools",
  "chatbot.usage.template": "{used} / {limit} messages used today",
  "chatbot.upgrade": "Upgrade",
  "chatbot.welcomeFreeNote": "Free plan - 3 questions per day. Upgrade to Plus to continue using the AI assistant.",
  "chatbot.responseBasic": "Basic response",
  "chatbot.responseStructured": "Structured response",
  "chatbot.relatedResources": "Related resources:",
  "chatbot.related.riskRegister": "Risk register template",
  "chatbot.related.policyPack": "Policy pack",
  "chatbot.limitReached": "You've reached today's message limit.",
  "chatbot.limit.upgradeBlurb": "Upgrade to Plus for extended usage and more detailed answers.",
  "chatbot.limit.upgradeTitle": "Upgrade to Plus",
  "chatbot.limit.upgradePrice": `$${PLUS}/mo`,
  "chatbot.limit.plusQuotaNote": "Your Plus quota resets daily. Try again after the reset.",
  "chatbot.limit.resetTemplate": "Resets in {hours} {unit}",
  "chatbot.limit.hour": "hour",
  "chatbot.limit.hours": "hours",
  "chatbot.toast.tooFast": "Please wait {secs}s before sending another message.",
  "chatbot.toast.dailyLimit": "You've reached today's message limit.",
  "chatbot.toast.tooLong": "Message too long (max {max} characters)",
  "chatbot.toast.failed": "Failed to send message",
  "chatbot.aria.upgradePlus": `Upgrade to Plus for $${PLUS} per month`,

  // Phase 2 - Home page sections
  // HeroSection
  "home.hero.badge": "ISO 27001:2022 ALIGNED",
  "home.hero.titleStart": "ISO 27001",
  "home.hero.titleEnd": "done in hours, not weeks.",
  "home.hero.trustedBy": "Trusted by 1,000+ consultants",
  "home.hero.tagline": "40+ audit-ready policies and templates - fully editable, no watermarks. Stop writing from scratch.",
  "home.hero.browseDocs": "Browse documents",
  "home.hero.openTools": "Open Tools",
  "home.hero.startHere": "Start Here",
  "home.hero.stat.documents": "Documents",
  "home.hero.stat.aiTools": "AI Tools",
  "home.hero.stat.riskCopilot": "Risk Copilot",
  "home.hero.stat.consultants": "Consultants",
  "home.hero.docPackLabel": "Document Pack",
  "home.hero.docPackPreview": `Preview free · From $${DOC}`,
  "home.hero.docCard.infoSec": "Information Security Policy",
  "home.hero.docCard.risk": "Risk Assessment Procedure",
  "home.hero.docCard.access": "Access Control Policy",
  "home.hero.docCard.pages": "{count} pages",
  "home.hero.docCard.fullInPack": "Full doc in pack",
  "home.hero.docCard.morePack": "More than 40 documents and registers in the pack",

  // ToolsShowcaseSection
  "home.toolsShowcase.title": "11 AI Implementation Tools",
  "home.toolsShowcase.subtitle": "Try free with limited output · Unlimited with Pro",
  "home.toolsShowcase.chip.gap": "Gap Assessor",
  "home.toolsShowcase.chip.risk": "Risk Assessor",
  "home.toolsShowcase.chip.soa": "SoA Generator",
  "home.toolsShowcase.chip.assets": "Asset Inventory",
  "home.toolsShowcase.chip.audit": "Audit Checklist",
  "home.toolsShowcase.more": "+ 5 more",
  "home.toolsShowcase.tryFree": "Try Tools For Free",

  // RiskCopilotBanner
  "home.copilotBanner.badge": "✦ NEW",
  "home.copilotBanner.title": "Risk Copilot - live risk management",
  "home.copilotBanner.subtitle": "Risk register, controls, treatments & reports in one dashboard. Free demo available.",
  "home.copilotBanner.openCopilot": "Open Copilot",
  "home.copilotBanner.tryCopilot": "Try Risk Copilot",

  // SampleOutputsSection
  "home.sample.previewBadge": "Preview",
  "home.sample.heading": "See What You Get",
  "home.sample.fullOnPro": "Full output available on Pro plan",
  "home.sample.card.riskRegister": "Risk Register",
  "home.sample.card.soa": "Statement of Applicability",
  "home.sample.card.gap": "Gap Analysis Report",
  "home.sample.col.asset": "Asset",
  "home.sample.col.threat": "Threat",
  "home.sample.col.likelihoodShort": "L",
  "home.sample.col.impactShort": "I",
  "home.sample.col.score": "Score",
  "home.sample.col.control": "Control",
  "home.sample.col.name": "Name",
  "home.sample.col.status": "Status",
  "home.sample.status.implemented": "Implemented",
  "home.sample.status.partial": "Partial",
  "home.sample.status.planned": "Planned",
  "home.sample.status.na": "N/A",
  "home.sample.row.erp": "ERP System",
  "home.sample.row.customerDb": "Customer DB",
  "home.sample.row.email": "Email Server",
  "home.sample.threat.ransomware": "Ransomware",
  "home.sample.threat.sqli": "SQL Injection",
  "home.sample.threat.phishing": "Phishing",
  "home.sample.soa.policies": "Policies for information security",
  "home.sample.soa.screening": "Screening",
  "home.sample.soa.physical": "Physical security perimeters",
  "home.sample.soa.endpoints": "User endpoint devices",
  "home.sample.gap.context": "4 - Context",
  "home.sample.gap.leadership": "5 - Leadership",
  "home.sample.gap.planning": "6 - Planning",
  "home.sample.gap.support": "7 - Support",
  "home.sample.gap.operation": "8 - Operation",

  // HomePricingSection
  "homePricing.headingDefault": "Simple, transparent pricing",
  "homePricing.headingActive": "Your Plan",
  "homePricing.subDefault": "Preview everything free - no credit card needed.",
  "homePricing.subActive": "You have full access to the platform.",
  "homePricing.free.title": "Free",
  "homePricing.free.tagline": "Explore before you commit",
  "homePricing.free.priceLabel": "forever",
  "homePricing.free.noCard": "No credit card needed",
  "homePricing.free.feature1": "First page of every document",
  "homePricing.free.feature2": "All tools - limited output",
  "homePricing.free.feature3": "Risk Copilot free demo",
  "homePricing.free.feature4": "Full documents locked",
  "homePricing.free.feature5": "Full exports locked",
  "homePricing.free.cta": "Start for free",
  "homePricing.free.noSignup": "No signup required",
  "homePricing.docPack.owned": "Owned",
  "homePricing.docPack.popular": "★ MOST POPULAR",
  "homePricing.docPack.title": "Document Pack",
  "homePricing.docPack.taglineOwned": "Download your documents anytime",
  "homePricing.docPack.tagline": "For teams that need the documents now",
  "homePricing.docPack.priceLabel": "one-time",
  "homePricing.docPack.payOnce": "Pay once, own forever",
  "homePricing.docPack.bonusLabel": "★ Includes",
  "homePricing.docPack.bonusTextStart": "Lifetime access with",
  "homePricing.docPack.bonusBold": "free updates",
  "homePricing.docPack.bonusTextEnd": "as ISO 27001 evolves. Delivered straight to your inbox.",
  "homePricing.docPack.feature1": "40+ fully editable documents",
  "homePricing.docPack.feature2": "Word docs + 2 Excel registers",
  "homePricing.docPack.feature3": "Unlimited exports",
  "homePricing.docPack.feature4": "Lifetime access & updates",
  "homePricing.docPack.feature5": "Free updates as standards evolve",
  "homePricing.docPack.feature6": "AI tools not included",
  "homePricing.docPack.downloadCta": "Download Documents",
  "homePricing.docPack.cta": "Get the Editable Pack",
  "homePricing.docPack.instant": "Instant access after payment",
  "homePricing.pro.active": "Active",
  "homePricing.pro.subscription": "SUBSCRIPTION",
  "homePricing.pro.title": "Pro",
  "homePricing.pro.taglineActive": "You have full access",
  "homePricing.pro.tagline": "All tools, Risk Copilot, and AI Assistant",
  "homePricing.pro.billingAria": "Billing interval",
  "homePricing.pro.monthly": "Monthly",
  "homePricing.pro.yearly": "Yearly",
  "homePricing.pro.savePrefix": "Save",
  "homePricing.pro.perMonth": "/mo",
  "homePricing.pro.perYear": "/yr",
  "homePricing.pro.worksOutTo": "Works out to ${amount}/mo",
  "homePricing.pro.orYearly": "or ${yearly}/yr - save ${savings}",
  "homePricing.pro.feature1": "Everything in Free",
  "homePricing.pro.feature2": "All 11 AI tools - unlimited",
  "homePricing.pro.feature3": `ISO 27001 AI Assistant - ${PRO_MSG} messages/day (${Math.round(PRO_MSG / PLUS_MSG)}x the Plus tier)`,
  "homePricing.pro.feature4": "Risk Copilot full access",
  "homePricing.pro.manage": "Manage Subscription",
  "homePricing.pro.ctaYearly": "Get Pro - Yearly",
  "homePricing.pro.ctaMonthly": "Get Pro - Monthly",
  "homePricing.pro.cancel": "Cancel anytime",
  "homePricing.enterprise.badge": "Enterprise Level",
  "homePricing.enterprise.title": "Enterprise",
  "homePricing.enterprise.tagline": "Tailored for organizations with custom compliance needs",
  "homePricing.enterprise.priceLabel": "Custom",
  "homePricing.enterprise.subtext": "Multi-user access, onboarding, and dedicated support",
  "homePricing.enterprise.contact": "Contact us",
  "homePricing.enterprise.learn": "Learn more about enterprise plans",

  // HomeReviewsSection
  "homeReviews.heading": "Trusted by security teams",

  // ProductJourneySection
  "home.journey.docs.title": "I need documents now",
  "home.journey.docs.desc": "Policies, templates, checklists and SoA - ready to download and use immediately.",
  "home.journey.docs.cta": "Browse the Kit",
  "home.journey.copilot.tag": "Already have the documents? Take it further.",
  "home.journey.copilot.title": "Manage risk with AI",
  "home.journey.copilot.desc": "12-module AI platform for ISO 27001 risk management, gap analysis, and compliance tracking.",
  "home.journey.copilot.ctaTry": "Try Risk Copilot",
  "home.journey.copilot.ctaOpen": "Open Risk Copilot",

  // ToolsGridSection
  "home.toolsGrid.title": "11 AI-Powered Implementation Tools",
  "home.toolsGrid.subtitle": "Built using AI with expert input and review.",
  "home.toolsGrid.tryFree": "Try Tools Free",
  "home.toolsGrid.unlock": `Unlock All Tools - $${PRO_M}/mo`,

  // RiskCopilotSpotlight
  "home.spotlight.badge.included": "Included in Your Plan",
  "home.spotlight.badge.available": "Now Available",
  "home.spotlight.title.your": "Your",
  "home.spotlight.title.intro": "Introducing",
  "home.spotlight.subtitle": "Stop managing risks in spreadsheets. Risk Copilot is a complete cloud-based platform to identify, assess, treat, and monitor your ISO 27001 risks - all in one place.",
  "home.spotlight.feature1": "12 integrated modules - dashboard to audit trail",
  "home.spotlight.feature2": "Inherent and residual risk scoring",
  "home.spotlight.feature3": "180-day risk review cycle tracking",
  "home.spotlight.feature4": "Export audit-ready Word and Excel reports",
  "home.spotlight.feature5": "Full audit trail with timestamps",
  "home.spotlight.feature6": "Custom risk framework and matrix",
  "home.spotlight.openCta": "Open Risk Copilot",
  "home.spotlight.tryCta": "Try Risk Copilot",
  "home.spotlight.allFeatures": "See All Features",
  "home.spotlight.dashboard": "Risk Dashboard",
  "home.spotlight.stat.total": "Total Risks",
  "home.spotlight.stat.critical": "Critical",
  "home.spotlight.stat.treatments": "Treatments",
  "home.spotlight.stat.controls": "Controls",
  "home.spotlight.heatmap": "Risk Heat Map (5×5)",
  "home.spotlight.rare": "Rare",
  "home.spotlight.almostCertain": "Almost Certain",

  // ProductSuiteSection
  "home.suite.title.has": "Your ISO 27001 Platform",
  "home.suite.title.default": "One Platform. Two Ways to Get Started.",
  "home.suite.subtitle.has": "You have full access to everything.",
  "home.suite.subtitle.default": "Whether you need documents or the full implementation platform - we have you covered.",
  "home.suite.pro.active": "Active",
  "home.suite.pro.popular": "Most Popular",
  "home.suite.pro.descActive": "You have full access to all 11 implementation tools and the 12-module Risk Copilot platform.",
  "home.suite.pro.descDefault": "All 11 implementation tools + full 12-module Risk Copilot platform. Complete exports, persistent data, audit trail, and custom risk framework.",
  "home.suite.pro.openCopilot": "Open Risk Copilot",
  "home.suite.pro.openTools": "Open Tools",
  "home.suite.pro.tryDemo": "Try Demo",
  "home.suite.pro.getPro": "Get Pro - ${amount}/mo",
  "home.suite.docs.owned": "Owned",
  "home.suite.docs.oneTime": "One-Time",
  "home.suite.docs.title": "ISO 27001 Document Pack",
  "home.suite.docs.descOwned": "Your 40+ documents are ready. Download them anytime from your account.",
  "home.suite.docs.descDefault": "40+ professionally written policies, procedures, and templates in clean editable Word format - plus a Risk Register Template (Excel) in your language.",
  "home.suite.docs.download": "Download Documents",
  "home.suite.docs.browse": "Browse Documents",
  "home.suite.docs.explore": "Explore Docs",
  "home.suite.docs.get": "Get the Editable Pack",

  // WhyChooseSection (homepage variant)
  "home.whyChooseV2.title": "Why Choose ISO27001KIT?",
  "home.whyChooseV2.complete.title": "Complete Ecosystem",
  "home.whyChooseV2.complete.desc": "From documentation to risk management - everything you need in one platform.",
  "home.whyChooseV2.expert.title": "Expert-Developed",
  "home.whyChooseV2.expert.desc": "Built by information security professionals with real ISO 27001 implementation experience.",
  "home.whyChooseV2.uptodate.title": "Always Up to Date",
  "home.whyChooseV2.uptodate.desc": "Fully aligned with ISO 27001:2022 and updated as standards evolve.",

  // FooterCtaSection
  "home.footerCta.title.has": "You Have Full Access to ISO27001KIT",
  "home.footerCta.title.default": "Ready to Achieve ISO 27001 Certification?",
  "home.footerCta.subtitle.has": "Access your tools, Risk Copilot, and documents from one place.",
  "home.footerCta.subtitle.default": "Join security professionals using ISO27001KIT to implement and maintain their ISMS.",
  "home.footerCta.openCopilot": "Open Risk Copilot",
  "home.footerCta.tryCopilot": "Try Risk Copilot",
  "home.footerCta.openTools": "Open Tools",
  "home.footerCta.exploreTools": "Explore Pro Tools",
  "home.footerCta.docs.owned": "Document Pack - Owned",
  "home.footerCta.docs.title": `ISO 27001 Document Pack - $${DOC}`,
  "home.footerCta.docs.f1": "40+ Word files",
  "home.footerCta.docs.f2": "Risk Register",
  "home.footerCta.docs.f3": "No watermarks",
  "home.footerCta.docs.download": "Download",
  "home.footerCta.docs.get": "Get It Now",

  // KeyDocumentsSection extras
  "home.keyDocs.bestSelling": "Best-Selling Product",
  "home.keyDocs.headline": "40+ ISO 27001 Documents. Ready to Edit.",
  "home.keyDocs.subtitle": "Preview free in PDF. Get the pack for clean, editable Word files with no watermarks.",
  "home.keyDocs.mandatoryHeading": "Mandatory Policies",
  "home.keyDocs.guidesHeading": "Implementation Guides",
  "home.keyDocs.getPack": "Get the Editable Pack",
  "home.keyDocs.exploreFree": "Explore Free",

  // ConsultationSection extras
  "home.consultation.expertTitle": "Need Expert Guidance?",
  "home.consultation.expertSubtitle": "Book a 30-minute consultation to discuss your ISO 27001 implementation journey. Get personalized advice and answers to your specific questions.",

  // Risk Manager Landing Page
  "riskLanding.hero.badge": "12 Integrated Modules",
  "riskLanding.hero.titlePrefix": "ISO 27001",
  "riskLanding.hero.titleHighlight": "Risk Management Tool",
  "riskLanding.hero.subtitleHas": "Your complete cloud-based platform for ISO 27001 ISMS risk management - identify, assess, treat and monitor information security risks.",
  "riskLanding.hero.subtitleDefault": "The complete cloud-based platform for ISO 27001 ISMS risk management - identify, assess, treat and monitor your Information Security Management System risks, fully aligned with ISO 27001:2022.",
  "riskLanding.hero.openCta": "Open Risk Copilot",
  "riskLanding.hero.startCta": "Start Risk Copilot",
  "riskLanding.hero.allFeatures": "See All Features",
  "riskLanding.trust.iso": "ISO 27001:2022 Aligned",
  "riskLanding.trust.security": "Enterprise-Grade Security",
  "riskLanding.trust.cloud": "100% Cloud-Based",

  "riskLanding.problem.title": "Still Managing Risks in Spreadsheets?",
  "riskLanding.problem.subtitle": "You're not alone. Most teams start with Excel. But spreadsheets don't scale, don't track changes, and won't impress your auditor.",
  "riskLanding.problem.scattered.title": "Scattered Data",
  "riskLanding.problem.scattered.desc": "Risks, controls, and treatments live in separate files with no linkage between them.",
  "riskLanding.problem.audit.title": "No Audit Trail",
  "riskLanding.problem.audit.desc": "No record of who changed what or when - a red flag in any ISO 27001 audit.",
  "riskLanding.problem.review.title": "Missing Review Cycles",
  "riskLanding.problem.review.desc": "No system to track which risks are overdue for reassessment every 180 days.",

  "riskLanding.features.title": "Everything You Need to Manage Risk",
  "riskLanding.features.subtitle": "12 powerful modules designed for security professionals, auditors, and compliance teams.",
  "riskLanding.features.dashboard.title": "Real-Time Risk Dashboard",
  "riskLanding.features.dashboard.desc": "Live stats, dynamic 5×5 heat map, risk distribution charts and trend lines.",
  "riskLanding.features.register.title": "Full Risk Register",
  "riskLanding.features.register.desc": "Create and score risks with inherent and residual Likelihood × Impact scoring.",
  "riskLanding.features.review.title": "Risk Review Workflow",
  "riskLanding.features.review.desc": "Track reassessments on a 180-day cycle with overdue and upcoming views.",
  "riskLanding.features.assets.title": "Asset Management",
  "riskLanding.features.assets.desc": "Catalog assets with CIA classification and link them to business processes.",
  "riskLanding.features.processes.title": "Business Process Registry",
  "riskLanding.features.processes.desc": "Map critical processes, assign owners, criticality ratings, and link to assets.",
  "riskLanding.features.threats.title": "Threat Library",
  "riskLanding.features.threats.desc": "Maintain a catalog of threats from industry standards with custom entries.",
  "riskLanding.features.vulns.title": "Vulnerability Library",
  "riskLanding.features.vulns.desc": "Track vulnerabilities with severity ratings, CVE references, and risk linkage.",
  "riskLanding.features.treatments.title": "Treatment Plans",
  "riskLanding.features.treatments.desc": "Plan mitigations with action plans, due dates, owners, and completion tracking.",
  "riskLanding.features.controls.title": "Control Register",
  "riskLanding.features.controls.desc": "Document controls with effectiveness scoring, test dates, and evidence notes.",
  "riskLanding.features.reports.title": "Reports & Export",
  "riskLanding.features.reports.desc": "Generate compliance-ready Excel and Word reports with charts and executive summaries.",
  "riskLanding.features.audit.title": "Audit Trail",
  "riskLanding.features.audit.desc": "Every create, update, and delete action logged with timestamps for full traceability.",
  "riskLanding.features.framework.title": "Risk Framework Settings",
  "riskLanding.features.framework.desc": "Configure likelihood and impact scales, risk matrix colors, scoring methodology, and risk appetite thresholds.",

  "riskLanding.why.title": "Why Risk Copilot?",
  "riskLanding.why.b1": "ISO 27001:2022 aligned risk methodology",
  "riskLanding.why.b2": "12 integrated modules - dashboard to audit trail",
  "riskLanding.why.b3": "No installation - runs entirely in your browser",
  "riskLanding.why.b4": "Your data stays private with row-level security",
  "riskLanding.why.b5": "Export audit-ready Word and Excel reports in one click",
  "riskLanding.why.b6": "Unlimited risks, assets, controls, and treatments",
  "riskLanding.why.b7": "Threat and vulnerability libraries with risk linkage",
  "riskLanding.why.b8": "180-day risk review cycle tracking",
  "riskLanding.why.b9": "Works on desktop, tablet, and mobile",
  "riskLanding.why.levels": "Risk Levels",
  "riskLanding.why.totalRisks": "Total Risks",
  "riskLanding.why.critical": "Critical",
  "riskLanding.why.treatments": "Treatments",
  "riskLanding.why.controls": "Controls",
  "riskLanding.why.heatmap": "5×5 Risk Heat Map",

  "riskLanding.demo.title": "See It Before You Subscribe",
  "riskLanding.demo.subtitle": "Explore the full platform with sample data - no signup, no credit card required.",
  "riskLanding.demo.cta": "Try Demo",

  "riskLanding.pricing.titleHas": "Your Pro Plan",
  "riskLanding.pricing.titleDefault": "Simple, Transparent Pricing",
  "riskLanding.pricing.subtitleHas": "You have full access to all features below.",
  "riskLanding.pricing.subtitleDefault": "Everything included in one plan. No hidden fees. Cancel anytime.",
  "riskLanding.pricing.active": "Active",
  "riskLanding.pricing.allInOne": "All-In-One",
  "riskLanding.pricing.monthly": "Monthly",
  "riskLanding.pricing.yearly": "Yearly",
  "riskLanding.pricing.save": "Save ${amount}",
  "riskLanding.pricing.perMo": "/mo",
  "riskLanding.pricing.perYr": "/yr",
  "riskLanding.pricing.worksOut": "Works out to ${amount}/mo",
  "riskLanding.pricing.orYearly": "or ${yearly}/yr - save ${savings}",
  "riskLanding.pricing.manage": "Manage Subscription",
  "riskLanding.pricing.getYearly": "Get Pro - Yearly",
  "riskLanding.pricing.getMonthly": "Get Pro - Monthly",
  "riskLanding.pricing.thanks": "Thank you for being a Pro subscriber.",
  "riskLanding.pricing.tagline": "Pro gets you certified. Risk Copilot keeps you certified.",
  "riskLanding.pricing.f1": "All 12 Risk Copilot modules included",
  "riskLanding.pricing.f2": "All 11 ISO 27001 implementation tools",
  "riskLanding.pricing.f3": `ISO 27001 AI Assistant - ${PRO_MSG} messages/day (${Math.round(PRO_MSG / PLUS_MSG)}x the Plus tier)`,
  "riskLanding.pricing.f4": "Unlimited risks, assets, and controls",
  "riskLanding.pricing.f5": "Real-Time Risk Dashboard",
  "riskLanding.pricing.f6": "Full Risk Register with inherent and residual scoring",
  "riskLanding.pricing.f7": "Risk Review Workflow - 180-day cycle",
  "riskLanding.pricing.f8": "Treatment Plans with owner tracking",
  "riskLanding.pricing.f9": "Control Register with effectiveness ratings",
  "riskLanding.pricing.f10": "Audit Trail - full action log",
  "riskLanding.pricing.f11": "Custom Risk Framework and Matrix",
  "riskLanding.pricing.f12": "Complete exports - Excel and Word",
  "riskLanding.pricing.f13": "Row-level data security",
  "riskLanding.pricing.f14": "Works on desktop, tablet, and mobile",
  "riskLanding.pricing.entBadge": "Enterprise Level",
  "riskLanding.pricing.entTitle": "Enterprise",
  "riskLanding.pricing.entCustom": "Custom",
  "riskLanding.pricing.entSubtext": "Tailored for organizations with custom compliance needs",
  "riskLanding.pricing.entContact": "Contact us",
  "riskLanding.pricing.entF1": "Everything in Pro",
  "riskLanding.pricing.entF2": "Multi-user access and team management",
  "riskLanding.pricing.entF3": "Custom onboarding and training",
  "riskLanding.pricing.entF4": "Tailored compliance support",
  "riskLanding.pricing.entF5": "Dedicated account contact",
  "riskLanding.pricing.entF6": "Custom integrations on request",

  "riskLanding.faq.title": "Frequently Asked Questions",
  "riskLanding.faq.q1": "Is Risk Copilot the same as the Pro Risk Assessment tool?",
  "riskLanding.faq.a1": "No. The Pro Risk Assessment tool is a single-session AI generator. Risk Copilot is a persistent platform where your data is saved, tracked, and reviewed over time.",
  "riskLanding.faq.q2": "Do I need an Audit Room subscription to use Risk Copilot?",
  "riskLanding.faq.a2": "No. Risk Copilot is completely standalone. You can subscribe to it independently without a Pro plan.",
  "riskLanding.faq.q3": "Is my data secure?",
  "riskLanding.faq.a3": "Yes. All data is stored with row-level security. Only you can access your organization's data.",
  "riskLanding.faq.q4": "Can I export my data?",
  "riskLanding.faq.a4": "Yes. Export your full risk register, treatment plans, and control register to Word and Excel at any time.",
  "riskLanding.faq.q5": "Can I cancel anytime?",
  "riskLanding.faq.a5": "Yes. You can cancel your subscription at any time. You will retain access until the end of your billing period.",

  "riskLanding.footerCta.titleHas": "Your Risk Copilot Is Ready",
  "riskLanding.footerCta.titleDefault": "Ready to Take Control of Your Risk?",
  "riskLanding.footerCta.subtitleHas": "Access your dashboard, risk register, and all 12 modules.",
  "riskLanding.footerCta.subtitleDefault": "Join security professionals using Risk Copilot to manage ISO 27001 risk the right way.",
  "riskLanding.footerCta.open": "Open Risk Copilot",
  "riskLanding.footerCta.tryDemo": "Try Demo",
  "riskLanding.footerCta.subscribe": "Subscribe Now",

  // Enterprise page
  "enterprise.hero.badge": "Enterprise Level",
  "enterprise.hero.title": "ISO 27001 at the scale your organization needs",
  "enterprise.hero.subtitle": "Built for security, compliance, and risk teams that need more than a single-seat plan. Get a tailored setup, custom support, and the flexibility to fit your environment.",
  "enterprise.hero.cta": "Request a proposal",
  "enterprise.hero.seePricing": "See standard pricing",
  "enterprise.features.title": "What Enterprise includes",
  "enterprise.features.subtitle": "Everything in Pro, plus the team-level capabilities below.",
  "enterprise.features.f1.title": "Multi-user access",
  "enterprise.features.f1.desc": "Roles, team management, and shared workspaces for your security and compliance teams.",
  "enterprise.features.f2.title": "Custom onboarding",
  "enterprise.features.f2.desc": "Hands-on setup, training sessions, and tailored guidance for your ISMS rollout.",
  "enterprise.features.f3.title": "Custom integrations",
  "enterprise.features.f3.desc": "Hook the platform into your existing tooling - SSO, ticketing, or evidence systems.",
  "enterprise.features.f4.title": "Tailored compliance support",
  "enterprise.features.f4.desc": "Help mapping ISO 27001 to your scope, controls, and audit requirements.",
  "enterprise.features.f5.title": "Dedicated contact",
  "enterprise.features.f5.desc": "A direct point of contact for questions, escalations, and review cycles.",
  "enterprise.features.f6.title": "Security & data controls",
  "enterprise.features.f6.desc": "Discuss data residency, retention, and access controls that fit your policies.",
  "enterprise.covered.title": "A platform built for ISO 27001 programs",
  "enterprise.covered.i1": "All 11 ISO 27001 implementation tools",
  "enterprise.covered.i2": `ISO 27001 AI Assistant - ${PRO_MSG} messages/day (included with Pro)`,
  "enterprise.covered.i3": "Full Risk Copilot platform (12 modules)",
  "enterprise.covered.i4": "Risk register, treatments, and audit trail",
  "enterprise.covered.i5": "Custom risk framework and matrix",
  "enterprise.covered.i6": "Excel and Word exports across modules",
  "enterprise.covered.i7": "Multi-user team workspace",
  "enterprise.covered.i8": "Dedicated onboarding session",
  "enterprise.cta.title": "Let's design a plan that fits your team",
  "enterprise.cta.subtitle": "Tell us about your organization, scope, and timeline. We'll come back with a proposal tailored to how you work - usually within one business day.",
  "enterprise.cta.button": "Request a proposal",
  "enterprise.cta.email": "Or email us directly at",

  // Start page
  "start.title": "What do you want to do?",
  "start.subtitle": "Pick the path that fits your ISO 27001 journey. Every option is free to try.",
  "start.featured.ai.title": "ISO 27001 AI Assistant",
  "start.featured.ai.desc": "Ask anything about ISO 27001 - clauses, controls, policies, risks, audits. Get clear, structured, practical answers in seconds.",
  "start.featured.ai.cta": "Open AI Assistant",
  "start.featured.ai.badge": "New",
  "start.featured.copilot.title": "Risk Copilot",
  "start.featured.copilot.desc": "Manage your ISO 27001 risks end-to-end - register, assets, controls, treatments, and audit-ready reports in one place.",
  "start.featured.copilot.cta": "Open Risk Copilot",
  "start.featured.docs.title": "ISO 27001 Document Pack",
  "start.featured.docs.desc": "Download 40+ ready-to-use ISO 27001 policy templates in editable Word format. No watermarks, yours forever.",
  "start.featured.docs.cta": "Get Document Pack",
  "start.tools.tag": "11 AI-powered tools",
  "start.tools.title": "All ISO 27001 implementation tools",
  "start.tools.subtitle": "Free to try. Run a gap analysis, build your SoA, draft your risk register, plan your audits, and more.",
  "start.tools.browse": "Browse all tools",
  "start.tools.openTool": "Open tool",
  "start.tools.t1.title": "SoA Generator",
  "start.tools.t1.desc": "Build your Statement of Applicability across all 93 Annex A controls.",
  "start.tools.t2.title": "Risk Assessment",
  "start.tools.t2.desc": "Identify threats, score likelihood and impact, plan treatments.",
  "start.tools.t3.title": "Gap Analysis",
  "start.tools.t3.desc": "Assess your posture against ISO 27001:2022 and prioritize actions.",
  "start.tools.t4.title": "Internal Audit Checklist",
  "start.tools.t4.desc": "Run internal audits with control-by-control checklists and findings.",
  "start.tools.t5.title": "Implementation Roadmap",
  "start.tools.t5.desc": "Generate a phased certification roadmap with milestones.",
  "start.tools.t6.title": "ISMS Scope Builder",
  "start.tools.t6.desc": "Define your ISMS scope - sites, systems, processes, legal context.",
  "start.tools.t7.title": "Asset Inventory",
  "start.tools.t7.desc": "Build an asset register with owners, locations, and CIA ratings.",
  "start.tools.t8.title": "Annex A Selector",
  "start.tools.t8.desc": "Get a tailored list of relevant Annex A controls for your SoA.",
  "start.tools.t9.title": "Supplier Risk Manager",
  "start.tools.t9.desc": "Score suppliers on data sensitivity, contracts, and country risk.",
  "start.tools.t10.title": "RTO/RPO & BIA Helper",
  "start.tools.t10.desc": "Set impact and downtime per process to compute RTO, RPO, BIA.",
  "start.enterprise.badge": "Enterprise Level",
  "start.enterprise.title": "Need Enterprise Support?",
  "start.enterprise.desc": "Custom onboarding, dedicated account management, multi-user access, and tailored ISO 27001 implementation services for organizations.",
  "start.enterprise.cta": "Contact us",

  // ===== Risk Copilot App (Phase 6a: Shell) =====
  "rm.sidebar.dashboard": "Dashboard",
  "rm.sidebar.riskRegister": "Risk Register",
  "rm.sidebar.riskReview": "Risk Review",
  "rm.sidebar.assets": "Assets",
  "rm.sidebar.processes": "Processes",
  "rm.sidebar.threats": "Threats",
  "rm.sidebar.vulnerabilities": "Vulnerabilities",
  "rm.sidebar.treatments": "Treatments",
  "rm.sidebar.controls": "Controls",
  "rm.sidebar.reports": "Reports",
  "rm.sidebar.auditTrail": "Audit Trail",
  "rm.sidebar.settings": "Settings",
  "rm.sidebar.expand": "Expand sidebar",
  "rm.sidebar.collapse": "Collapse sidebar",
  "rm.sidebar.tagline": "Your Risk Management Companion",
  "rm.placeholder.comingSoon": "Coming Soon",
  "rm.placeholder.underDevelopment": "This module is under development. Check back soon for full functionality.",
  "rm.dashboard.title": "Dashboard",
  "rm.dashboard.subtitle": "Risk overview for your organization",
  "rm.dashboard.signInTitle": "Sign in to view your dashboard",
  "rm.dashboard.signInDesc": "Authenticate to access your risk register, assets, and controls data.",
  "rm.dashboard.signIn": "Sign In",
  "rm.dashboard.totalRisks": "Total Risks",
  "rm.dashboard.criticalHigh": "Critical / High",
  "rm.dashboard.assetsManaged": "Assets Managed",
  "rm.dashboard.overdueActions": "Overdue Actions",
  "rm.dashboard.heatMap": "Risk Heat Map",
  "rm.dashboard.recentRisks": "Recent Risks",
  "rm.dashboard.noRisks": "No risks recorded yet. Add your first risk in the Risk Register.",
  "rm.impact.insignificant": "Insignificant",
  "rm.impact.minor": "Minor",
  "rm.impact.moderate": "Moderate",
  "rm.impact.major": "Major",
  "rm.impact.catastrophic": "Catastrophic",
  "rm.likelihood.rare": "Rare",
  "rm.likelihood.unlikely": "Unlikely",
  "rm.likelihood.possible": "Possible",
  "rm.likelihood.likely": "Likely",
  "rm.likelihood.certain": "Certain",

  // ===== Risk Copilot App (Phase 6b: List pages chrome) =====
  // Shared
  "rm.common.signIn": "Sign In",
  "rm.common.cancel": "Cancel",
  "rm.common.delete": "Delete",
  "rm.common.edit": "Edit",
  "rm.common.excel": "Excel",
  "rm.common.word": "Word",
  "rm.common.exportExcel": "Export Excel",
  "rm.common.exportWord": "Export Word",
  "rm.common.exportPdf": "Export PDF",
  "rm.common.exportFailed": "Export failed",
  "rm.common.exported": "Exported",
  "rm.common.actions": "Actions",
  "rm.common.action": "Action",
  "rm.common.name": "Name",
  "rm.common.title": "Title",
  "rm.common.category": "Category",
  "rm.common.status": "Status",
  "rm.common.owner": "Owner",
  "rm.common.description": "Description",
  "rm.common.type": "Type",
  "rm.common.priority": "Priority",
  "rm.common.progress": "Progress",
  "rm.common.dueDate": "Due Date",
  "rm.common.assigned": "Assigned",
  "rm.common.location": "Location",
  "rm.common.id": "ID",
  "rm.common.deleteConfirm": "Are you sure you want to delete",
  "rm.common.deleteUndone": "This action cannot be undone.",
  // Risk Register
  "rm.risks.title": "Risk Register",
  "rm.risks.subtitle": "Identify, assess, and track organizational risks",
  "rm.risks.signInToManage": "Sign in to manage risks",
  "rm.risks.add": "Add Risk",
  "rm.risks.empty": "No risks yet. Click \"Add Risk\" to create your first entry.",
  "rm.risks.col.inherent": "Inherent",
  "rm.risks.col.residual": "Residual",
  "rm.risks.editAria": "Edit risk",
  "rm.risks.deleteAria": "Delete risk",
  "rm.risks.deleteTitle": "Delete Risk",
  "rm.risks.exported": "Risk Register exported",
  "rm.risks.exportedWord": "Risk Register exported to Word",
  // Assets
  "rm.assets.title": "Assets",
  "rm.assets.subtitle": "Manage information assets and CIA classification",
  "rm.assets.signInToManage": "Sign in to manage assets",
  "rm.assets.add": "Add Asset",
  "rm.assets.empty": "No assets yet. Click \"Add Asset\" to create your first entry.",
  "rm.assets.editAria": "Edit asset",
  "rm.assets.deleteAria": "Delete asset",
  "rm.assets.deleteTitle": "Delete Asset",
  "rm.assets.exported": "Assets exported",
  "rm.assets.exportedWord": "Assets exported to Word",
  // Processes
  "rm.processes.title": "Business Processes",
  "rm.processes.subtitle": "Map organizational processes and their criticality",
  "rm.processes.signInToManage": "Sign in to manage processes",
  "rm.processes.add": "Add Process",
  "rm.processes.empty": "No processes yet. Click \"Add Process\" to start mapping your business processes.",
  "rm.processes.criticality": "Criticality",
  "rm.processes.deleteTitle": "Delete Process",
  "rm.processes.exported": "Processes exported",
  "rm.processes.exportedWord": "Processes exported to Word",
  // Threats
  "rm.threats.title": "Threat Library",
  "rm.threats.subtitle": "Catalog of threats relevant to your organization",
  "rm.threats.signInToManage": "Sign in to manage threats",
  "rm.threats.add": "Add Threat",
  "rm.threats.empty": "No threats yet. Click \"Add Threat\" to start building your library.",
  "rm.threats.source": "Source",
  "rm.threats.deleteTitle": "Delete Threat",
  "rm.threats.exported": "Threats exported",
  "rm.threats.exportedWord": "Threats exported to Word",
  // Vulnerabilities
  "rm.vulns.title": "Vulnerability Library",
  "rm.vulns.subtitle": "Track known vulnerabilities and their severity",
  "rm.vulns.signInToManage": "Sign in to manage vulnerabilities",
  "rm.vulns.add": "Add Vulnerability",
  "rm.vulns.empty": "No vulnerabilities yet. Click \"Add Vulnerability\" to start.",
  "rm.vulns.severity": "Severity",
  "rm.vulns.cveId": "CVE ID",
  "rm.vulns.deleteTitle": "Delete Vulnerability",
  "rm.vulns.exported": "Vulnerabilities exported",
  "rm.vulns.exportedWord": "Vulnerabilities exported to Word",
  // Treatments
  "rm.treatments.title": "Treatments",
  "rm.treatments.subtitle": "Plan and track risk mitigation actions",
  "rm.treatments.signInToManage": "Sign in to manage treatments",
  "rm.treatments.add": "Add Treatment",
  "rm.treatments.empty": "No treatments yet. Click \"Add Treatment\" to create your first entry.",
  "rm.treatments.needRisksFirst": "Create risks in the Risk Register first before adding treatments.",
  "rm.treatments.option": "Option",
  "rm.treatments.editAria": "Edit treatment",
  "rm.treatments.deleteAria": "Delete treatment",
  "rm.treatments.deleteTitle": "Delete Treatment",
  "rm.treatments.exported": "Treatments exported",
  "rm.treatments.exportedWord": "Treatments exported to Word",
  // Controls
  "rm.controls.title": "Controls",
  "rm.controls.subtitle": "Control library with effectiveness tracking",
  "rm.controls.signInToManage": "Sign in to manage controls",
  "rm.controls.add": "Add Control",
  "rm.controls.empty": "No controls yet. Click \"Add Control\" to create your first entry.",
  "rm.controls.effectiveness": "Effectiveness",
  "rm.controls.frequency": "Frequency",
  "rm.controls.lastTested": "Last Tested",
  "rm.controls.nextReview": "Next Review",
  "rm.controls.editAria": "Edit control",
  "rm.controls.deleteAria": "Delete control",
  "rm.controls.deleteTitle": "Delete Control",
  "rm.controls.exported": "Controls exported",
  "rm.controls.exportedWord": "Controls exported to Word",
  // Reports
  "rm.reports.title": "Reports",
  "rm.reports.subtitle": "Visual analytics and compliance reporting",
  "rm.reports.signInToView": "Sign in to view reports",
  "rm.reports.isoRegister": "ISO 27001 Risk Register",
  "rm.reports.isoRegisterWord": "Risk Register (Word)",
  "rm.reports.generatingRegister": "Generating ISO 27001 Risk Register…",
  "rm.reports.generatingRegisterWord": "Generating ISO 27001 Risk Register (Word)…",
  "rm.reports.registerDownloaded": "Risk Register workbook downloaded",
  "rm.reports.registerWordDownloaded": "Risk Register Word document downloaded",
  "rm.reports.generatingPdf": "Generating PDF…",
  "rm.reports.pdfDownloaded": "PDF downloaded",
  "rm.reports.pdfFailed": "PDF generation failed",
  "rm.reports.excelDownloaded": "Excel downloaded",
  "rm.reports.excelFailed": "Excel export failed",
  "rm.reports.generatingWord": "Generating Word report…",
  "rm.reports.wordDownloaded": "Word report downloaded",
  "rm.reports.wordFailed": "Word export failed",
  "rm.reports.statRisks": "Risks",
  "rm.reports.statTreatments": "Treatments",
  "rm.reports.statControls": "Controls",
  "rm.reports.statAssets": "Assets",
  "rm.reports.chart.riskLevel": "Risk Level Distribution",
  "rm.reports.chart.riskByCategory": "Risks by Category",
  "rm.reports.chart.riskByStatus": "Risks by Status",
  "rm.reports.chart.treatmentStatus": "Treatment Status",
  "rm.reports.chart.treatmentOptions": "Treatment Options",
  "rm.reports.chart.avgCompletion": "Avg Completion by Priority",
  "rm.reports.chart.controlEffectiveness": "Control Effectiveness",
  "rm.reports.chart.controlsByCategory": "Controls by Category",
  "rm.reports.chart.controlsByType": "Controls by Type",
  "rm.reports.chart.assetsByType": "Assets by Type",
  "rm.reports.empty.risk": "No risk data",
  "rm.reports.empty.treatment": "No treatment data",
  "rm.reports.empty.control": "No control data",
  "rm.reports.empty.asset": "No asset data",
  // Audit Trail
  "rm.audit.title": "Audit Trail",
  "rm.audit.subtitle": "Complete log of all actions taken within the Risk Copilot - metadata only, no sensitive data stored",
  "rm.audit.signInToView": "Sign in to view audit trail",
  "rm.audit.search": "Search actions, names, details...",
  "rm.audit.allTypes": "All Types",
  "rm.audit.events": "events",
  "rm.audit.event": "event",
  "rm.audit.timestamp": "Timestamp",
  "rm.audit.empty": "No audit events yet. Actions will be logged automatically as you use the platform.",
  "rm.audit.emptyFilter": "No events match your filter.",
  "rm.audit.details": "Details",
  // Risk Review
  "rm.review.title": "Risk Review & Reassessment",
  "rm.review.subtitle": "Periodically review and reassess risks to keep your register current",
  "rm.review.signInToReview": "Sign in to review risks",
  "rm.review.totalRisks": "Total Risks",
  "rm.review.overdueReviews": "Overdue Reviews",
  "rm.review.dueSoon": "Due Soon",
  "rm.review.empty": "No risks to review. Add risks in the Risk Register first.",
  "rm.review.col.reviewStatus": "Review Status",
  "rm.review.col.lastUpdated": "Last Updated",
  "rm.review.btnReview": "Review",
  "rm.review.exported": "Risk Review exported",
  "rm.review.statusOverdue": "Overdue",
  "rm.review.statusDueSoon": "Due Soon",
  "rm.review.statusCurrent": "Current",
  "rm.review.dialogTitle": "Review Risk:",
  "rm.review.noDescription": "No description",
  "rm.review.updatedStatus": "Updated Status",
  "rm.review.residualLikelihood": "Residual Likelihood (1-5)",
  "rm.review.residualImpact": "Residual Impact (1-5)",
  "rm.review.reviewNotes": "Review Notes",
  "rm.review.notesPlaceholder": "Document findings, changes, or justification...",
  "rm.review.complete": "Complete Review",
  // === Phase 6c: Reports / Audit Trail / Risk Review / Form Dialogs ===
  "rm.reports.signInTitle": "Sign in to view reports",
  "rm.reports.btn.isoRegister": "ISO 27001 Risk Register",
  "rm.reports.btn.isoRegisterWord": "Risk Register (Word)",
  "rm.reports.btn.exportPdf": "Export PDF",
  "rm.reports.btn.exportExcel": "Export Excel",
  "rm.reports.btn.exportWord": "Export Word",
  "rm.reports.toast.generatingWord": "Generating Word report...",
  "rm.reports.toast.wordSuccess": "Word report downloaded",
  "rm.reports.toast.wordFailed": "Word export failed",
  "rm.reports.toast.generatingPdf": "Generating PDF...",
  "rm.reports.toast.pdfSuccess": "PDF downloaded",
  "rm.reports.toast.pdfFailed": "PDF generation failed",
  "rm.reports.toast.excelSuccess": "Excel downloaded",
  "rm.reports.toast.excelFailed": "Excel export failed",
  "rm.reports.toast.generatingRegister": "Generating ISO 27001 Risk Register...",
  "rm.reports.toast.registerSuccess": "Risk Register workbook downloaded",
  "rm.reports.toast.registerFailed": "Export failed",
  "rm.reports.toast.generatingRegisterWord": "Generating ISO 27001 Risk Register (Word)...",
  "rm.reports.toast.registerWordSuccess": "Risk Register Word document downloaded",
  "rm.reports.stat.risks": "Risks",
  "rm.reports.stat.treatments": "Treatments",
  "rm.reports.stat.controls": "Controls",
  "rm.reports.stat.assets": "Assets",
  "rm.reports.chart.risksByCategory": "Risks by Category",
  "rm.reports.chart.risksByStatus": "Risks by Status",
  "rm.audit.signInTitle": "Sign in to view audit trail",
  "rm.audit.searchPlaceholder": "Search actions, names, details...",
  "rm.audit.eventCount": "events",
  "rm.audit.eventCountSingular": "event",
  "rm.audit.empty.none": "No audit events yet. Actions will be logged automatically as you use the platform.",
  "rm.audit.empty.filter": "No events match your filter.",
  "rm.audit.col.timestamp": "Timestamp",
  "rm.audit.col.action": "Action",
  "rm.audit.col.type": "Type",
  "rm.audit.col.name": "Name",
  "rm.audit.col.details": "Details",
  "rm.review.signInTitle": "Sign in to review risks",
  "rm.review.short": "Risk Review",
  "rm.review.exportToast": "Risk Review exported",
  "rm.review.overdue": "Overdue Reviews",
  "rm.review.status.overdue": "Overdue",
  "rm.review.status.dueSoon": "Due Soon",
  "rm.review.status.current": "Current",
  "rm.review.btn.review": "Review",
  "rm.review.dialog.title": "Review Risk",
  "rm.review.dialog.noDescription": "No description",
  "rm.review.dialog.updatedStatus": "Updated Status",
  "rm.review.dialog.residualLikelihood": "Residual Likelihood (1-5)",
  "rm.review.dialog.residualImpact": "Residual Impact (1-5)",
  "rm.review.dialog.notes": "Review Notes",
  "rm.review.dialog.notesPlaceholder": "Document findings, changes, or justification...",
  "rm.review.dialog.complete": "Complete Review",
  "rm.dialog.cancel": "Cancel",
  "rm.dialog.saving": "Saving...",
  "rm.dialog.update": "Update",
  "rm.dialog.create": "Create",
  "rm.risk.dialog.editTitle": "Edit Risk",
  "rm.risk.dialog.addTitle": "Add New Risk",
  "rm.risk.dialog.riskId": "Risk ID",
  "rm.risk.dialog.ownerPlaceholder": "e.g. John Doe",
  "rm.risk.dialog.titlePlaceholder": "Brief risk title",
  "rm.risk.dialog.descriptionPlaceholder": "Detailed description of the risk",
  "rm.risk.dialog.threat": "Threat",
  "rm.risk.dialog.vulnerability": "Vulnerability",
  "rm.risk.dialog.existingControls": "Existing Controls",
  "rm.risk.dialog.inherentScore": "Inherent Risk (Score:",
  "rm.risk.dialog.residualScore": "Residual Risk (Score:",
  "rm.risk.dialog.likelihood": "Likelihood:",
  "rm.risk.dialog.impact": "Impact:",
  "rm.risk.dialog.update": "Update Risk",
  "rm.risk.dialog.add": "Add Risk",
  "rm.asset.dialog.editTitle": "Edit Asset",
  "rm.asset.dialog.addTitle": "Add New Asset",
  "rm.asset.dialog.name": "Asset Name",
  "rm.asset.dialog.namePlaceholder": "e.g. Customer Database",
  "rm.asset.dialog.ownerPlaceholder": "e.g. IT Department",
  "rm.asset.dialog.locationPlaceholder": "e.g. AWS eu-west-1",
  "rm.asset.dialog.cia": "CIA Classification",
  "rm.asset.dialog.overall": "Overall:",
  "rm.asset.dialog.confidentiality": "Confidentiality",
  "rm.asset.dialog.integrity": "Integrity",
  "rm.asset.dialog.availability": "Availability",
  "rm.asset.dialog.update": "Update Asset",
  "rm.asset.dialog.add": "Add Asset",
  "rm.asset.cia.low": "Low",
  "rm.asset.cia.medium": "Medium",
  "rm.asset.cia.high": "High",
  "rm.asset.cia.veryHigh": "Very High",
  "rm.asset.cia.critical": "Critical",
  "rm.control.dialog.editTitle": "Edit Control",
  "rm.control.dialog.addTitle": "Add New Control",
  "rm.control.dialog.name": "Control Name",
  "rm.control.dialog.namePlaceholder": "e.g. Multi-Factor Authentication",
  "rm.control.dialog.effectiveness": "Effectiveness",
  "rm.control.dialog.frequency": "Frequency",
  "rm.control.dialog.ownerPlaceholder": "e.g. Security Team",
  "rm.control.dialog.lastTested": "Last Tested",
  "rm.control.dialog.nextReview": "Next Review",
  "rm.control.dialog.evidence": "Evidence / Notes",
  "rm.control.dialog.add": "Add Control",
  "rm.process.dialog.editTitle": "Edit Business Process",
  "rm.process.dialog.addTitle": "Add Business Process",
  "rm.process.dialog.namePlaceholder": "e.g. Payroll Processing",
  "rm.process.dialog.criticality": "Criticality",
  "rm.process.dialog.ownerPlaceholder": "e.g. HR Department",
  "rm.threat.dialog.editTitle": "Edit Threat",
  "rm.threat.dialog.addTitle": "Add Threat",
  "rm.threat.dialog.namePlaceholder": "e.g. Ransomware attack",
  "rm.threat.dialog.source": "Source",
  "rm.threat.dialog.sourcePlaceholder": "e.g. ISO 27005, NIST SP 800-30",
  "rm.vuln.dialog.editTitle": "Edit Vulnerability",
  "rm.vuln.dialog.addTitle": "Add Vulnerability",
  "rm.vuln.dialog.namePlaceholder": "e.g. Unpatched operating system",
  "rm.vuln.dialog.severity": "Severity",
  "rm.vuln.dialog.cveId": "CVE ID",
  "rm.vuln.dialog.cvePlaceholder": "e.g. CVE-2024-12345",
  "rm.treatment.dialog.editTitle": "Edit Treatment",
  "rm.treatment.dialog.addTitle": "Add New Treatment",
  "rm.treatment.dialog.linkedRisk": "Linked Risk",
  "rm.treatment.dialog.selectRisk": "Select a risk",
  "rm.treatment.dialog.noRisks": "No risks found. Create a risk first.",
  "rm.treatment.dialog.titlePlaceholder": "e.g. Deploy WAF for web servers",
  "rm.treatment.dialog.option": "Option",
  "rm.treatment.dialog.assignedTo": "Assigned To",
  "rm.treatment.dialog.assignedPlaceholder": "e.g. Jane Smith",
  "rm.treatment.dialog.actionPlan": "Action Plan",
  "rm.treatment.dialog.evidence": "Evidence / Notes",
  "rm.treatment.dialog.completion": "Completion",
  "rm.treatment.dialog.add": "Add Treatment",
  // ===== Risk Copilot Settings (Phase 6d) =====
  "rm.settings.title": "Settings",
  "rm.settings.subtitle": "Framework configuration, scales, and risk appetite",
  "rm.settings.signInToManage": "Sign in to manage settings",
  "rm.settings.demoTitle": "Settings are view-only in demo mode",
  "rm.settings.demoDescription": "Subscribe to the Audit Room plan to customize your risk framework, scales, and appetite thresholds.",
  "rm.settings.subscribePro": "Subscribe to Pro →",
  "rm.settings.org.title": "Organization",
  "rm.settings.org.description": "Your company name appears in exported reports and documents",
  "rm.settings.org.label": "Organization Name",
  "rm.settings.org.placeholder": "e.g. Acme Corporation",
  "rm.settings.org.save": "Save",
  "rm.settings.org.saved": "Organization name saved",
  "rm.settings.fw.title": "Risk Frameworks",
  "rm.settings.fw.description": "Define methodology, scoring, and activate your working framework",
  "rm.settings.fw.new": "New",
  "rm.settings.fw.empty": "No frameworks yet. Create one to get started.",
  "rm.settings.fw.colMethodology": "Methodology",
  "rm.settings.fw.colScoring": "Scoring",
  "rm.settings.fw.colActive": "Active",
  "rm.settings.fw.active": "Active",
  "rm.settings.fw.setActive": "Set Active",
  "rm.settings.fw.dialog.editTitle": "Edit Framework",
  "rm.settings.fw.dialog.newTitle": "New Framework",
  "rm.settings.fw.dialog.namePlaceholder": "e.g. ISO 27005 Framework",
  "rm.settings.fw.dialog.methodology": "Methodology",
  "rm.settings.fw.dialog.scoringFormula": "Scoring Formula",
  "rm.settings.fw.deleteTitle": "Delete Framework",
  "rm.settings.fw.deleteDescription": "Delete \"{name}\"? This will also remove its scales, matrix, and thresholds.",
  "rm.settings.noActive": "Create and activate a framework above to configure scales, matrix, and thresholds.",
  "rm.settings.methodology": "Risk Methodology",
  "rm.settings.configuringFor": "Configuring scales for",
  "rm.settings.tab.likelihood": "Likelihood",
  "rm.settings.tab.impact": "Impact",
  "rm.settings.tab.matrix": "Matrix",
  "rm.settings.tab.thresholds": "Thresholds",
  "rm.settings.lk.title": "Likelihood Scale",
  "rm.settings.lk.description": "Define probability levels for risk assessment",
  "rm.settings.lk.addLevel": "Add Level",
  "rm.settings.lk.empty": "No likelihood levels defined. Add levels (e.g. 1-5: Rare to Almost Certain).",
  "rm.settings.lk.colLevel": "Level",
  "rm.settings.lk.colLabel": "Label",
  "rm.settings.lk.colDescription": "Description",
  "rm.settings.lk.colProbability": "Probability",
  "rm.settings.lk.dialog.editTitle": "Edit Likelihood Level",
  "rm.settings.lk.dialog.newTitle": "New Likelihood Level",
  "rm.settings.lk.dialog.labelPlaceholder": "e.g. Rare",
  "rm.settings.lk.dialog.descPlaceholder": "Describe when this level applies",
  "rm.settings.lk.dialog.probMin": "Probability Min (%)",
  "rm.settings.lk.dialog.probMax": "Probability Max (%)",
  "rm.settings.lk.deleteTitle": "Delete Likelihood Level",
  "rm.settings.lk.deleteDescription": "Delete level {level} \"{label}\"?",
  "rm.settings.im.title": "Impact Scale",
  "rm.settings.im.description": "Define impact severity levels with financial, operational, and reputational dimensions",
  "rm.settings.im.empty": "No impact levels defined. Add levels (e.g. 1-5: Negligible to Catastrophic).",
  "rm.settings.im.colFinancial": "Financial",
  "rm.settings.im.colOperational": "Operational",
  "rm.settings.im.colReputational": "Reputational",
  "rm.settings.im.dialog.editTitle": "Edit Impact Level",
  "rm.settings.im.dialog.newTitle": "New Impact Level",
  "rm.settings.im.dialog.labelPlaceholder": "e.g. Negligible",
  "rm.settings.im.dialog.descPlaceholder": "General description of this impact level",
  "rm.settings.im.dialog.financial": "Financial Impact",
  "rm.settings.im.dialog.financialPlaceholder": "e.g. < $10,000 loss",
  "rm.settings.im.dialog.operational": "Operational Impact",
  "rm.settings.im.dialog.operationalPlaceholder": "e.g. Minor disruption < 1 hour",
  "rm.settings.im.dialog.reputational": "Reputational Impact",
  "rm.settings.im.dialog.reputationalPlaceholder": "e.g. No media coverage",
  "rm.settings.im.deleteTitle": "Delete Impact Level",
  "rm.settings.mx.title": "Risk Matrix Cells",
  "rm.settings.mx.description": "Define the score, label, and color for each Likelihood × Impact intersection",
  "rm.settings.mx.addCell": "Add Cell",
  "rm.settings.mx.empty": "No matrix cells defined. Add cells for each Likelihood × Impact combination.",
  "rm.settings.mx.preview": "Matrix Preview",
  "rm.settings.mx.notDefined": "Not defined",
  "rm.settings.mx.colColor": "Color",
  "rm.settings.mx.colLikelihood": "Likelihood",
  "rm.settings.mx.colImpact": "Impact",
  "rm.settings.mx.colScore": "Score",
  "rm.settings.mx.colLabel": "Label",
  "rm.settings.mx.dialog.editTitle": "Edit Matrix Cell",
  "rm.settings.mx.dialog.newTitle": "New Matrix Cell",
  "rm.settings.mx.dialog.likelihoodLevel": "Likelihood Level",
  "rm.settings.mx.dialog.impactLevel": "Impact Level",
  "rm.settings.mx.dialog.riskScore": "Risk Score",
  "rm.settings.mx.dialog.riskLabel": "Risk Label",
  "rm.settings.mx.dialog.riskLabelPlaceholder": "e.g. Low, Medium, High",
  "rm.settings.mx.dialog.cellColor": "Cell Color",
  "rm.settings.mx.deleteTitle": "Delete Matrix Cell",
  "rm.settings.mx.deleteDescription": "Delete cell L{l}×I{i} \"{label}\"?",
  "rm.settings.th.title": "Risk Appetite Thresholds",
  "rm.settings.th.description": "Define score ranges that classify risk appetite levels",
  "rm.settings.th.empty": "No thresholds defined. Add score ranges to classify risk appetite.",
  "rm.settings.th.colScoreRange": "Score Range",
  "rm.settings.th.colTreatment": "Treatment",
  "rm.settings.th.colEscalation": "Escalation",
  "rm.settings.th.required": "Required",
  "rm.settings.th.no": "No",
  "rm.settings.th.dialog.editTitle": "Edit Threshold",
  "rm.settings.th.dialog.newTitle": "New Threshold",
  "rm.settings.th.dialog.labelPlaceholder": "e.g. Acceptable",
  "rm.settings.th.dialog.minScore": "Min Score",
  "rm.settings.th.dialog.maxScore": "Max Score",
  "rm.settings.th.dialog.color": "Color",
  "rm.settings.th.dialog.treatmentRequired": "Treatment required",
  "rm.settings.th.dialog.escalationRequired": "Escalation required",
  "rm.settings.th.deleteTitle": "Delete Threshold",
  "rm.settings.th.deleteDescription": "Delete threshold \"{label}\"?",
  "rm.settings.dialog.create": "Create",
  "rm.settings.dialog.update": "Update",
  "rm.settings.dialog.saving": "Saving...",
  "rm.topbar.guest": "Guest",
  "rm.demo.modal.title": "This is a demo",
  "rm.demo.modal.description": "You're viewing sample data in read-only mode. Subscribe to the Risk Copilot plan to create, edit, and manage your own risk register, assets, controls, and more.",
  "rm.demo.modal.subscribe": "Subscribe to Pro →",
  "rm.demo.modal.continue": "Continue exploring demo",
  "rm.demo.banner.pro": "You're on the Audit Room plan - upgrade to Copilot to manage your own risks.",
  "rm.demo.banner.guest": "Demo mode - sample data, changes are not saved.",
  "rm.demo.banner.subscribe": "Subscribe to Pro →",

  // ISO 27001 AI Assistant (Copilot page)
  "copilot.seo.title": "ISO 27001 AI Assistant",
  "copilot.seo.description": "Use the ISO 27001 AI Assistant to get help with policies, controls, clauses, risk management, audit readiness and implementation questions.",
  "copilot.sidebar.newChat": "New chat",
  "copilot.sidebar.untitled": "New chat",
  "copilot.sidebar.deleteAria": "Delete conversation",
  "copilot.group.today": "Today",
  "copilot.group.yesterday": "Yesterday",
  "copilot.group.thisWeek": "This week",
  "copilot.group.earlier": "Earlier",
  "copilot.plan.plus": "Plus plan",
  "copilot.plan.pro": "Pro plan",
  "copilot.plan.free": "Free trial",
  "copilot.usage.sidebar": "{plan} - {remaining}/{limit} messages left today",
  "copilot.usage.header": "{plan} - {remaining} of {limit} messages left today",
  "copilot.warning.noSensitive": "Do not share sensitive company data in this chat.",
  "copilot.title": "ISO 27001 AI Assistant",
  "copilot.badge.tryFree": "Try for free",
  "copilot.upgrade.full": "Upgrade to Plus",
  "copilot.upgrade.short": "Plus",
  "copilot.upgrade.priceSuffix": "/mo",
  "copilot.upgrade.aria": "Upgrade to Plus for ${price} per month",
  "copilot.newChat.aria": "New chat",
  "copilot.empty.title": "How can I help with ISO 27001?",
  "copilot.empty.subtitle": "Ask about ISO 27001 ISMS policies, controls, risk management and audit readiness - including ISMS scope, Annex A controls, internal audits and certification. Free for everyone.",
  "copilot.prompt.scope.title": "Define ISMS scope",
  "copilot.prompt.scope.text": "Help me define the scope of my ISMS for a 50-person SaaS company.",
  "copilot.prompt.risk.title": "Risk assessment methodology",
  "copilot.prompt.risk.text": "Walk me through choosing a risk assessment methodology for ISO 27001.",
  "copilot.prompt.soa.title": "Statement of Applicability",
  "copilot.prompt.soa.text": "Explain how to build a Statement of Applicability and which Annex A controls to justify.",
  "copilot.prompt.audit.title": "Internal audit plan",
  "copilot.prompt.audit.text": "Draft an internal audit plan covering all ISO 27001 clauses over 12 months.",
  "copilot.message.you": "You",
  "copilot.message.assistant": "ISO 27001 AI Assistant",
  "copilot.limit.reached": "You've reached today's message limit.",
  "copilot.limit.upgradePrompt": "Upgrade to Plus for extended usage and more detailed answers.",
  "copilot.limit.upgradeCta": "Upgrade to Plus",
  "copilot.limit.perks": `${PLUS_MSG} messages/day · AI Assistant only`,
  "copilot.limit.resetSoon": "Your daily quota resets soon. Try again after the reset.",
  "copilot.limit.resetIn": "Resets in {hours} {unit}",
  "copilot.limit.hour": "hour",
  "copilot.limit.hours": "hours",
  "copilot.input.placeholder": "Message ISO 27001 AI Assistant...",
  "copilot.input.placeholderLimit": "Daily limit reached - upgrade to keep chatting",
  "copilot.input.sendAria": "Send message",
  "copilot.disclaimer": "Do not share sensitive or confidential information. Messages are processed by third-party AI providers. Verify critical compliance decisions with a qualified auditor.",
  "copilot.toast.dailyLimit": "Daily message limit reached. Try again later or upgrade for more messages.",
  "copilot.toast.tooLong": "Message too long (max {max} characters)",
  "copilot.toast.failed": "Failed to send message",
  "copilot.toast.signin": "Please sign in or create an account to subscribe.",
  "copilot.toast.checkoutOpened": "Checkout opened in a new tab. Come back here when you're done.",
  "copilot.toast.subActive": "Subscription active! Your new message limit is ready to use.",
  "copilot.toast.syncing": "Payment received. Your access is still syncing - please wait a moment and refresh if needed.",
};

export default en;